import {
  ArrowUp,
  Bell,
  Check,
  Code,
  ChevronDown,
  Download,
  Edit,
  Eye,
  EyeOff,
  Image,
  Link,
  Link2Off,
  MousePointer2,
  MoreVertical,
  Plus,
  Minus,
  X,
  Zap,
  PartyPopper,
  SquareMousePointer,
  Trash2,
  Type,
  icons,
} from "lucide-react";
import { useConvex } from "convex/react";
import { renderToStaticMarkup } from "react-dom/server";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { TextVariableInput } from "@/components/ui/text-variable-input";
import { Label } from "@/components/ui/label";
import { NumberDropdown } from "@/components/ui/number-dropdown";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ActionsPanel } from "@/components/shared/ActionsPanel";
import { useEditorActions } from "@/hooks/useEditorActions";
import { useToast } from "@/hooks/use-toast";
import type { CMSAction } from "@/services/cmsActionService";
import { StorageServiceFactory } from "@/services/storage/StorageServiceFactory";
import { StoragePaths, resolveStoragePathSegments } from "@/utils/storagePaths";
import { computeFileHash } from "@/utils/fileHash";
import { getVideoDimensions } from "@/utils/videoUtils";
import { useEditorStore } from "@/store/editorStore";
import { useVariablesStore } from "@/store/variablesStore";
import type { Tables } from "@/types/database";
import { api } from "../../../convex/_generated/api";
import {
  createVersion as createLocalVersion,
  getNextVersionNumber as getNextLocalVersionNumber,
} from "@/data/adapters/local/assetVersions";
import { updateProject as updateLocalProject } from "@/data/adapters/local/projects";
import { isElectron } from "@/data";
import { positionToYawPitch, yawPitchToPosition } from "@/utils/coordinateSystem";
import { resolveCSSVariable } from "@/utils/resolveCSSVariables";
import { toCSSVarReference } from "@/utils/cssVariables";
import { sanitizeSvgMarkup } from "@/utils/svgSanitizer";
import { cn } from "@/lib/utils";
import { BREAKPOINT_PRESETS } from "@/lib/breakpoints";
import { BulkEditPanel } from "./BulkEditPanel";
import { ColorPicker } from "./ColorPicker";
import { HotspotTextEditor } from "./HotspotTextEditor";
import { ConfettiDialog } from "./ConfettiDialog";
import LucideIconSearch from "../skin/LucideIconSearch";
import { CollectionItemsPanel, type CollectionItemsConfig } from "@/components/skin/CollectionItemsPanel";
import { useViewportControls } from "@/components/editor/ViewportControlsContext";

// Smooth Number Input Component for continuous updates
interface SmoothNumberInputProps {
  value: number;
  onChange: (value: number) => void;
  className?: string;
  step?: number;
  min?: number;
  max?: number;
  allowAngleVariables?: boolean;
}

interface DraggableSizeInputProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  defaultUnit?: string;
  step?: number;
  min?: number;
  onNumericDragChange?: (value: string) => void;
  onNonNumericClick?: () => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
}

const DraggableSizeInput = ({
  value,
  onChange,
  className,
  placeholder,
  defaultUnit = "px",
  step = 1,
  min,
  onNumericDragChange,
  onNonNumericClick,
  onBlur,
  onKeyDown,
}: DraggableSizeInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const dragCleanupRef = useRef<(() => void) | null>(null);
  const parsedValue = value.trim().match(/^(-?\d+(?:\.\d+)?)(px|%|rem|em|vw|vh)?$/i);
  const numericValue = parsedValue ? parseFloat(parsedValue[1]) : null;
  const unit = parsedValue?.[2] ?? defaultUnit;
  const isDraggable = numericValue !== null && !Number.isNaN(numericValue);

  useEffect(() => {
    return () => {
      dragCleanupRef.current?.();
    };
  }, []);

  const handleMouseDown = (event: React.MouseEvent<HTMLInputElement>) => {
    if (event.button !== 0 || document.activeElement === inputRef.current) return;

    if (!isDraggable || numericValue === null) {
      onNonNumericClick?.();
      return;
    }

    event.preventDefault();

    const input = inputRef.current;
    if (!input) return;

    const startY = event.clientY;
    const startValue = numericValue;
    let hasDragged = false;

    const clearSelection = () => {
      window.getSelection()?.removeAllRanges();
    };

    const cleanup = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("selectstart", onSelectStart);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      dragCleanupRef.current = null;
    };

    const onSelectStart = (selectEvent: Event) => {
      selectEvent.preventDefault();
    };

    const onMouseMove = (moveEvent: MouseEvent) => {
      const deltaY = moveEvent.clientY - startY;
      if (!hasDragged && Math.abs(deltaY) < 3) return;

      moveEvent.preventDefault();
      clearSelection();

      if (!hasDragged) {
        hasDragged = true;
        document.body.style.cursor = "ns-resize";
        document.body.style.userSelect = "none";
      }

      let nextValue = startValue + Math.round(-deltaY / 2) * step;
      if (min !== undefined && nextValue < min) nextValue = min;
      const nextDisplayValue = `${nextValue}${unit}`;
      onChange(nextDisplayValue);
      onNumericDragChange?.(nextDisplayValue);
    };

    const onMouseUp = () => {
      cleanup();
      if (!hasDragged) {
        input.focus();
        input.select();
      }
    };

    dragCleanupRef.current = cleanup;
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("selectstart", onSelectStart);
  };

  return (
    <Input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onMouseDown={handleMouseDown}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      className={className}
      placeholder={placeholder}
      style={{ cursor: isDraggable ? "ns-resize" : "text" }}
    />
  );
};

const SmoothNumberInput = ({
  value,
  onChange,
  className,
  step = 1,
  min,
  max,
  allowAngleVariables = false,
}: SmoothNumberInputProps) => {
  const [displayValue, setDisplayValue] = useState(value.toString());
  const [isEditing, setIsEditing] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isDraggingRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update display value when prop value changes
  useEffect(() => {
    if (!isEditing) {
      setDisplayValue(value.toString());
    }
  }, [value, isEditing]);

  const commitValue = (rawValue: string) => {
    const normalized = rawValue.replace(",", ".");
    const numValue = Number(normalized);
    if (!Number.isNaN(numValue)) {
      onChange(numValue);
      return true;
    }
    return false;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayValue(e.target.value);
  };

  const handleInputBlur = () => {
    setIsEditing(false);

    // Apply the final value immediately on blur
    const committed = commitValue(displayValue);

    if (!committed) {
      setDisplayValue(value.toString());
    }
  };

  const handleInputFocus = () => {
    setIsEditing(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const committed = commitValue(displayValue);
      if (!committed) {
        setDisplayValue(value.toString());
      }
      e.currentTarget.select();
      return;
    }
    if (e.key === "Escape") {
      setDisplayValue(value.toString());
      e.currentTarget.blur();
      return;
    }
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();

      const direction = e.key === "ArrowUp" ? "up" : "down";
      const updateValue = () => {
        const newValue = direction === "up" ? value + step : value - step;
        onChange(newValue);
        setDisplayValue(String(newValue));
      };

      // Immediate update
      updateValue();

      // Start continuous updates if not already running
      if (!intervalRef.current) {
        intervalRef.current = setInterval(updateValue, 50); // 20fps for smooth movement
      }
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      // Stop continuous updates
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  };

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const handleMouseDown = (e: React.MouseEvent<HTMLInputElement>) => {
    if (e.button !== 0 || isEditing) return;
    // Prevent text selection / default focus — we handle focus manually
    e.preventDefault();

    const inputEl = e.currentTarget;
    const startY = e.clientY;
    const startValue = Number(displayValue) || 0;
    let hasDragged = false;
    isDraggingRef.current = true;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const deltaY = moveEvent.clientY - startY;
      if (!hasDragged && Math.abs(deltaY) < 3) return;
      if (!hasDragged) {
        hasDragged = true;
        document.body.style.cursor = "ns-resize";
      }
      let newValue = startValue + Math.round(-deltaY / 2) * step;
      if (min !== undefined && newValue < min) newValue = min;
      if (max !== undefined && newValue > max) newValue = max;
      onChange(newValue);
      setDisplayValue(String(newValue));
    };

    const onMouseUp = () => {
      isDraggingRef.current = false;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      if (!hasDragged) {
        // Treat as click — focus the input
        inputEl.focus();
        inputEl.select();
      }
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        pattern="[0-9]*[.,]?[0-9]*"
        value={displayValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onMouseDown={handleMouseDown}
        className={cn("pr-8", className)}
        style={{ cursor: isEditing ? "text" : "ns-resize" }}
        step={step}
      />
      <NumberDropdown
        value={Number.isNaN(Number(displayValue)) ? null : Number(displayValue)}
        onNumberChange={(nextValue) => {
          onChange(nextValue);
          setDisplayValue(String(nextValue));
        }}
        step={step}
        min={min}
        max={max}
        allowAngleVariables={allowAngleVariables}
        className="absolute right-2 top-1/2 -translate-y-1/2"
      />
    </div>
  );
};


type Hotspot = Tables<"hotspots"> & {
  cms_action?: CMSAction;
  cmsAction?: CMSAction; // Keep both for backwards compatibility during transition
};
type HotspotContentRecord = Record<string, unknown>;
type Vector3Like = { x?: number; y?: number; z?: number };
export type HotspotStylePreset = {
  id: string;
  name: string;
  hotspotType: string;
  content: HotspotContentRecord;
  scale?: { x: number; y: number; z: number };
  createdAt: string;
};

export const HOTSPOT_STYLE_CONTENT_KEYS: Record<string, string[]> = {
  svg: ["color", "fontColor", "iconOrSvg", "padding"],
  text: [
    "color",
    "textColor",
    "backgroundColor",
    "backgroundOpacity",
    "borderColor",
    "borderWidth",
    "borderRadius",
    "borderStyle",
    "outlineColor",
    "outlineWidth",
    "outlineStyle",
    "outlineOffset",
    "padding",
    "paddingTop",
    "paddingRight",
    "paddingBottom",
    "paddingLeft",
    "fontSize",
    "fontWeight",
    "fontFamily",
    "textAlign",
    "lineHeight",
    "letterSpacing",
    "textShadow",
    "boxShadow",
    "opacity",
    "transform",
    "textDecoration",
    "textTransform",
    "fontStyle",
    "aspectRatio",
    "customAspectRatio",
    "width",
    "height",
  ],
  html: [
    "contentType",
    "iconOrSvg",
    "iconVariable",
    "lucideIcon",
    "color",
    "textColor",
    "backgroundColor",
    "borderColor",
    "borderWidth",
    "borderRadius",
    "borderStyle",
    "outlineColor",
    "outlineWidth",
    "outlineStyle",
    "outlineOffset",
    "hoverBackgroundColor",
    "hoverBorderColor",
    "hoverTextColor",
    "hoverBoxShadow",
    "hoverTextShadow",
    "hoverOpacity",
    "hoverTransform",
    "activeBackgroundColor",
    "activeBorderColor",
    "activeTextColor",
    "activeBoxShadow",
    "activeTextShadow",
    "activeOpacity",
    "activeTransform",
    "visitedBackgroundColor",
    "visitedBorderColor",
    "visitedTextColor",
    "visitedBoxShadow",
    "visitedTextShadow",
    "visitedOpacity",
    "visitedTransform",
    "padding",
    "paddingTop",
    "paddingRight",
    "paddingBottom",
    "paddingLeft",
    "fontSize",
    "fontWeight",
    "fontFamily",
    "textAlign",
    "lineHeight",
    "letterSpacing",
    "textShadow",
    "boxShadow",
    "opacity",
    "transform",
    "textDecoration",
    "textTransform",
    "fontStyle",
    "aspectRatio",
    "customAspectRatio",
    "width",
    "height",
    "variant",
    "showPointer",
    "pointerDirection",
    "pointerSize",
    "pointerOverlap",
    "tooltipEnabled",
    "tooltipArrow",
    "tooltipArrowDirection",
    "tooltipBgColor",
    "tooltipFontColor",
    "tooltipFontSize",
    "tooltipFontWeight",
    "tooltipBorderRadius",
    "tooltipPadding",
  ],
  video: [
    "videoUrl",
    "videoLoop",
    "videoMuted",
    "videoAutoplay",
    "videoWidth",
    "videoHeight",
    "chromaKeyEnabled",
    "chromaKeyColor",
    "chromaKeySimilarity",
    "chromaKeySmoothness",
    "chromaKeySpill",
  ],
};

const HOTSPOT_STYLE_DEFAULTS: Record<string, HotspotContentRecord> = {
  svg: {
    color: "#E3F7FF",
    fontColor: "#ffffff",
    iconOrSvg: "",
    padding: 6,
  },
  text: {
    text: "",
    color: "#ffffff",
    backgroundColor: "#000000",
    backgroundOpacity: 0.7,
    borderRadius: 0,
    padding: 6,
  },
  html: {
    text: "",
    iconVariable: "",
    backgroundColor: "#FFFFFFE6",
    borderWidth: 0,
    borderColor: "#ffffff",
    textColor: "#000000",
    borderRadius: 0,
    padding: 0,
    fontSize: "12px",
    iconSize: "100%",
    fontWeight: 500,
    textAlign: "center",
    lineHeight: "1",
    textShadow: "none",
    showPointer: false,
    pointerDirection: "bottom",
    pointerSize: 8,
    pointerOverlap: 2,
    tooltipArrowDirection: "top",
    aspectRatio: "auto",
    width: "auto",
    height: "auto",
  },
  video: {
    videoUrl: "",
    videoLoop: true,
    videoMuted: true,
    videoAutoplay: true,
    videoWidth: 80,
    videoHeight: 45,
    chromaKeyEnabled: true,
    chromaKeyColor: "#00ff00",
    chromaKeySimilarity: 0.12,
    chromaKeySmoothness: 0.08,
    chromaKeySpill: 0.2,
  },
};

export const extractHotspotStyleContent = (hotspot: Hotspot): HotspotContentRecord => {
  const type = hotspot.type || "";
  const styleKeys = HOTSPOT_STYLE_CONTENT_KEYS[type] || [];
  const sourceContent = (hotspot.content as HotspotContentRecord) || {};
  return styleKeys.reduce<HotspotContentRecord>((acc, key) => {
    if (sourceContent[key] !== undefined) {
      acc[key] = sourceContent[key];
    }
    return acc;
  }, {});
};

const normalizeHotspotScale = (scale: unknown) => {
  if (!scale || typeof scale !== "object") {
    return undefined;
  }
  const scaleValue = scale as Vector3Like;
  const hasX = Number.isFinite(scaleValue.x);
  const hasY = Number.isFinite(scaleValue.y);
  if (!hasX || !hasY) {
    return undefined;
  }
  return {
    x: scaleValue.x as number,
    y: scaleValue.y as number,
    z: Number.isFinite(scaleValue.z) ? (scaleValue.z as number) : 1,
  };
};

export const normalizeStyleName = (name: string) => name.trim().replace(/\s+/g, " ").toLowerCase();
export const HOTSPOT_STYLE_PRESET_ID_KEY = "stylePresetId";
export const HOTSPOT_STYLE_PRESET_IDS_KEY = "stylePresetIds";
export const HOTSPOT_LOCAL_STYLE_CONTENT_KEY = "localStyleContent";
export const HOTSPOT_LOCAL_STYLE_ID = "local";

export const getHotspotStylePresetIds = (content: HotspotContentRecord): string[] => {
  const rawIds = content[HOTSPOT_STYLE_PRESET_IDS_KEY];
  if (Array.isArray(rawIds)) {
    return rawIds.filter((value): value is string => typeof value === "string" && value.trim().length > 0);
  }
  const rawId = content[HOTSPOT_STYLE_PRESET_ID_KEY];
  return typeof rawId === "string" && rawId.trim().length > 0 ? [rawId] : [];
};

export const getHotspotLocalStyleContent = (
  hotspotType: string,
  content: HotspotContentRecord,
): HotspotContentRecord => {
  const raw = content[HOTSPOT_LOCAL_STYLE_CONTENT_KEY];
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {};
  }
  const allowedKeys = new Set(HOTSPOT_STYLE_CONTENT_KEYS[hotspotType] || []);
  return Object.entries(raw as HotspotContentRecord).reduce<HotspotContentRecord>((acc, [key, value]) => {
    if (allowedKeys.has(key) && value !== undefined) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

const stripHotspotStyleFields = (hotspotType: string, content: HotspotContentRecord): HotspotContentRecord => {
  const nextContent = { ...content };
  for (const key of HOTSPOT_STYLE_CONTENT_KEYS[hotspotType] || []) {
    delete nextContent[key];
  }
  return nextContent;
};

export const materializeHotspotStyleContent = (
  hotspotType: string,
  baseContent: HotspotContentRecord,
  presets: HotspotStylePreset[],
  localStyleContent: HotspotContentRecord,
): HotspotContentRecord => {
  const nextContent = stripHotspotStyleFields(hotspotType, baseContent);
  const defaults = HOTSPOT_STYLE_DEFAULTS[hotspotType] || {};
  for (const [key, value] of Object.entries(defaults)) {
    if (nextContent[key] === undefined) {
      nextContent[key] = value;
    }
  }
  for (const preset of presets) {
    Object.assign(nextContent, preset.content);
  }
  Object.assign(nextContent, localStyleContent);
  const presetIds = presets.map((preset) => preset.id);
  nextContent[HOTSPOT_STYLE_PRESET_IDS_KEY] = presetIds;
  nextContent[HOTSPOT_STYLE_PRESET_ID_KEY] = presetIds[presetIds.length - 1] ?? "";
  nextContent[HOTSPOT_LOCAL_STYLE_CONTENT_KEY] = localStyleContent;
  return nextContent;
};

export const buildStyleFieldSourceMap = (
  hotspotType: string,
  presets: HotspotStylePreset[],
  localStyleContent: HotspotContentRecord,
): Record<string, string> => {
  const sourceMap: Record<string, string> = {};
  for (const preset of presets) {
    for (const key of Object.keys(preset.content || {})) {
      sourceMap[key] = preset.id;
    }
  }
  for (const key of Object.keys(localStyleContent)) {
    sourceMap[key] = HOTSPOT_LOCAL_STYLE_ID;
  }
  for (const key of HOTSPOT_STYLE_CONTENT_KEYS[hotspotType] || []) {
    if (!(key in sourceMap)) {
      sourceMap[key] = "";
    }
  }
  return sourceMap;
};

const mergeStyleValuesWithDeletion = (
  base: HotspotContentRecord,
  updates: HotspotContentRecord,
): HotspotContentRecord =>
  Object.entries(updates).reduce<HotspotContentRecord>((acc, [key, value]) => {
    if (value === undefined) {
      delete acc[key];
    } else {
      acc[key] = value;
    }
    return acc;
  }, { ...base });

const areStyleValuesEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return a === b;
  if (typeof a !== "object") return a === b;

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, idx) => areStyleValuesEqual(item, b[idx]));
  }

  const aRecord = a as Record<string, unknown>;
  const bRecord = b as Record<string, unknown>;
  const aKeys = Object.keys(aRecord);
  const bKeys = Object.keys(bRecord);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => areStyleValuesEqual(aRecord[key], bRecord[key]));
};

export const mergeContentWithPreset = (
  hotspotType: string,
  baseContent: HotspotContentRecord,
  preset: HotspotStylePreset,
) => {
  const styleKeys = HOTSPOT_STYLE_CONTENT_KEYS[hotspotType] || [];
  const nextContent: HotspotContentRecord = { ...baseContent };
  for (const key of styleKeys) {
    delete nextContent[key];
  }
  Object.assign(nextContent, preset.content);
  nextContent[HOTSPOT_STYLE_PRESET_ID_KEY] = preset.id;
  return nextContent;
};

interface PanoramaDestinationSelectProps {
  panoramas: Panorama[];
  value?: string;
  onChange: (panoramaId: string) => void;
  placeholder?: string;
  excludePanoramaId?: string;
}

const PanoramaDestinationSelect = ({
  panoramas,
  value,
  onChange,
  placeholder = "Select panorama",
  excludePanoramaId,
}: PanoramaDestinationSelectProps) => {
  const [open, setOpen] = useState(false);

  const availablePanoramas = useMemo(
    () => panoramas.filter((panorama) => panorama.id !== excludePanoramaId),
    [panoramas, excludePanoramaId],
  );

  const selectedPanorama = availablePanoramas.find((panorama) => panorama.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-7 w-full justify-between bg-gray-700/60 border-0 px-3 pr-2 text-xs text-white hover:bg-gray-700/70"
        >
          <span className="truncate">
            {selectedPanorama ? selectedPanorama.name : placeholder}
          </span>
          <ChevronDown size={14} className="ml-2 shrink-0 opacity-70" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 bg-gray-800 border-gray-700">
        <Command className="bg-gray-800">
          <CommandInput
            placeholder="Search panoramas..."
            className="h-8 text-xs text-white bg-gray-700/60 border-0"
          />
          <CommandList className="max-h-64">
            <CommandEmpty className="text-gray-400 text-xs">No panoramas found.</CommandEmpty>
            <CommandGroup>
              {availablePanoramas.map((panorama) => (
                <CommandItem
                  key={panorama.id}
                  value={panorama.name}
                  onSelect={() => {
                    onChange(panorama.id);
                    setOpen(false);
                  }}
                  className="text-xs text-white cursor-pointer hover:bg-gray-700 data-[selected=true]:bg-gray-700"
                >
                  <span className="truncate">{panorama.name}</span>
                  {value === panorama.id && (
                    <Check className="ml-auto h-3.5 w-3.5 text-blue-400" />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

interface PropertiesPanelProps {
  updateHotspotImpl?: (hotspotId: string, updates: Partial<Hotspot>) => Promise<void>;
  deleteHotspotImpl?: (hotspotId: string) => Promise<void>;
  duplicateHotspotImpl?: (hotspot: Hotspot) => Promise<void>;
  skinComponents: Array<{ id: string; name: string; type: string; parentId?: string }>;
}

interface NotificationDialogProps {
  onAdd: (title: string, message: string, autoHideDuration?: number) => void;
  initialTitle?: string;
  initialMessage?: string;
  initialAutoHideDuration?: number;
}

export const NotificationDialog = ({
  onAdd,
  initialTitle = "",
  initialMessage = "",
  initialAutoHideDuration,
}: NotificationDialogProps) => {
  const [title, setTitle] = useState(initialTitle);
  const [message, setMessage] = useState(initialMessage);
  const [notificationStyle, setNotificationStyle] = useState<"banner" | "alert">(
    initialAutoHideDuration !== undefined ? "banner" : "banner",
  );
  const [autoHideDuration, setAutoHideDuration] = useState(initialAutoHideDuration || 4);
  const [autoHideDurationInput, setAutoHideDurationInput] = useState(
    String(initialAutoHideDuration || 4),
  );

  useEffect(() => {
    setAutoHideDurationInput(String(autoHideDuration));
  }, [autoHideDuration]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if ((title || "").trim() || (message || "").trim()) {
      const duration = notificationStyle === "banner" ? autoHideDuration : undefined;
      onAdd((title || "").trim(), (message || "").trim(), duration);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-4">
        <Label className="text-white text-sm font-medium">Notification Style</Label>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setNotificationStyle("banner")}
            className={`p-4 rounded-lg border-2 transition-all ${
              notificationStyle === "banner"
                ? "border-blue-500 bg-blue-500/10"
                : "border-gray-600 bg-gray-700 hover:border-gray-500"
            }`}
          >
            <div className="flex flex-col items-center space-y-2">
              <div className="w-16 h-12 bg-gray-800 rounded border border-gray-600 relative">
                <div className="w-full h-3 bg-gray-600 rounded-t"></div>
                <div className="absolute top-4 left-2 right-2 h-2 bg-white rounded"></div>
              </div>
              <span
                className={`text-xs font-medium ${notificationStyle === "banner" ? "text-blue-400" : "text-gray-300"}`}
              >
                Banners
              </span>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setNotificationStyle("alert")}
            className={`p-4 rounded-lg border-2 transition-all ${
              notificationStyle === "alert"
                ? "border-blue-500 bg-blue-500/10"
                : "border-gray-600 bg-gray-700 hover:border-gray-500"
            }`}
          >
            <div className="flex flex-col items-center space-y-2">
              <div className="w-16 h-12 bg-gray-800 rounded border border-gray-600 relative">
                <div className="w-full h-3 bg-gray-600 rounded-t"></div>
                <div className="absolute top-4 left-2 right-2 h-2 bg-white rounded"></div>
              </div>
              <span
                className={`text-xs font-medium ${notificationStyle === "alert" ? "text-blue-400" : "text-gray-300"}`}
              >
                Alerts
              </span>
            </div>
          </button>
        </div>

        <div className="text-xs text-gray-400">
          {notificationStyle === "banner" && (
            <p>Banners appear briefly and disappear automatically.</p>
          )}
          {notificationStyle === "alert" && (
            <p>Alerts stay on screen until dismissed by the user.</p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="notification-title" className="text-white">
          Notification Title
        </Label>
        <Input
          id="notification-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter notification title..."
          className="bg-gray-700 border-gray-600 text-white"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="notification-message" className="text-white">
          Notification Message
        </Label>
        <Textarea
          id="notification-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Enter the notification message that will be shown when someone clicks this hotspot..."
          className="bg-gray-700 border-gray-600 text-white"
          rows={3}
        />
      </div>
      <div className="space-y-2">
        <Label
          htmlFor="auto-hide-duration"
          className={`text-sm ${notificationStyle === "alert" ? "text-gray-500" : "text-white"}`}
        >
          Hide after (seconds)
        </Label>
        <Input
          id="auto-hide-duration"
          type="text"
          inputMode="decimal"
          pattern="[0-9]*[.,]?[0-9]*"
          min="1"
          max="60"
          value={notificationStyle === "alert" ? "" : autoHideDurationInput}
          placeholder={notificationStyle === "alert" ? "-" : undefined}
          onChange={(e) => {
            const raw = e.target.value;
            setAutoHideDurationInput(raw);
          }}
          onBlur={() => {
            if (notificationStyle === "alert") return;
            const normalized = autoHideDurationInput.replace(",", ".");
            const numericValue = parseFloat(normalized);
            if (!Number.isNaN(numericValue)) {
              const clamped = Math.max(1, Math.min(60, numericValue));
              setAutoHideDuration(clamped);
            } else {
              setAutoHideDurationInput(String(autoHideDuration));
            }
          }}
          onKeyDown={(e) => {
            if (notificationStyle === "alert") return;
            if (e.key === "Enter") {
              const normalized = autoHideDurationInput.replace(",", ".");
              const numericValue = parseFloat(normalized);
              if (!Number.isNaN(numericValue)) {
                const clamped = Math.max(1, Math.min(60, numericValue));
                setAutoHideDuration(clamped);
              } else {
                setAutoHideDurationInput(String(autoHideDuration));
              }
              e.currentTarget.select();
            } else if (e.key === "Escape") {
              setAutoHideDurationInput(String(autoHideDuration));
              e.currentTarget.blur();
            }
          }}
          disabled={notificationStyle === "alert"}
          className={`border-gray-600 text-white w-20 ${
            notificationStyle === "alert"
              ? "bg-gray-800 text-gray-500 cursor-not-allowed"
              : "bg-gray-700"
          }`}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="submit" disabled={!(title || "").trim() && !(message || "").trim()}>
          Add Notification
        </Button>
      </div>
    </form>
  );
};

interface DownloadDialogProps {
  onAdd: (fileUrl: string, fileName: string) => void;
  initialFileUrl?: string;
  initialFileName?: string;
}

export const DownloadDialog = ({
  onAdd,
  initialFileUrl = "",
  initialFileName = "",
}: DownloadDialogProps) => {
  const [fileUrl, setFileUrl] = useState(initialFileUrl);
  const [fileName, setFileName] = useState(initialFileName);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if ((fileUrl || "").trim() && (fileName || "").trim()) {
      onAdd((fileUrl || "").trim(), (fileName || "").trim());
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="file-url" className="text-white">
          File URL
        </Label>
        <Input
          id="file-url"
          type="url"
          value={fileUrl}
          onChange={(e) => setFileUrl(e.target.value)}
          placeholder="https://example.com/file.pdf"
          className="bg-gray-700 border-gray-600 text-white"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="file-name" className="text-white">
          File Name
        </Label>
        <Input
          id="file-name"
          type="text"
          value={fileName}
          onChange={(e) => setFileName(e.target.value)}
          placeholder="document.pdf"
          className="bg-gray-700 border-gray-600 text-white"
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="submit" disabled={!(fileUrl || "").trim() || !(fileName || "").trim()}>
          Add Download
        </Button>
      </div>
    </form>
  );
};

export const PropertiesPanel = ({
  updateHotspotImpl,
  deleteHotspotImpl,
  duplicateHotspotImpl,
  skinComponents,
}: PropertiesPanelProps) => {
  // Get all state from the store
  const {
    project,
    projectBackend,
    selectedHotspot,
    selectedHotspotIds,
    hotspots,
    panoramas,
    placementMode,
    changePanorama,
    showHotspots,
    hideHotspots,
    chromaKeyPickHotspotId,
    setChromaKeyPickHotspotId,
    setProject,
  } = useEditorStore();
  const {
    getCombinedVariables,
    getProjectVariables,
    getCraftspaceVariables,
    projectVariables,
    craftspaceVariables,
  } = useVariablesStore();
  const navigate = useNavigate();
  const viewportControls = useViewportControls();

  // Get actions from custom hook
  const { updateHotspotFromPanel, deleteHotspotFromPanel } = useEditorActions(
    undefined,
    updateHotspotImpl,
    undefined,
    deleteHotspotImpl,
  );
  const convex = useConvex();
  const { toast } = useToast();
  const useLocalBackend = isElectron() && projectBackend === "local";

  // Add timeout ref for debouncing updates
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [localHotspot, setLocalHotspot] = useState<Hotspot | null>(null);
  const [borderWidthInput, setBorderWidthInput] = useState<string>("0");
  const [borderRadiusInput, setBorderRadiusInput] = useState<string>("0");
  const [borderStyleState, setBorderStyleState] = useState<string>("solid");
  const [outlineStyleState, setOutlineStyleState] = useState<string>("none");
  const [outlineWidthInput, setOutlineWidthInput] = useState<string>("0");
  const [outlineOffsetInput, setOutlineOffsetInput] = useState<string>("0");
  const [borderTabActive, setBorderTabActive] = useState<"border" | "outline">("border");
  const [facingTabActive, setFacingTabActive] = useState<"billboard" | "3d">("billboard");
  const [bordersEnabled, setBordersEnabled] = useState<boolean>(false);
  const [facingEnabled, setFacingEnabled] = useState<boolean>(false);
  const [htmlStyleTab, setHtmlStyleTab] = useState<"base" | "hover" | "active" | "visited">("base");
  const [paddingInput, setPaddingInput] = useState<string>("0.3");
  const [paddingTopInput, setPaddingTopInput] = useState<string>("");

  const projectId = useMemo(() => {
    if (!project) return undefined;
    return typeof project.id === "string" ? project.id : (project as any)._id;
  }, [project]);

  const craftspaceId = project?.craftspace_id;
  const projectData = project?.data as Record<string, unknown> | null;
  const storageSegments = project
    ? resolveStoragePathSegments({
        craftspaceId: project.craftspace_id || "default",
        projectId: project.id,
        projectName: project.name,
        projectData,
        storagePath: typeof projectData?.storage_path === "string" ? projectData.storage_path : null,
      })
    : { craftspaceKey: "default", projectKey: "default" };

  const [paddingRightInput, setPaddingRightInput] = useState<string>("");
  const [paddingBottomInput, setPaddingBottomInput] = useState<string>("");
  const [paddingLeftInput, setPaddingLeftInput] = useState<string>("");
  const [showPaddingFields, setShowPaddingFields] = useState<boolean>(false);
  const [fontWeightInput, setFontWeightInput] = useState<string>("500");
  const [fontSizeInput, setFontSizeInput] = useState<string>("12px");
  const [iconSizeInput, setIconSizeInput] = useState<string>("100%");
  const sizeVariables = useMemo(() => {
    if (!project?.craftspace_id || !project?.id) return [];
    return getCombinedVariables(project.craftspace_id, project.id).sizes;
  }, [getCombinedVariables, project?.id, project?.craftspace_id]);

  const projectIconVariables = useMemo(() => {
    if (!project?.id) return [];
    return getProjectVariables(project.id).icons || [];
  }, [getProjectVariables, project?.id, projectVariables]);

  const craftspaceIconVariables = useMemo(() => {
    if (!project?.craftspace_id) return [];
    return getCraftspaceVariables(project.craftspace_id).icons || [];
  }, [getCraftspaceVariables, project?.craftspace_id, craftspaceVariables]);

  const craftspaceOnlyIconVariables = useMemo(
    () =>
      craftspaceIconVariables.filter(
        (wsIcon) => !projectIconVariables.some((projIcon) => projIcon.name === wsIcon.name),
      ),
    [projectIconVariables, craftspaceIconVariables],
  );
  const preferredIconOrder = [
    "Info",
    "ChevronUp",
    "ChevronDown",
    "ChevronLeft",
    "ChevronRight",
    "Plus",
    "Play",
    "CirclePlay",
    "Heart",
    "Star",
    "Volume1",
    "Volume2",
    "VolumeOff",
    "VolumeX",
    "ShoppingCart",
    "Map",
    "MapPin",
    "MoveUpRight",
    "SquareArrowOutUpRight",
  ];
  const lucideIconNames = useMemo(() => {
    const names = Object.keys(icons).filter((name) => /^[A-Z]/.test(name));
    const preferredSet = new Set(preferredIconOrder);
    const orderedPreferred = preferredIconOrder.filter((name) => names.includes(name));
    const remaining = names
      .filter((name) => !preferredSet.has(name))
      .sort((a, b) => a.localeCompare(b));
    return [...orderedPreferred, ...remaining];
  }, []);
  const getIconVariableByName = useCallback(
    (name: string) => {
      if (!project?.craftspace_id || !project?.id) return undefined;
      const combined = getCombinedVariables(project.craftspace_id, project.id).icons;
      return (
        combined.find((iconVar) => iconVar.name === name) ||
        combined.find((iconVar) => iconVar.name.toLowerCase() === name.toLowerCase())
      );
    },
    [getCombinedVariables, project?.id, project?.craftspace_id],
  );

  const iconContentSnapshot = useMemo(
    () => (localHotspot?.content as HotspotContentRecord) || {},
    [localHotspot?.content],
  );
  const iconVariableName =
    typeof iconContentSnapshot.iconVariable === "string"
      ? (iconContentSnapshot.iconVariable as string)
      : "";
  const iconVariableValue = iconVariableName
    ? getIconVariableByName(iconVariableName)?.value || ""
    : "";
  const allIconVariableNames = useMemo(
    () =>
      [
        ...craftspaceOnlyIconVariables.map((iconVar) => iconVar.name),
        ...projectIconVariables.map((iconVar) => iconVar.name),
      ].filter(Boolean),
    [projectIconVariables, craftspaceOnlyIconVariables],
  );

  useEffect(() => {
    setIconVariableInput(iconVariableName || "");
  }, [iconVariableName, localHotspot?.id]);

  const resolveSizeVariableInput = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed || trimmed.startsWith("var(")) return null;
    const normalized = trimmed.toLowerCase();
    const exact = sizeVariables.find((variable) => variable.name.toLowerCase() === normalized);
    if (exact) return exact;
    const matches = sizeVariables.filter((variable) =>
      variable.name.toLowerCase().startsWith(normalized),
    );
    if (matches.length === 1) return matches[0];
    return null;
  };

  const displaySizeValue = (value: string) => {
    const match = value.trim().match(/^var\(--([^)]+)\)$/);
    if (!match) return value;
    return match[1];
  };

  const applySizeVariableInput = (
    raw: string,
    setInput: (next: string) => void,
    applyValue: (value: string) => void,
  ) => {
    const match = resolveSizeVariableInput(raw);
    if (!match) return false;
    const ref = toCSSVarReference(match.name);
    setInput(match.name);
    applyValue(ref);
    return true;
  };
  const tryAutocompleteSizeName = (
    raw: string,
    inputEl: HTMLInputElement | null,
    setInput: (next: string) => void,
  ) => {
    const normalized = raw.trim().toLowerCase();
    if (!normalized || normalized.startsWith("var(") || /[0-9]/.test(normalized)) return false;
    const matches = sizeVariables.filter((variable) =>
      variable.name.toLowerCase().startsWith(normalized),
    );
    if (matches.length !== 1) return false;
    const match = matches[0].name;
    if (match.toLowerCase() === normalized) return false;
    setInput(match);
    if (inputEl) {
      const start = normalized.length;
      requestAnimationFrame(() => {
        try {
          inputEl.setSelectionRange(start, match.length);
        } catch {
          // Ignore selection errors for non-text inputs
        }
      });
    }
    return true;
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);
  const [isConfettiDialogOpen, setIsConfettiDialogOpen] = useState(false);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const [hotspotTab, setHotspotTab] = useState<"style" | "settings" | "actions">("style");
  const hotspotPanelTab = useEditorStore((state) => state.hotspotPanelTab);
  const setHotspotPanelTab = useEditorStore((state) => state.setHotspotPanelTab);
  const hotspotTextAutoFocus = useEditorStore((state) => state.hotspotTextAutoFocus);
  const setHotspotTextAutoFocus = useEditorStore((state) => state.setHotspotTextAutoFocus);

  useEffect(() => {
    if (!hotspotPanelTab) return;
    setHotspotTab(hotspotPanelTab);
    setHotspotPanelTab(null);
  }, [hotspotPanelTab, setHotspotPanelTab]);
  const [isNavigateExpanded, setIsNavigateExpanded] = useState(true);
  const [expandedSkinComponents, setExpandedSkinComponents] = useState<Set<string>>(new Set());
  const [expandedActionIds, setExpandedActionIds] = useState<Set<string>>(new Set());
  const [hotspotVisibilityConfigs, setHotspotVisibilityConfigs] = useState<Record<string, CollectionItemsConfig>>({});
  // Legacy fallback - kept for backward compat with single-action UI
  const [hotspotVisibilityConfig, setHotspotVisibilityConfig] = useState<CollectionItemsConfig>({
    mode: "selected",
    filters: [],
    sort: null,
    selectedItemIds: [],
  });
  const [videoUploadName, setVideoUploadName] = useState("");
  const [isVideoUploading, setIsVideoUploading] = useState(false);
  const [downloadUploadName, setDownloadUploadName] = useState("");
  const [isDownloadUploading, setIsDownloadUploading] = useState(false);
  const [iconPopoverOpen, setIconPopoverOpen] = useState(false);
  const [iconVariablePopoverOpen, setIconVariablePopoverOpen] = useState(false);
  const [iconVariableInput, setIconVariableInput] = useState("");
  const [emojiPopoverOpen, setEmojiPopoverOpen] = useState(false);
  const [linkTransitionType, setLinkTransitionType] = useState<
    "default" | "same-view" | "slide-to" | "morph"
  >("default");
  const nameInputRef = useRef<HTMLInputElement>(null);
  const iconVariableInputRef = useRef<HTMLInputElement>(null);
  const [nameInputValue, setNameInputValue] = useState(localHotspot?.name || "");
  const [isEditingName, setIsEditingName] = useState(false);
  const lastSelectedHotspotRef = useRef<string | null>(null);
  const wasInPlacementMode = useRef<boolean>(false);
  const copiedHotspotRef = useRef<Hotspot | null>(null);
  const [_showBulkEdit, setShowBulkEdit] = useState(false);
  const [stylePresets, setStylePresets] = useState<HotspotStylePreset[]>([]);
  const [stylePresetInputValue, setStylePresetInputValue] = useState("");
  const [selectedStylePresetId, setSelectedStylePresetId] = useState<string>(HOTSPOT_LOCAL_STYLE_ID);
  const [hoveredStyleSourceId, setHoveredStyleSourceId] = useState<string | null>(null);
  const [isStyleDropdownOpen, setIsStyleDropdownOpen] = useState(false);
  const [deleteConfirmPopoverOpen, setDeleteConfirmPopoverOpen] = useState(false);
  const [presetToDelete, setPresetToDelete] = useState<HotspotStylePreset | null>(null);
  const [deleteButtonPosition, setDeleteButtonPosition] = useState<{ top: number; left: number } | null>(null);
  const stylePresetInputRef = useRef<HTMLInputElement>(null);
  const { hotspotRenderStyle, isAspectRatioLocked, setIsAspectRatioLocked } = useEditorStore((state) => ({
    hotspotRenderStyle: state.hotspotRenderStyle,
    isAspectRatioLocked: state.isAspectRatioLocked,
    setIsAspectRatioLocked: state.setIsAspectRatioLocked,
  }));

  const facingModeForTab = useMemo<"billboard" | "3d">(() => {
    const renderStyle = (localHotspot?.content as HotspotContentRecord | undefined)?.renderStyle;
    if (renderStyle === "3d" || renderStyle === "billboard") return renderStyle;
    return hotspotRenderStyle;
  }, [localHotspot, hotspotRenderStyle]);

  useEffect(() => {
    setFacingTabActive(facingModeForTab);
  }, [facingModeForTab]);

  useEffect(() => {
    if (!localHotspot) return;
    const currentContent = (localHotspot.content as HotspotContentRecord) || {};
    const hasBorders =
      Number(currentContent.borderWidth ?? 0) > 0 ||
      Number(currentContent.outlineWidth ?? 0) > 0;
    setBordersEnabled(hasBorders);
  }, [localHotspot?.id]);

  useEffect(() => {
    if (!localHotspot) return;
    const currentContent = (localHotspot.content as HotspotContentRecord) || {};
    const renderStyle = currentContent.renderStyle;
    const hasFacing = renderStyle === "3d";
    setFacingEnabled(hasFacing);
  }, [localHotspot?.id]);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);

  const stylePresetStorageKey = useMemo(
    () => `hotspotStylePresets:${project?.id || "global"}`,
    [project?.id],
  );

  // Note: CSS variable watching is now handled globally in Viewer360 component

  const persistStylePresets = useCallback(
    async (presets: HotspotStylePreset[]) => {
      setStylePresets(presets);
      if (!project) return;
      const currentData =
        (project.data as Record<string, unknown> | null | undefined) ?? {};
      const nextData = { ...currentData, hotspotStylePresets: presets };
      if (useLocalBackend) {
        await updateLocalProject({
          id: project.id,
          craftspace_id: project.craftspace_id || "default",
          data: nextData,
        });
      } else {
        await convex.mutation(api.projects.update, { id: project.id, data: nextData });
      }
      setProject({ ...project, data: nextData });
    },
    [convex, project, setProject, useLocalBackend],
  );

  const getTooltipEl = useCallback(() => {
    if (!localHotspot?.id) return null;
    return document.querySelector(
      `[data-hotspot-id="${localHotspot.id}"] .html-hotspot-tooltip`,
    ) as HTMLElement | null;
  }, [localHotspot?.id]);

  const getHotspotEl = useCallback(() => {
    if (!localHotspot?.id) return null;
    return document.querySelector(
      `[data-hotspot-id="${localHotspot.id}"]`,
    ) as HTMLElement | null;
  }, [localHotspot?.id]);

  const showTooltip = useCallback(() => {
    const hotspotEl = getHotspotEl();
    if (hotspotEl) hotspotEl.dataset.tooltipPreview = "true";
    const tooltipEl = getTooltipEl();
    if (tooltipEl) tooltipEl.style.display = "block";
  }, [getHotspotEl, getTooltipEl]);

  const hideTooltip = useCallback(() => {
    const hotspotEl = getHotspotEl();
    if (hotspotEl) delete hotspotEl.dataset.tooltipPreview;
    const tooltipEl = getTooltipEl();
    if (tooltipEl) tooltipEl.style.display = "none";
  }, [getHotspotEl, getTooltipEl]);

  const tooltipArrowDirections = useMemo(
    () => new Set(["top", "right", "bottom", "left"]),
    [],
  );

  const resolveTooltipArrowDirection = useCallback(
    (value: unknown, fallback: string) => {
      if (typeof value === "string") {
        const normalized = value
          .trim()
          .replace(/([a-z])([A-Z])/g, "$1-$2")
          .replace(/[_\\s]+/g, "-")
          .toLowerCase();
        if (tooltipArrowDirections.has(normalized)) return normalized;
        if (normalized.includes("bottom")) return "bottom";
        if (normalized.includes("top")) return "top";
        if (normalized.includes("right")) return "right";
        if (normalized.includes("left")) return "left";
      }
      return fallback;
    },
    [tooltipArrowDirections],
  );

  const applyTooltipPlacement = useCallback((tooltipEl: HTMLElement, direction: string, offset: number) => {
    tooltipEl.style.top = "";
    tooltipEl.style.bottom = "";
    tooltipEl.style.left = "";
    tooltipEl.style.right = "";
    tooltipEl.style.transform = "";
    tooltipEl.style.marginTop = "0";
    tooltipEl.style.marginBottom = "0";
    tooltipEl.style.marginLeft = "0";
    tooltipEl.style.marginRight = "0";

    switch (direction) {
      case "top":
      case "top-left":
      case "top-right":
        tooltipEl.style.top = "100%";
        tooltipEl.style.left = "50%";
        tooltipEl.style.transform = "translateX(-50%)";
        tooltipEl.style.marginTop = `${offset}px`;
        break;
      case "bottom":
      case "bottom-left":
      case "bottom-right":
        tooltipEl.style.bottom = "100%";
        tooltipEl.style.left = "50%";
        tooltipEl.style.transform = "translateX(-50%)";
        tooltipEl.style.marginBottom = `${offset}px`;
        break;
      case "left":
        tooltipEl.style.left = "100%";
        tooltipEl.style.top = "50%";
        tooltipEl.style.transform = "translateY(-50%)";
        tooltipEl.style.marginLeft = `${offset}px`;
        break;
      case "right":
        tooltipEl.style.right = "100%";
        tooltipEl.style.top = "50%";
        tooltipEl.style.transform = "translateY(-50%)";
        tooltipEl.style.marginRight = `${offset}px`;
        break;
    }
  }, []);

  const applyTooltipArrowStyles = useCallback((arrowEl: HTMLElement, direction: string, color: string) => {
    const size = 6;
    arrowEl.style.position = "absolute";
    arrowEl.style.top = "";
    arrowEl.style.bottom = "";
    arrowEl.style.left = "";
    arrowEl.style.right = "";
    arrowEl.style.transformOrigin = "center";

    switch (direction) {
      case "top":
        arrowEl.style.top = `${-size}px`;
        arrowEl.style.left = "50%";
        arrowEl.style.transform = "translateX(-50%)";
        break;
      case "bottom":
        arrowEl.style.bottom = `${-size}px`;
        arrowEl.style.left = "50%";
        arrowEl.style.transform = "translateX(-50%)";
        break;
      case "left":
        arrowEl.style.left = `${-size}px`;
        arrowEl.style.top = "50%";
        arrowEl.style.transform = "translateY(-50%)";
        break;
      case "right":
        arrowEl.style.right = `${-size}px`;
        arrowEl.style.top = "50%";
        arrowEl.style.transform = "translateY(-50%)";
        break;
      case "top-left":
        arrowEl.style.top = `${-size}px`;
        arrowEl.style.left = `${-size}px`;
        break;
      case "top-right":
        arrowEl.style.top = `${-size}px`;
        arrowEl.style.right = `${-size}px`;
        break;
      case "bottom-right":
        arrowEl.style.bottom = `${-size}px`;
        arrowEl.style.right = `${-size}px`;
        break;
      case "bottom-left":
        arrowEl.style.bottom = `${-size}px`;
        arrowEl.style.left = `${-size}px`;
        break;
    }

    arrowEl.style.width = "0";
    arrowEl.style.height = "0";
    arrowEl.style.borderStyle = "solid";
    arrowEl.style.borderTopWidth = "0";
    arrowEl.style.borderRightWidth = "0";
    arrowEl.style.borderBottomWidth = "0";
    arrowEl.style.borderLeftWidth = "0";
    arrowEl.style.borderTopColor = "transparent";
    arrowEl.style.borderRightColor = "transparent";
    arrowEl.style.borderBottomColor = "transparent";
    arrowEl.style.borderLeftColor = "transparent";
    arrowEl.style.transform = arrowEl.style.transform || "";

    switch (direction) {
      case "top":
        arrowEl.style.borderLeftWidth = `${size}px`;
        arrowEl.style.borderRightWidth = `${size}px`;
        arrowEl.style.borderBottomWidth = `${size}px`;
        arrowEl.style.borderBottomColor = color;
        break;
      case "bottom":
        arrowEl.style.borderLeftWidth = `${size}px`;
        arrowEl.style.borderRightWidth = `${size}px`;
        arrowEl.style.borderTopWidth = `${size}px`;
        arrowEl.style.borderTopColor = color;
        break;
      case "left":
        arrowEl.style.borderTopWidth = `${size}px`;
        arrowEl.style.borderBottomWidth = `${size}px`;
        arrowEl.style.borderRightWidth = `${size}px`;
        arrowEl.style.borderRightColor = color;
        break;
      case "right":
        arrowEl.style.borderTopWidth = `${size}px`;
        arrowEl.style.borderBottomWidth = `${size}px`;
        arrowEl.style.borderLeftWidth = `${size}px`;
        arrowEl.style.borderLeftColor = color;
        break;
      case "top-left":
      case "top-right":
      case "bottom-right":
      case "bottom-left": {
        arrowEl.style.borderLeftWidth = `${size}px`;
        arrowEl.style.borderRightWidth = `${size}px`;
        arrowEl.style.borderBottomWidth = `${size}px`;
        arrowEl.style.borderBottomColor = color;
        const rotation =
          direction === "top-left"
            ? "-45deg"
            : direction === "top-right"
              ? "45deg"
              : direction === "bottom-right"
                ? "135deg"
                : "-135deg";
        arrowEl.style.transform = `${arrowEl.style.transform} rotate(${rotation})`.trim();
        break;
      }
    }
  }, []);

  const updateTooltipDOM = useCallback(
    (updates: {
      text?: string;
      bgColor?: string;
      fontColor?: string;
      fontSize?: string;
      fontWeight?: string;
      borderRadius?: string;
      padding?: string;
      arrowDirection?: string;
    }) => {
      const tooltipEl = getTooltipEl();
      if (!tooltipEl) return;
      if (updates.text !== undefined) tooltipEl.textContent = updates.text;
      if (updates.bgColor !== undefined) {
        tooltipEl.style.backgroundColor = updates.bgColor;
        const arrowEl = tooltipEl.querySelector(".html-hotspot-tooltip-arrow") as HTMLElement | null;
        const currentDirection = resolveTooltipArrowDirection(
          updates.arrowDirection,
          resolveTooltipArrowDirection(
            (localHotspot?.content as HotspotContentRecord)?.tooltipArrowDirection,
            "top",
          ),
        );
        if (arrowEl) applyTooltipArrowStyles(arrowEl, currentDirection, updates.bgColor);
      }
      if (updates.fontColor !== undefined) tooltipEl.style.color = updates.fontColor;
      if (updates.fontSize !== undefined) tooltipEl.style.fontSize = updates.fontSize;
      if (updates.fontWeight !== undefined) tooltipEl.style.fontWeight = updates.fontWeight;
      if (updates.borderRadius !== undefined) tooltipEl.style.borderRadius = updates.borderRadius;
      if (updates.padding !== undefined) tooltipEl.style.padding = updates.padding;
      if (updates.arrowDirection !== undefined) {
        const direction = resolveTooltipArrowDirection(updates.arrowDirection, "top");
        applyTooltipPlacement(tooltipEl, direction, 8);
        const arrowEl = tooltipEl.querySelector(".html-hotspot-tooltip-arrow") as HTMLElement | null;
        if (arrowEl) {
          const bgColor =
            (updates.bgColor as string) ||
            (tooltipEl.style.backgroundColor as string) ||
            "#000000CC";
          applyTooltipArrowStyles(arrowEl, direction, bgColor);
        }
      }
    },
    [
      applyTooltipArrowStyles,
      applyTooltipPlacement,
      getTooltipEl,
      localHotspot?.content,
      resolveTooltipArrowDirection,
    ],
  );

  // Initialize transition type from hotspot content
  useEffect(() => {
    if (!localHotspot) return;
    const existingTransitionType = (localHotspot.content as HotspotContentRecord)
      ?.linkTransitionType;
    setLinkTransitionType(
      existingTransitionType === "morph" ||
        existingTransitionType === "morph-1" ||
        existingTransitionType === "morph-2" ||
        existingTransitionType === "morph-3" ||
        existingTransitionType === "morph-screen-space" ||
        existingTransitionType === "morph-camera-driven"
        ? "morph"
        : existingTransitionType || "default",
    );
  }, [localHotspot]);

  // Keyboard shortcuts for tab switching and duplication
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle shortcuts if a hotspot is selected
      if (!selectedHotspot) return;

      // Prevent shortcuts when typing in input fields
      const target = event.target as HTMLElement | null;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        target?.isContentEditable ||
        target?.closest(".ProseMirror")
      ) {
        return;
      }

      // Command+C (Mac) or Ctrl+C (Windows/Linux) to copy hotspot
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "c") {
        event.preventDefault();
        copiedHotspotRef.current = { ...selectedHotspot };
        return;
      }

      // Command+V (Mac) or Ctrl+V (Windows/Linux) to paste hotspot
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "v") {
        if (!copiedHotspotRef.current) return;
        event.preventDefault();
        if (duplicateHotspotImpl) {
          duplicateHotspotImpl(copiedHotspotRef.current);
        }
        return;
      }

      // Command+D (Mac) or Ctrl+D (Windows/Linux) to duplicate hotspot
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "d") {
        event.preventDefault();
        if (duplicateHotspotImpl) {
          duplicateHotspotImpl(selectedHotspot);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedHotspot, duplicateHotspotImpl]);

  // Clear delete confirmation when dropdown closes
  useEffect(() => {
    if (!isStyleDropdownOpen) {
      setDeleteConfirmPopoverOpen(false);
      setPresetToDelete(null);
    }
  }, [isStyleDropdownOpen]);

  // ESC key to close delete confirmation popover, ENTER to confirm delete
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!deleteConfirmPopoverOpen) return;

      if (event.key === "Escape") {
        event.preventDefault();
        setDeleteConfirmPopoverOpen(false);
        setPresetToDelete(null);
      } else if (event.key === "Enter") {
        event.preventDefault();
        if (presetToDelete) {
          handleDeleteStylePreset(presetToDelete.id);
          setIsStyleDropdownOpen(false);
          setDeleteConfirmPopoverOpen(false);
          setPresetToDelete(null);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [deleteConfirmPopoverOpen, presetToDelete]);

  useEffect(() => {
    setLocalHotspot(selectedHotspot);

    // Only auto-focus if this hotspot was just created in placement mode
    if (selectedHotspot?.id !== lastSelectedHotspotRef.current && wasInPlacementMode.current) {
      setTimeout(() => {
        nameInputRef.current?.focus();
        nameInputRef.current?.select();
      }, 100);
    }

    // Tab state is now persisted in localStorage, so no need to restore here

    // Update the ref with current hotspot ID
    lastSelectedHotspotRef.current = selectedHotspot?.id || null;
  }, [selectedHotspot]);

  // Track placement mode changes
  useEffect(() => {
    wasInPlacementMode.current = placementMode;
  }, [placementMode]);

  useEffect(() => {
    setNameInputValue(localHotspot?.name || "");
    setIsEditingName(false);
  }, [localHotspot?.name]);

  useEffect(() => {
    if (isEditingName) {
      nameInputRef.current?.focus();
      nameInputRef.current?.select();
    }
  }, [isEditingName]);

  useEffect(() => {
    const sanitizePresets = (value: unknown) =>
      Array.isArray(value)
        ? value.filter(
            (item): item is HotspotStylePreset =>
              item &&
              typeof item === "object" &&
              typeof item.id === "string" &&
              typeof item.name === "string" &&
              typeof item.hotspotType === "string" &&
              item.content &&
              typeof item.content === "object",
          )
        : [];

    const projectPresets = sanitizePresets(
      (project?.data as Record<string, unknown> | null | undefined)?.hotspotStylePresets,
    );

    if (projectPresets.length > 0) {
      setStylePresets(projectPresets);
      setStylePresetInputValue("");
      return;
    }

    const raw = localStorage.getItem(stylePresetStorageKey);
    if (!raw) {
      setStylePresets([]);
      setStylePresetInputValue("");
      setSelectedStylePresetId(HOTSPOT_LOCAL_STYLE_ID);
      return;
    }

    try {
      const parsed = JSON.parse(raw);
      const sanitized = sanitizePresets(parsed);
      setStylePresets(sanitized);
      if (sanitized.length > 0) {
        void persistStylePresets(sanitized);
        localStorage.removeItem(stylePresetStorageKey);
      }
    } catch {
      setStylePresets([]);
    }
    setStylePresetInputValue("");
    setSelectedStylePresetId(HOTSPOT_LOCAL_STYLE_ID);
  }, [project?.data, stylePresetStorageKey, persistStylePresets]);

  const compatibleStylePresets = useMemo(
    () => stylePresets.filter((preset) => preset.hotspotType === (localHotspot?.type || "")),
    [stylePresets, localHotspot?.type],
  );

  useEffect(() => {
    if (!localHotspot) {
      setStylePresetInputValue("");
      setSelectedStylePresetId(HOTSPOT_LOCAL_STYLE_ID);
      return;
    }
    const currentContent = (localHotspot.content as HotspotContentRecord) || {};
    const attachedPresetIds = getHotspotStylePresetIds(currentContent);
    const attachedPreset = attachedPresetIds
      .map((presetId) => compatibleStylePresets.find((preset) => preset.id === presetId) || null)
      .filter((preset): preset is HotspotStylePreset => Boolean(preset));
    const preferredPresetId =
      (selectedStylePresetId === HOTSPOT_LOCAL_STYLE_ID
        ? HOTSPOT_LOCAL_STYLE_ID
        : attachedPreset.find((preset) => preset.id === selectedStylePresetId)?.id) ||
      attachedPreset[attachedPreset.length - 1]?.id ||
      HOTSPOT_LOCAL_STYLE_ID;
    setSelectedStylePresetId(preferredPresetId);
    setStylePresetInputValue("");
  }, [localHotspot?.id, localHotspot?.content, localHotspot?.scale, compatibleStylePresets]);


  useEffect(() => {
    const content = (localHotspot?.content as HotspotContentRecord) || {};
    const value = content.borderWidth ?? 0;
    setBorderWidthInput(displaySizeValue(String(value)));
  }, [localHotspot?.id]);
  useEffect(() => {
    const content = (localHotspot?.content as HotspotContentRecord) || {};
    const value = content.borderRadius ?? 4;
    setBorderRadiusInput(displaySizeValue(String(value)));
  }, [localHotspot?.id]);
  useEffect(() => {
    const content = (localHotspot?.content as HotspotContentRecord) || {};
    const value =
      content.borderStyle === "none" ? "solid" : (content.borderStyle ?? "solid");
    setBorderStyleState(String(value));
  }, [localHotspot?.id]);
  useEffect(() => {
    const content = (localHotspot?.content as HotspotContentRecord) || {};
    const value = content.outlineWidth ?? 0;
    setOutlineWidthInput(displaySizeValue(String(value)));
  }, [localHotspot?.id]);
  useEffect(() => {
    const content = (localHotspot?.content as HotspotContentRecord) || {};
    const value = content.outlineOffset ?? 0;
    setOutlineOffsetInput(displaySizeValue(String(value)));
  }, [localHotspot?.id]);
  useEffect(() => {
    const content = (localHotspot?.content as HotspotContentRecord) || {};
    setFontSizeInput(displaySizeValue(String(content.fontSize ?? "12px")));
  }, [localHotspot?.id]);
  useEffect(() => {
    const content = (localHotspot?.content as HotspotContentRecord) || {};
    setIconSizeInput(displaySizeValue(String(content.iconSize ?? "100%")));
  }, [localHotspot?.id]);
  useEffect(() => {
    const content = (localHotspot?.content as HotspotContentRecord) || {};
    const value = content.outlineStyle ?? "none";
    setOutlineStyleState(String(value));
  }, [localHotspot?.id]);
  useEffect(() => {
    const content = (localHotspot?.content as HotspotContentRecord) || {};
    const value = content.outlineWidth ?? 0;
    setOutlineWidthInput(String(value));
  }, [localHotspot?.id]);
  useEffect(() => {
    const content = (localHotspot?.content as HotspotContentRecord) || {};
    const value = content.outlineOffset ?? 0;
    setOutlineOffsetInput(String(value));
  }, [localHotspot?.id]);
  useEffect(() => {
    const content = (localHotspot?.content as HotspotContentRecord) || {};
    const value = content.padding ?? 0.3;
    setPaddingInput(String(value));
    setPaddingTopInput(content.paddingTop !== undefined ? String(content.paddingTop) : "");
    setPaddingRightInput(content.paddingRight !== undefined ? String(content.paddingRight) : "");
    setPaddingBottomInput(content.paddingBottom !== undefined ? String(content.paddingBottom) : "");
    setPaddingLeftInput(content.paddingLeft !== undefined ? String(content.paddingLeft) : "");
  }, [localHotspot?.id]);
  useEffect(() => {
    const content = (localHotspot?.content as HotspotContentRecord) || {};
    const value = content.fontWeight ?? 500;
    setFontWeightInput(String(value));
  }, [localHotspot?.id]);

  useEffect(() => {
    const content = (localHotspot?.content as HotspotContentRecord) || {};
    setHotspotVisibilityConfig(
      (content.hotspotVisibilityItemsConfig as CollectionItemsConfig) || {
        mode: "selected",
        filters: [],
        sort: null,
        selectedItemIds: (content.hotspotVisibilityTargetIds as string[]) || [],
      },
    );
  }, [localHotspot?.id]);

  const getResolvedStyleState = useCallback(
    (hotspot: Hotspot | null) => {
      if (!hotspot) {
        return {
          attachedPresetIds: [] as string[],
          attachedPresets: [] as HotspotStylePreset[],
          localStyleContent: {} as HotspotContentRecord,
          fieldSourceMap: {} as Record<string, string>,
        };
      }
      const currentContent = (hotspot.content as HotspotContentRecord) || {};
      const attachedPresetIds = getHotspotStylePresetIds(currentContent);
      const attachedPresets = attachedPresetIds
        .map((presetId) => stylePresets.find((preset) => preset.id === presetId) || null)
        .filter((preset): preset is HotspotStylePreset => Boolean(preset));
      const localStyleContent = getHotspotLocalStyleContent(hotspot.type || "", currentContent);
      return {
        attachedPresetIds,
        attachedPresets,
        localStyleContent,
        fieldSourceMap: buildStyleFieldSourceMap(
          hotspot.type || "",
          attachedPresets,
          localStyleContent,
        ),
      };
    },
    [stylePresets],
  );

  const syncHotspotStyleContent = useCallback(
    (hotspot: Hotspot, options?: { presetIds?: string[]; localStyleContent?: HotspotContentRecord }) => {
      const currentContent = (hotspot.content as HotspotContentRecord) || {};
      const presetIds = options?.presetIds ?? getHotspotStylePresetIds(currentContent);
      const attachedPresets = presetIds
        .map((presetId) => stylePresets.find((preset) => preset.id === presetId) || null)
        .filter((preset): preset is HotspotStylePreset => Boolean(preset));
      const localStyleContent =
        options?.localStyleContent ?? getHotspotLocalStyleContent(hotspot.type || "", currentContent);
      return materializeHotspotStyleContent(
        hotspot.type || "",
        currentContent,
        attachedPresets,
        localStyleContent,
      );
    },
    [stylePresets],
  );

  const applyStyleContentUpdates = useCallback(
    (
      hotspot: Hotspot,
      contentUpdates: HotspotContentRecord,
      mode: "persist" | "local-only",
      flushImmediately: boolean = false,
    ) => {
      if (contentUpdates.text !== undefined) {
        console.log("[Hotspot] apply text update", {
          id: hotspot.id,
          mode,
          flushImmediately,
          textLength: typeof contentUpdates.text === "string" ? contentUpdates.text.length : null,
        });
      }
      const hotspotType = hotspot.type || "";
      const styleKeys = new Set(HOTSPOT_STYLE_CONTENT_KEYS[hotspotType] || []);
      const currentContent = (hotspot.content as HotspotContentRecord) || {};
      const hasStyleUpdates = Object.keys(contentUpdates).some((key) => styleKeys.has(key));

      let nextContent: HotspotContentRecord;
      if (hasStyleUpdates) {
        if (selectedStylePresetId === HOTSPOT_LOCAL_STYLE_ID) {
          const nextLocalStyleContent = mergeStyleValuesWithDeletion(
            getHotspotLocalStyleContent(hotspotType, currentContent),
            contentUpdates,
          );
          nextContent = syncHotspotStyleContent(hotspot, { localStyleContent: nextLocalStyleContent });
        } else {
          const targetPreset = stylePresets.find((preset) => preset.id === selectedStylePresetId);
          if (!targetPreset) {
            nextContent = { ...currentContent, ...contentUpdates };
          } else {
            const nextPresets = stylePresets.map((preset) =>
              preset.id === selectedStylePresetId
                ? { ...preset, content: mergeStyleValuesWithDeletion(preset.content, contentUpdates) }
                : preset,
            );
            void persistStylePresets(nextPresets);

            // Update all OTHER hotspots that use this preset
            const otherAffectedHotspots = hotspots.filter((h) => {
              if (h.id === hotspot.id) return false;
              const hContent = (h.content as HotspotContentRecord) || {};
              return (
                h.type === hotspotType &&
                getHotspotStylePresetIds(hContent).includes(selectedStylePresetId)
              );
            });
            for (const otherHotspot of otherAffectedHotspots) {
              const otherContent = (otherHotspot.content as HotspotContentRecord) || {};
              const otherAttachedPresets = getHotspotStylePresetIds(otherContent)
                .map((presetId) => nextPresets.find((preset) => preset.id === presetId) || null)
                .filter((preset): preset is HotspotStylePreset => Boolean(preset));
              const otherNextContent = materializeHotspotStyleContent(
                otherHotspot.type || "",
                otherContent,
                otherAttachedPresets,
                getHotspotLocalStyleContent(otherHotspot.type || "", otherContent),
              );
              updateHotspotFromPanel(otherHotspot.id, { content: otherNextContent });
            }

            const attachedPresetIds = getHotspotStylePresetIds(currentContent);
            const attachedPresets = attachedPresetIds
              .map((presetId) => nextPresets.find((preset) => preset.id === presetId) || null)
              .filter((preset): preset is HotspotStylePreset => Boolean(preset));
            nextContent = materializeHotspotStyleContent(
              hotspotType,
              currentContent,
              attachedPresets,
              getHotspotLocalStyleContent(hotspotType, currentContent),
            );
          }
        }
      } else {
        nextContent = { ...currentContent, ...contentUpdates };
      }

      if (
        contentUpdates.color ||
        contentUpdates.fontColor ||
        contentUpdates.iconOrSvg !== undefined ||
        contentUpdates.padding !== undefined
      ) {
        const color = resolveCSSVariable(nextContent.color || "#E3F7FF");
        const fontColor = resolveCSSVariable(nextContent.fontColor || "#ffffff");
        const iconOrSvg = nextContent.iconOrSvg;
        const padding = nextContent.padding || 0;
        let svg = "";
        if (iconOrSvg && String(iconOrSvg).trim()) {
          const input = String(iconOrSvg).trim();
          if (input.startsWith("<svg")) {
            const svgTagReplaced = input
              .replace(/(<svg[^>]*)(fill=("|')[^"']*("|'))/i, `$1`)
              .replace(/(<svg[^>]*)(stroke=("|')[^"']*("|'))/i, `$1`);
            let innerContent = svgTagReplaced.replace(/<svg[^>]*>/, "").replace(/<\/svg>/, "");
            innerContent = innerContent
              .replace(/fill=(["'])(?!none)(.*?)(["'])/gi, `fill='${fontColor}'`)
              .replace(/stroke=(["'])(?!none)(.*?)(["'])/gi, `stroke='${fontColor}'`);
            innerContent = innerContent.replace(
              /(<(path|rect|circle|polygon|ellipse)\b((?!fill=)[^>])*)\/?>/gi,
              (match) => {
                if (/fill=/.test(match)) return match;
                return match.endsWith("/>")
                  ? match.replace(/\/>$/, ` fill='${fontColor}'/>`)
                  : match.replace(/>$/, ` fill='${fontColor}'>`);
              },
            );
            svg = `<svg width='256' height='256' viewBox='0 0 256 256'>
            <circle cx='128' cy='128' r='110' fill='white' opacity='0.5'/>
            <circle cx='128' cy='128' r='90' fill='white' opacity='1'/>
            <circle cx='128' cy='128' r='70' fill='${color}'/>
            <g transform='translate(128, 128) scale(4, 4) translate(-12, -12)'>${innerContent}</g>
          </svg>`;
          } else if (input.startsWith("<path") || input.startsWith("<g") || input.startsWith("<rect")) {
            svg = `<svg width='256' height='256' viewBox='0 0 256 256'>
            <circle cx='128' cy='128' r='110' fill='white' opacity='0.5'/>
            <circle cx='128' cy='128' r='90' fill='white' opacity='1'/>
            <circle cx='128' cy='128' r='70' fill='${color}'/>
            <g transform='translate(128, 128) scale(4, 4) translate(-12, -12)'>${input}</g>
          </svg>`;
          } else {
            let fontSize = 128;
            if (typeof window !== "undefined") {
              const canvas = document.createElement("canvas");
              const ctx = canvas.getContext("2d");
              if (ctx) {
                while (fontSize > 16) {
                  ctx.font = `bold ${fontSize}px sans-serif`;
                  if (ctx.measureText(input).width <= 186) break;
                  fontSize -= 2;
                }
              }
            }
            svg = `<svg width='256' height='256' viewBox='0 0 256 256'>
            <circle cx='128' cy='128' r='110' fill='white' opacity='0.5'/>
            <circle cx='128' cy='128' r='90' fill='white' opacity='1'/>
            <circle cx='128' cy='128' r='70' fill='${color}'/>
            <text x='128' y='148' text-anchor='middle' alignment-baseline='middle' font-size='${fontSize - 24}' fill='${fontColor}'>${input}</text>
          </svg>`;
          }
        } else {
          svg = `<svg width='256' height='256' viewBox='0 0 256 256'>
          <circle cx='128' cy='128' r='110' fill='white' opacity='0.5'/>
          <circle cx='128' cy='128' r='90' fill='white' opacity='1'/>
          <circle cx='128' cy='128' r='70' fill='${color}'/>
        </svg>`;
        }
        nextContent.svg = sanitizeSvgMarkup(svg);
      }

      const nextHotspot = { ...hotspot, content: nextContent };
      setLocalHotspot(nextHotspot);

      if (mode === "local-only") {
        return nextContent;
      }

      const immediateContentKeys = new Set([
        "borderWidth",
        "borderColor",
        "borderRadius",
        "backgroundColor",
        "textColor",
        "color",
        "padding",
        "paddingTop",
        "paddingRight",
        "paddingBottom",
        "paddingLeft",
        "fontSize",
        "fontWeight",
        "fontFamily",
        "textAlign",
        "lineHeight",
        "letterSpacing",
        "textShadow",
        "boxShadow",
        "opacity",
        "transform",
        "hoverBackgroundColor",
        "hoverBorderColor",
        "hoverTextColor",
        "hoverBoxShadow",
        "hoverTextShadow",
        "hoverOpacity",
        "hoverTransform",
        "activeBackgroundColor",
        "activeBorderColor",
        "activeTextColor",
        "activeBoxShadow",
        "activeTextShadow",
        "activeOpacity",
        "activeTransform",
        "visitedBackgroundColor",
        "visitedBorderColor",
        "visitedTextColor",
        "visitedBoxShadow",
        "visitedTextShadow",
        "visitedOpacity",
        "visitedTransform",
        "textDecoration",
        "textTransform",
        "fontStyle",
        "aspectRatio",
        "width",
        "height",
        "showPointer",
        "pointerShape",
        "pointerLineThickness",
      ]);
      const shouldFlushImmediately =
        flushImmediately || Object.keys(contentUpdates).some((key) => immediateContentKeys.has(key));

      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }

      if (shouldFlushImmediately) {
        updateHotspotFromPanel(hotspot.id, { content: nextContent });
        return nextContent;
      }

      updateTimeoutRef.current = setTimeout(() => {
        updateHotspotFromPanel(hotspot.id, { content: nextContent });
      }, 300);
      return nextContent;
    },
    [hotspots, persistStylePresets, selectedStylePresetId, stylePresets, syncHotspotStyleContent, updateHotspotFromPanel],
  );

  const handleUpdateField = (field: string, value: unknown) => {
    if (!localHotspot) return;
    const updates = { [field]: value };
    setLocalHotspot({ ...localHotspot, ...updates });
    updateHotspotFromPanel(localHotspot.id, updates);
  };

  const handleUpdateContent = (contentUpdates: HotspotContentRecord) => {
    if (!localHotspot) return;
    if (contentUpdates.text !== undefined) {
      console.log("[Hotspot] text save", {
        id: localHotspot.id,
        textLength: typeof contentUpdates.text === "string" ? contentUpdates.text.length : null,
        textPreview:
          typeof contentUpdates.text === "string" ? contentUpdates.text.slice(0, 80) : null,
      });
    }
    applyStyleContentUpdates(localHotspot, contentUpdates, "persist");
  };

  const handleVideoUpload = useCallback(
    async (file: File) => {
      if (!project || !localHotspot) return;
      setIsVideoUploading(true);
      setVideoUploadName(file.name);

      try {
        const craftspaceId = project.craftspace_id || "default";
        const projectId = typeof project.id === "string" ? project.id : (project as any)._id;
        const entityId = localHotspot.id;

        const storageService = await StorageServiceFactory.createStorageService(craftspaceId);
        const fileHash = await computeFileHash(file);

        let nextVersion = 1;
        if (useLocalBackend) {
          nextVersion = await getNextLocalVersionNumber("video", entityId, craftspaceId);
        } else {
          const existing = await convex.query(api.assetVersions.listByEntity, {
            entity_type: "video",
            entity_id: entityId,
          });
          const maxVersion = (existing || []).reduce(
            (max: number, v: any) => Math.max(max, v.version_number || 0),
            0,
          );
          nextVersion = maxVersion + 1;
        }

        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        const versionPath = StoragePaths.private.assetVersion(
          storageSegments.craftspaceKey,
          storageSegments.projectKey,
          "video",
          entityId,
          nextVersion,
          `${Date.now()}_${safeName}`,
        );

        await storageService.uploadFile(file, versionPath);
        const fileUrl = await storageService.getFileUrl(versionPath);

        let dimensions: { width: number; height: number } | null = null;
        try {
          dimensions = await getVideoDimensions(file);
        } catch (_error) {
          dimensions = null;
        }

        handleUpdateContent({ videoUrl: fileUrl });

        if (useLocalBackend) {
          await createLocalVersion({
            craftspace_id: craftspaceId,
            project_id: projectId,
            entity_type: "video",
            entity_id: entityId,
            file_url: fileUrl,
            private_file_url: fileUrl,
            file_hash: fileHash,
            file_size: file.size,
            width: dimensions?.width || undefined,
            height: dimensions?.height || undefined,
            original_filename: file.name,
          });
        } else {
          await convex.mutation(api.assetVersions.create, {
            project_id: projectId as any,
            entity_type: "video",
            entity_id: entityId,
            file_url: fileUrl,
            private_file_url: fileUrl,
            file_hash: fileHash,
            file_size: file.size,
            width: dimensions?.width || undefined,
            height: dimensions?.height || undefined,
            original_filename: file.name,
            metadata: { source: "hotspot-upload" },
          });
        }

        toast({
          title: "Video uploaded",
          description: `${file.name} uploaded and versioned.`,
        });
      } catch (error) {
        console.error("Video upload failed:", error);
        toast({
          variant: "destructive",
          title: "Upload failed",
          description: "Could not upload the video file.",
        });
      } finally {
        setIsVideoUploading(false);
      }
    },
    [project, localHotspot, convex, toast, useLocalBackend, handleUpdateContent, storageSegments],
  );

  const handleDownloadUpload = useCallback(
    async (file: File, onUpdate?: (updates: HotspotContentRecord) => void) => {
      if (!project || !localHotspot) return;
      setIsDownloadUploading(true);
      setDownloadUploadName(file.name);

      try {
        const craftspaceId = project.craftspace_id || "default";
        const projectId = typeof project.id === "string" ? project.id : (project as any)._id;
        const entityId = localHotspot.id;

        const storageService = await StorageServiceFactory.createStorageService(craftspaceId);
        const fileHash = await computeFileHash(file);

        let nextVersion = Date.now();
        if (!useLocalBackend) {
          const existing = await convex.query(api.assetVersions.listByEntity, {
            entity_type: "download",
            entity_id: entityId,
          });
          const maxVersion = (existing || []).reduce(
            (max: number, v: any) => Math.max(max, v.version_number || 0),
            0,
          );
          nextVersion = maxVersion + 1;
        }

        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        const versionPath = StoragePaths.private.assetVersion(
          storageSegments.craftspaceKey,
          storageSegments.projectKey,
          "download",
          entityId,
          nextVersion,
          `${Date.now()}_${safeName}`,
        );

        await storageService.uploadFile(file, versionPath);
        const fileUrl = await storageService.getFileUrl(versionPath);

        const updates = {
          downloadFileUrl: fileUrl,
          downloadFileName: file.name,
          downloadFileSize: file.size,
        };
        if (onUpdate) onUpdate(updates);
        else handleUpdateContent(updates);

        if (!useLocalBackend) {
          await convex.mutation(api.assetVersions.create, {
            project_id: projectId as any,
            entity_type: "download",
            entity_id: entityId,
            file_url: fileUrl,
            private_file_url: fileUrl,
            file_hash: fileHash,
            file_size: file.size,
            original_filename: file.name,
            metadata: { source: "hotspot-download-upload" },
          });
        }

        toast({
          title: "File uploaded",
          description: `${file.name} uploaded.`,
        });
      } catch (error) {
        console.error("Download upload failed:", error);
        toast({
          variant: "destructive",
          title: "Upload failed",
          description: "Could not upload the file.",
        });
      } finally {
        setIsDownloadUploading(false);
      }
    },
    [project, localHotspot, convex, toast, useLocalBackend, handleUpdateContent, storageSegments],
  );

  const handleUpdateContentLocalOnly = (contentUpdates: HotspotContentRecord) => {
    if (!localHotspot) return;
    applyStyleContentUpdates(localHotspot, contentUpdates, "local-only");
  };

  const flushUpdateContent = (contentUpdates: HotspotContentRecord) => {
    if (!localHotspot) return;
    applyStyleContentUpdates(localHotspot, contentUpdates, "persist", true);
  };

  const normalizedStylePresetInput = normalizeStyleName(stylePresetInputValue);
  const matchingPresetByInput = normalizedStylePresetInput
    ? compatibleStylePresets.find(
        (preset) => normalizeStyleName(preset.name) === normalizedStylePresetInput,
      ) || null
    : null;
  const filteredStylePresets = normalizedStylePresetInput
    ? compatibleStylePresets.filter((preset) =>
        normalizeStyleName(preset.name).includes(normalizedStylePresetInput),
      )
    : compatibleStylePresets;

  const handleCreateStylePreset = (name: string) => {
    if (!localHotspot) return;
    const trimmedName = name.trim();
    if (!trimmedName) return;

    const normalizedName = normalizeStyleName(trimmedName);
    const existingAnyType = stylePresets.find(
      (preset) => normalizeStyleName(preset.name) === normalizedName,
    );
    if (existingAnyType) {
      setStylePresetInputValue("");
      setSelectedStylePresetId(existingAnyType.id);
      handleApplyStylePreset(existingAnyType);
      return;
    }

    const contentStyle = extractHotspotStyleContent(localHotspot);
    if (Object.keys(contentStyle).length === 0) {
      return;
    }

    const preset: HotspotStylePreset = {
      id: crypto.randomUUID(),
      name: trimmedName,
      hotspotType: localHotspot.type || "",
      content: contentStyle,
      scale: normalizeHotspotScale(localHotspot.scale),
      createdAt: new Date().toISOString(),
    };
    const nextPresets = [...stylePresets, preset];
    void persistStylePresets(nextPresets);
    const currentContent = (localHotspot.content as HotspotContentRecord) || {};
    const attachedPresetIds = getHotspotStylePresetIds(currentContent);
    const nextPresetIds = [...attachedPresetIds, preset.id];
    const nextContent = materializeHotspotStyleContent(
      localHotspot.type || "",
      currentContent,
      nextPresetIds
        .map((presetId) => nextPresets.find((item) => item.id === presetId) || null)
        .filter((item): item is HotspotStylePreset => Boolean(item)),
      {},
    );
    setLocalHotspot({ ...localHotspot, content: nextContent });
    updateHotspotFromPanel(localHotspot.id, { content: nextContent });
    setStylePresetInputValue("");
    setSelectedStylePresetId(preset.id);
  };

  const handleDeleteStylePreset = (presetId: string) => {
    const nextPresets = stylePresets.filter((preset) => preset.id !== presetId);
    void persistStylePresets(nextPresets);
    if (selectedStylePresetId === presetId) {
      setSelectedStylePresetId(HOTSPOT_LOCAL_STYLE_ID);
    }
    setStylePresetInputValue("");
  };

  const handleDetachStylePreset = (presetId: string) => {
    if (!localHotspot) return;
    const currentContent = (localHotspot.content as HotspotContentRecord) || {};
    const currentPresetIds = getHotspotStylePresetIds(currentContent);
    const targetIndex = currentPresetIds.indexOf(presetId);
    if (targetIndex === -1) return;
    const nextPresetIds = currentPresetIds.filter((id) => id !== presetId);
    const nextContent = syncHotspotStyleContent(localHotspot, {
      presetIds: nextPresetIds,
      localStyleContent: getHotspotLocalStyleContent(localHotspot.type || "", currentContent),
    });
    setLocalHotspot({ ...localHotspot, content: nextContent });
    updateHotspotFromPanel(localHotspot.id, { content: nextContent });
    if (selectedStylePresetId === presetId) {
      const fallbackPresetId =
        nextPresetIds[Math.max(0, Math.min(targetIndex - 1, nextPresetIds.length - 1))] ||
        HOTSPOT_LOCAL_STYLE_ID;
      setSelectedStylePresetId(fallbackPresetId);
    }
    setStylePresetInputValue("");
  };

  const handleApplyStylePreset = (preset: HotspotStylePreset) => {
    if (!localHotspot || preset.hotspotType !== (localHotspot.type || "")) return;
    const currentContent = (localHotspot.content as HotspotContentRecord) || {};
    const attachedPresetIds = getHotspotStylePresetIds(currentContent);
    const nextPresetIds = attachedPresetIds.includes(preset.id)
      ? attachedPresetIds
      : [...attachedPresetIds, preset.id];
    const nextContent = syncHotspotStyleContent(localHotspot, { presetIds: nextPresetIds });
    setLocalHotspot({ ...localHotspot, content: nextContent });
    updateHotspotFromPanel(localHotspot.id, { content: nextContent });
    if (preset.scale) {
      handleUpdateField("scale", normalizeHotspotScale(preset.scale));
    }
    setSelectedStylePresetId(preset.id);
    setStylePresetInputValue("");
  };

  const handleUpdateCurrentStylePreset = (presetId: string) => {
    if (!localHotspot) return;

    // Clear any pending debounced content update to prevent it from
    // overwriting the merged content we're about to save.
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
      updateTimeoutRef.current = null;
    }

    const targetPreset = stylePresets.find((preset) => preset.id === presetId);
    if (!targetPreset) return;

    const sourceState = getResolvedStyleState(localHotspot);
    const updatedPreset: HotspotStylePreset = {
      ...targetPreset,
      content:
        presetId === HOTSPOT_LOCAL_STYLE_ID
          ? sourceState.localStyleContent
          : extractHotspotStyleContent(localHotspot),
      scale: normalizeHotspotScale(localHotspot.scale),
    };
    const nextPresets = stylePresets.map((preset) =>
      preset.id === presetId ? updatedPreset : preset,
    );
    void persistStylePresets(nextPresets);

    const attachedHotspots = hotspots.filter((hotspot) => {
      const content = (hotspot.content as HotspotContentRecord) || {};
      return (
        hotspot.type === updatedPreset.hotspotType &&
        getHotspotStylePresetIds(content).includes(updatedPreset.id)
      );
    });

    for (const hotspot of attachedHotspots) {
      const baseHotspot = hotspot.id === localHotspot.id ? localHotspot : hotspot;
      const mergedContent = materializeHotspotStyleContent(
        hotspot.type || "",
        ((baseHotspot.content as HotspotContentRecord) || {}),
        getHotspotStylePresetIds((baseHotspot.content as HotspotContentRecord) || {})
          .map((id) => nextPresets.find((preset) => preset.id === id) || null)
          .filter((preset): preset is HotspotStylePreset => Boolean(preset)),
        getHotspotLocalStyleContent(hotspot.type || "", (baseHotspot.content as HotspotContentRecord) || {}),
      );
      const updates: Partial<Hotspot> = { content: mergedContent };
      if (updatedPreset.scale) {
        updates.scale = updatedPreset.scale;
      }
      updateHotspotFromPanel(hotspot.id, updates);
      if (hotspot.id === localHotspot.id) {
        setLocalHotspot((prev) => (prev ? { ...prev, content: mergedContent, ...updates } : prev));
      }
    }

    setSelectedStylePresetId(presetId);
    setStylePresetInputValue("");
  };

  const handleRemoveStylePreset = () => {
    if (!localHotspot) return;
    const currentContent = (localHotspot.content as HotspotContentRecord) || {};
    const currentPresetIds = getHotspotStylePresetIds(currentContent);
    const nextPresetIds =
      selectedStylePresetId === HOTSPOT_LOCAL_STYLE_ID
        ? currentPresetIds
        : currentPresetIds.filter((presetId) => presetId !== selectedStylePresetId);
    const nextLocalStyleContent =
      selectedStylePresetId === HOTSPOT_LOCAL_STYLE_ID
        ? {}
        : getHotspotLocalStyleContent(localHotspot.type || "", currentContent);
    const restContent = syncHotspotStyleContent(localHotspot, {
      presetIds: nextPresetIds,
      localStyleContent: nextLocalStyleContent,
    });
    setIsStyleDropdownOpen(false);
    setLocalHotspot({ ...localHotspot, content: restContent });
    updateHotspotFromPanel(localHotspot.id, { content: restContent });
    setStylePresetInputValue("");
    setSelectedStylePresetId(HOTSPOT_LOCAL_STYLE_ID);
  };

  const handleStylePresetInputEnter = () => {
    setIsStyleDropdownOpen(false);
    const trimmedName = stylePresetInputValue.trim();
    if (!trimmedName) {
      setStylePresetInputValue("");
      return;
    }
    if (normalizeStyleName(trimmedName) === HOTSPOT_LOCAL_STYLE_ID) {
      setSelectedStylePresetId(HOTSPOT_LOCAL_STYLE_ID);
      setStylePresetInputValue("");
      return;
    }
    if (matchingPresetByInput) {
      handleApplyStylePreset(matchingPresetByInput);
      return;
    }
    handleCreateStylePreset(trimmedName);
  };

  // Use unified coordinate system function
  const positionToYawPitchLocal = (position: Vector3Like) => {
    return positionToYawPitch({
      x: position?.x || 0,
      y: position?.y || 0,
      z: position?.z || 0,
    });
  };

  // Use unified coordinate system function
  const yawPitchToPositionLocal = (yaw: number, pitch: number, distance: number = 480) => {
    return yawPitchToPosition(yaw, pitch, distance);
  };

  // Convert XYZ rotation to Yaw/Pitch
  const rotationToYawPitch = (rotation: Vector3Like) => {
    const x = rotation?.x || 0;
    const y = rotation?.y || 0;

    return { yaw: y, pitch: x };
  };

  const yawPitchToRotation = (yaw: number, pitch: number) => {
    return { x: pitch, y: yaw, z: 0 };
  };

  // Get width and height scale values from XYZ scale
  const getWidthValue = (scale: Vector3Like) => {
    return Number.isFinite(scale?.x) ? (scale?.x as number) : 1;
  };
  const getHeightValue = (scale: Vector3Like) => {
    return Number.isFinite(scale?.y) ? (scale?.y as number) : 1;
  };
  const getDepthValue = (scale: Vector3Like) => {
    return Number.isFinite(scale?.z) ? (scale?.z as number) : 1;
  };
  const setScaleValues = (width: number, height: number) => {
    const currentScale = (localHotspot?.scale as Vector3Like) || {};
    const z = Number.isFinite(currentScale.z) ? (currentScale.z as number) : 1;
    return { x: Math.max(0.05, width), y: Math.max(0.05, height), z };
  };

  const handleUpdateWidth = (width: number) => {
    let height = getHeightValue(scale);

    // If aspect ratio is locked, maintain it
    if (isAspectRatioLocked) {
      const originalWidth = getWidthValue(scaleBaseline);
      const originalHeight = getHeightValue(scaleBaseline);
      if (originalWidth > 0) {
        const aspectRatio = originalHeight / originalWidth;
        height = width * aspectRatio;
      }
    }

    const newScale = setScaleValues(width, height);
    handleUpdateField("scale", newScale);
  };
  const handleUpdateHeight = (height: number) => {
    let width = getWidthValue(scale);

    // If aspect ratio is locked, maintain it
    if (isAspectRatioLocked) {
      const originalWidth = getWidthValue(scaleBaseline);
      const originalHeight = getHeightValue(scaleBaseline);
      if (originalHeight > 0) {
        const aspectRatio = originalWidth / originalHeight;
        width = height * aspectRatio;
      }
    }

    const newScale = setScaleValues(width, height);
    handleUpdateField("scale", newScale);
  };
  const handleUpdateDepth = (depth: number) => {
    const width = getWidthValue(scale);
    const height = getHeightValue(scale);
    const currentScale = (localHotspot?.scale as Vector3Like) || {};
    const newScale = {
      x: Math.max(0.05, width),
      y: Math.max(0.05, height),
      z: Math.max(0.05, depth),
    };
    handleUpdateField("scale", newScale);
  };

  // For HTML hotspots: pixel-based width/height that writes to content.width/height
  const getContentWidthPx = (): number => {
    const w = content.width;
    if (!w || w === "auto") return 150;
    return parseFloat(String(w)) || 150;
  };
  const getContentHeightPx = (): number => {
    const h = content.height;
    if (!h || h === "auto") return 150;
    return parseFloat(String(h)) || 150;
  };
  const resetHtmlScale = () => {
    const currentZ = (localHotspot?.scale as Vector3Like)?.z ?? 1;
    handleUpdateField("scale", { x: 1, y: 1, z: currentZ });
  };
  const handleUpdateWidthPx = (width: number) => {
    const updates: HotspotContentRecord = { width: `${Math.max(1, Math.round(width))}px` };
    if (isAspectRatioLocked) {
      const currentW = getContentWidthPx();
      const currentH = getContentHeightPx();
      if (currentW > 0) {
        updates.height = `${Math.max(1, Math.round(width * (currentH / currentW)))}px`;
      }
    }
    handleUpdateContent(updates);
    resetHtmlScale();
  };
  const handleUpdateHeightPx = (height: number) => {
    const updates: HotspotContentRecord = { height: `${Math.max(1, Math.round(height))}px` };
    if (isAspectRatioLocked) {
      const currentW = getContentWidthPx();
      const currentH = getContentHeightPx();
      if (currentH > 0) {
        updates.width = `${Math.max(1, Math.round(height * (currentW / currentH)))}px`;
      }
    }
    handleUpdateContent(updates);
    resetHtmlScale();
  };

  // Get individual rotation values from XYZ rotation
  const getRotationX = () => {
    return localHotspot?.rotation?.x ?? 0;
  };
  const getRotationY = () => {
    return localHotspot?.rotation?.y ?? 0;
  };
  const getRotationZ = () => {
    return localHotspot?.rotation?.z ?? 0;
  };

  const setRotationValue = (axis: "x" | "y" | "z", value: number) => {
    const currentRot = (localHotspot?.rotation as Vector3Like) || { x: 0, y: 0, z: 0 };
    const newRot = { ...currentRot, [axis]: value };
    handleUpdateField("rotation", newRot);
  };

  const handleUpdateYawPitch = (type: "position" | "rotation", yaw?: number, pitch?: number) => {
    if (!localHotspot) return;

    if (type === "position") {
      const currentPos = localHotspot.position as Vector3Like;
      const currentYawPitch = positionToYawPitchLocal(currentPos);
      const newYaw = yaw !== undefined ? yaw : currentYawPitch.yaw;
      const newPitch = pitch !== undefined ? pitch : currentYawPitch.pitch;
      const newPosition = yawPitchToPositionLocal(newYaw, newPitch);
      handleUpdateField("position", newPosition);
    } else if (type === "rotation") {
      const currentRot = localHotspot.rotation as Vector3Like;
      const currentYawPitch = rotationToYawPitch(currentRot);
      const newYaw = yaw !== undefined ? yaw : currentYawPitch.yaw;
      const newPitch = pitch !== undefined ? pitch : currentYawPitch.pitch;
      const newRotation = yawPitchToRotation(newYaw, newPitch);
      handleUpdateField("rotation", newRotation);
    }
  };

  const handleLinkPanorama = (panoramaId: string) => {
    handleUpdateContent({
      linkedPanoramaId: panoramaId,
      linkTransitionType: linkTransitionType,
    });
  };

  const _handleNavigateToLinkedPanorama = () => {
    if (linkedPanorama && changePanorama) {
      changePanorama(linkedPanorama);
    }
  };

  const handleShrinkModeChange = (checked: boolean) => {
    handleUpdateContent({ shrinkMode: checked });
  };

  const handleShowHotspotNamesChange = (checked: boolean) => {
    handleUpdateContent({ showHotspotNames: checked });
  };

  const handleMoveCameraToHotspotChange = (checked: boolean) => {
    handleUpdateContent({ moveCameraToHotspot: checked });
  };

  // Multi-action helpers
  const readActions = (currentContent: HotspotContentRecord): Array<Record<string, unknown>> => {
    const actions = (currentContent.actions as Array<Record<string, unknown>>) || [];
    if (actions.length > 0) return actions;

    // Backward compat: convert old flat structure to actions array
    const actionType = (currentContent.actionType as string | undefined)
      || (currentContent.linkedPanoramaId ? "navigate" : undefined);
    if (!actionType) return [];

    const actionId = "legacy-action";
    const baseAction: Record<string, unknown> = { id: actionId, type: actionType, trigger: (currentContent.navigateTrigger as string) || "click" };

    if (actionType === "navigate") {
      return [{
        ...baseAction,
        linkedPanoramaId: currentContent.linkedPanoramaId,
        linkTransitionType: currentContent.linkTransitionType,
        linkTransitionViewMode: currentContent.linkTransitionViewMode,
        linkTransitionDirectionView: currentContent.linkTransitionDirectionView,
        linkedPanoramaView: currentContent.linkedPanoramaView,
        moveCameraToHotspot: currentContent.moveCameraToHotspot,
      }];
    } else if (actionType === "notification") {
      return [{
        ...baseAction,
        notificationTitle: currentContent.notificationTitle,
        notificationMessage: currentContent.notificationMessage,
        notificationAutoHideDuration: currentContent.notificationAutoHideDuration,
        notificationStyle: currentContent.notificationStyle,
      }];
    } else if (actionType === "download") {
      return [{
        ...baseAction,
        downloadFileUrl: currentContent.downloadFileUrl,
        downloadFileName: currentContent.downloadFileName,
      }];
    } else if (actionType === "showHideSkin") {
      return [{
        ...baseAction,
        skinVisibilityAction: currentContent.skinVisibilityAction,
        skinVisibilityTargetComponentIds: currentContent.skinVisibilityTargetComponentIds,
      }];
    } else if (actionType === "showHideHotspots") {
      return [{
        ...baseAction,
        hotspotVisibilityAction: currentContent.hotspotVisibilityAction,
        hotspotVisibilityTargetIds: currentContent.hotspotVisibilityTargetIds,
        hotspotVisibilityItemsConfig: currentContent.hotspotVisibilityItemsConfig,
      }];
    }

    return [];
  };

  const writeActions = (actions: Array<Record<string, unknown>>) => {
    if (!localHotspot) return;
    // Preserve existing style tokens/local content while clearing legacy action fields.
    const currentContent = (localHotspot.content as HotspotContentRecord) || {};
    const nextContent: Record<string, unknown> = {
      ...currentContent,
      actions,
      // Clear legacy fields
      actionType: undefined,
      navigateTrigger: undefined,
      linkedPanoramaId: undefined,
      linkTransitionType: undefined,
      linkTransitionViewMode: undefined,
      linkTransitionDirectionView: undefined,
      linkedPanoramaView: undefined,
      moveCameraToHotspot: undefined,
      notificationTitle: undefined,
      notificationMessage: undefined,
      notificationAutoHideDuration: undefined,
      notificationStyle: undefined,
      downloadFileUrl: undefined,
      downloadFileName: undefined,
      skinVisibilityAction: undefined,
      skinVisibilityTargetComponentIds: undefined,
      hotspotVisibilityAction: undefined,
      hotspotVisibilityTargetIds: undefined,
      hotspotVisibilityItemsConfig: undefined,
    };
    handleUpdateField("content", nextContent);
  };

  const addAction = (type: string) => {
    const currentContent = (localHotspot.content as HotspotContentRecord) || {};
    const actions = readActions(currentContent);
    const newActionId = crypto.randomUUID();
    const newAction: Record<string, unknown> = { id: newActionId, type, trigger: "click" };

    // Set defaults based on type
    if (type === "notification") {
      newAction.notificationStyle = "toast";
    } else if (type === "showHideSkin") {
      newAction.skinVisibilityAction = "show";
    } else if (type === "showHideHotspots") {
      newAction.hotspotVisibilityAction = "show";
      setHotspotVisibilityConfigs(prev => ({
        ...prev,
        [newActionId]: { mode: "selected", filters: [], sort: null, selectedItemIds: [] }
      }));
    }

    writeActions([...actions, newAction]);
    setExpandedActionIds(prev => new Set([...prev, newActionId]));
    setIsActionMenuOpen(false);
  };

  const removeAction = (actionId: string) => {
    const currentContent = (localHotspot.content as HotspotContentRecord) || {};
    const actions = readActions(currentContent);
    writeActions(actions.filter(a => (a.id as string) !== actionId));
    setExpandedActionIds(prev => {
      const next = new Set(prev);
      next.delete(actionId);
      return next;
    });
  };

  const updateAction = (actionId: string, updates: Record<string, unknown>) => {
    const currentContent = (localHotspot.content as HotspotContentRecord) || {};
    const actions = readActions(currentContent);
    writeActions(actions.map(a => (a.id as string) === actionId ? { ...a, ...updates } : a));
  };

  const handleRemoveNavigateAction = () => {
    const currentContent = (localHotspot.content as HotspotContentRecord) || {};
    const {
      linkedPanoramaId,
      linkTransitionType,
      actionType: _actionType,
      linkedPanoramaView,
      navigateTrigger: _navigateTrigger,
      notificationTitle,
      notificationMessage,
      notificationAutoHideDuration,
      notificationStyle: _notificationStyle,
      downloadFileUrl,
      downloadFileName,
      skinVisibilityAction,
      skinVisibilityTargetComponentIds,
      hotspotVisibilityAction,
      hotspotVisibilityTargetIds,
      hotspotVisibilityItemsConfig,
      ...restContent
    } = currentContent;
    handleUpdateField("content", restContent);
  };


  const _handleAddConfetti = () => {
    setIsConfettiDialogOpen(true);
  };

  const handleConfettiActionAdd = (confettiAction: HotspotContentRecord) => {
    handleUpdateContent({ confettiAction });

    // Play confetti preview immediately after adding
    import("@/utils/confettiService").then(({ confettiService }) => {
      // Trigger confetti from center of screen for preview
      confettiService.triggerConfetti({
        ...confettiAction,
        origin: { x: 0.5, y: 0.5 },
      });
    });
  };

  const handleEditConfetti = () => {
    setIsConfettiDialogOpen(true);
  };

  const handleRemoveConfetti = () => {
    const currentContent = (localHotspot.content as HotspotContentRecord) || {};
    const { confettiAction, ...restContent } = currentContent;
    handleUpdateField("content", restContent);
  };

  // Show bulk edit panel if multiple hotspots are selected
  if (selectedHotspotIds.length > 1) {
    return <BulkEditPanel onClose={() => setShowBulkEdit(false)} />;
  }

  if (!localHotspot) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center text-gray-400">
          <div className="text-4xl mb-4">⚙️</div>
          <h3 className="text-lg font-semibold mb-2">No Selection</h3>
          <p className="text-sm">Select a hotspot to edit its properties</p>
        </div>
      </div>
    );
  }

  const position = localHotspot.position as Vector3Like;
  const rotation = localHotspot.rotation as Vector3Like;
  const scale = localHotspot.scale as Vector3Like;
  const content = (localHotspot.content as HotspotContentRecord) || {};
  const linkedPanorama = panoramas?.find((p) => p.id === content.linkedPanoramaId);
  const hasNotification = content.notificationMessage || content.notificationTitle;
  const notificationStyle = (content.notificationStyle as string) || "banner";
  const actionType =
    (content.actionType as string) || (hasNotification ? "notification" : "navigate");
  const hasDownload = content.downloadFileUrl;
  const hasConfetti = content.confettiAction;
  const navigateTrigger = (content.navigateTrigger as string) || "click";
  const textResizeMode = content.textResizeMode === "scale-with-box" ? "scale-with-box" : "fixed";
  const hasNavigateAction = Boolean(
    (actionType === "navigate" ||
      actionType === "notification" ||
      actionType === "download" ||
      actionType === "showHideSkin" ||
      actionType === "showHideHotspots") &&
      (content.navigateTrigger ||
        content.linkedPanoramaId ||
        hasNotification ||
        hasDownload ||
        content.skinVisibilityAction ||
        content.hotspotVisibilityAction),
  );
  const isPickingChromaKey = chromaKeyPickHotspotId === localHotspot.id;
  const hotspotFacingMode =
    content.renderStyle === "3d" || content.renderStyle === "billboard"
      ? (content.renderStyle as "billboard" | "3d")
      : hotspotRenderStyle;

  const positionYawPitch = positionToYawPitchLocal(position);
  const _rotationYawPitch = rotationToYawPitch(rotation);

  const iconOrSvgValue = content.iconOrSvg || "";
  const resolvedStyleState = getResolvedStyleState(localHotspot);
  const attachedStyleSourceChips = resolvedStyleState.attachedPresetIds
    .map((presetId) => stylePresets.find((preset) => preset.id === presetId) || null)
    .filter((preset): preset is HotspotStylePreset => Boolean(preset));
  const stylePresetNameById = new Map(stylePresets.map((preset) => [preset.id, preset.name]));
  const inheritedWithoutSelectedStyleBaseline = attachedStyleSourceChips
    .filter((preset) => preset.id !== selectedStylePresetId)
    .reduce<HotspotContentRecord>(
      (acc, preset) => ({ ...acc, ...preset.content }),
      { ...(HOTSPOT_STYLE_DEFAULTS[localHotspot.type || ""] || {}) },
    );
  const inheritedStyleBaseline = attachedStyleSourceChips.reduce<HotspotContentRecord>(
    (acc, preset) => ({ ...acc, ...preset.content }),
    { ...(HOTSPOT_STYLE_DEFAULTS[localHotspot.type || ""] || {}) },
  );
  const activeStylePreset =
    selectedStylePresetId !== HOTSPOT_LOCAL_STYLE_ID
      ? stylePresets.find((preset) => preset.id === selectedStylePresetId) || null
      : null;
  const styleBaseline =
    (selectedStylePresetId === HOTSPOT_LOCAL_STYLE_ID
      ? resolvedStyleState.localStyleContent
      : activeStylePreset?.content) || HOTSPOT_STYLE_DEFAULTS[localHotspot.type || ""] || {};
  const scaleBaseline = activeStylePreset?.scale || { x: 1, y: 1, z: 1 };
  const changedLabelClass = (state: boolean | "selected" | "inherited") => {
    if (state === true || state === "selected") {
      return "text-xs text-blue-200 bg-blue-500/30 rounded px-1 p-0.5 mb-1 cursor-pointer";
    }
    if (state === "inherited") {
      return "text-xs text-orange-200 bg-orange-500/30 rounded px-1 p-0.5 mb-1 cursor-pointer";
    }
    return "text-xs text-gray-400 p-0.5 mb-1";
  };
  const isActiveStyleLabel = (state: boolean | "selected" | "inherited") =>
    state === true || state === "selected";
  const hotspotFieldClassName =
    "bg-gray-700 text-white rounded-md h-7 text-xs border-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus:ring-0 outline-none";
  const hotspotFieldSmClassName = `${hotspotFieldClassName} text-xs`;
  const hotspotSelectClassName =
    "bg-gray-700 text-white rounded-md px-3 h-7 w-full text-xs border-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus:ring-0 outline-none";
  const fontSizeValue = typeof content.fontSize === "string" ? content.fontSize : "12px";
  const fontSizeMatch = fontSizeValue.match(/^(-?\d+(?:\.\d+)?)(px|%|rem|em|vw|vh)?$/i);
  const fontSizeNumber = fontSizeMatch ? parseFloat(fontSizeMatch[1]) : null;
  const fontSizeUnit = fontSizeMatch?.[2] ?? "px";
  const iconSizeValue = typeof content.iconSize === "string" ? content.iconSize : "100%";
  const iconSizeMatch = iconSizeValue.match(/^(-?\d+(?:\.\d+)?)(px|%|rem|em|vw|vh)?$/i);
  const iconSizeNumber = iconSizeMatch ? parseFloat(iconSizeMatch[1]) : null;
  const iconSizeUnit = iconSizeMatch?.[2] ?? "%";
  const normalizeFontSizeInputValue = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return trimmed;
    if (/^-?\d+(?:\.\d+)?$/.test(trimmed)) {
      return `${trimmed}px`;
    }
    return trimmed;
  };
  const normalizedFontWeight = fontWeightInput.replace(",", ".");
  const fontWeightNumberRaw =
    normalizedFontWeight === "" || normalizedFontWeight === "-" ? null : Number(normalizedFontWeight);
  const fontWeightNumber = Number.isNaN(fontWeightNumberRaw as number) ? null : fontWeightNumberRaw;
  const getStyleBaselineValue = (key: string, fallbackValue?: unknown) =>
    styleBaseline[key] !== undefined ? styleBaseline[key] : fallbackValue;
  const getInheritedBaselineValue = (key: string, fallbackValue?: unknown) =>
    inheritedStyleBaseline[key] !== undefined ? inheritedStyleBaseline[key] : fallbackValue;
  const getStyleSourceTooltip = (key: string) => {
    const sourceId = resolvedStyleState.fieldSourceMap[key] || "";
    if (sourceId === HOTSPOT_LOCAL_STYLE_ID) {
      return "From local (this hotspot)";
    }
    if (sourceId) {
      const presetName = stylePresetNameById.get(sourceId) || "Style";
      return `From "${presetName}" hotspot style`;
    }
    return "From defaults";
  };
  const getStyleSourceIdForKey = (key: string) => resolvedStyleState.fieldSourceMap[key] || "";
  const labelHoverHandlers = (key: string) => ({
    onMouseEnter: () => {
      const sourceId = getStyleSourceIdForKey(key);
      setHoveredStyleSourceId(sourceId || null);
    },
    onMouseLeave: () => setHoveredStyleSourceId(null),
  });
  const getResetBaselineValue = (key: string, fallbackValue?: unknown) => {
    if (selectedStylePresetId === HOTSPOT_LOCAL_STYLE_ID) {
      return getInheritedBaselineValue(key, fallbackValue);
    }
    return inheritedWithoutSelectedStyleBaseline[key] !== undefined
      ? inheritedWithoutSelectedStyleBaseline[key]
      : fallbackValue;
  };
  const resetStyleFieldToBaseline = (key: string, fallbackValue?: unknown) => {
    const baselineValue = getResetBaselineValue(key, fallbackValue);
    if (key === "padding") {
      handleUpdateContent(
        {
          padding: undefined,
          paddingTop: undefined,
          paddingRight: undefined,
          paddingBottom: undefined,
          paddingLeft: undefined,
        },
      );
    } else {
      handleUpdateContent({ [key]: undefined });
    }
    if (key === "borderRadius") {
      setBorderRadiusInput(String(baselineValue ?? 4));
    } else if (key === "padding") {
      const value = baselineValue ?? 0.3;
      const valueString = String(value);
      setPaddingInput(valueString);
      setPaddingTopInput(valueString);
      setPaddingRightInput(valueString);
      setPaddingBottomInput(valueString);
      setPaddingLeftInput(valueString);
    } else if (key === "borderWidth") {
      setBorderWidthInput(String(baselineValue ?? 0));
    } else if (key === "fontWeight") {
      setFontWeightInput(String(baselineValue ?? 500));
    } else if (key === "fontSize") {
      const value = baselineValue ?? "12px";
      setFontSizeInput(displaySizeValue(String(value)));
    }
  };
  const handleStyleLabelOptionClick = (
    e: React.MouseEvent<HTMLElement>,
    key: string,
    fallbackValue?: unknown,
  ) => {
    if (!e.altKey && !e.metaKey) return;
    e.preventDefault();
    e.stopPropagation();
    resetStyleFieldToBaseline(key, fallbackValue);
  };
  const handleScaleLabelOptionClick = (e: React.MouseEvent<HTMLElement>, axis: "x" | "y") => {
    if (!e.altKey && !e.metaKey) return;
    e.preventDefault();
    e.stopPropagation();
    const baselineValue = axis === "x" ? Number(scaleBaseline.x ?? 1) : Number(scaleBaseline.y ?? 1);
    if (axis === "x") {
      handleUpdateWidth(baselineValue);
      return;
    }
    handleUpdateHeight(baselineValue);
  };
  const renderStyleLabel = ({
    label,
    key,
    currentValue,
    fallbackValue,
    title,
    onClick,
    hoverHandlers,
  }: {
    label: React.ReactNode;
    key: string;
    currentValue: unknown;
    fallbackValue?: unknown;
    title?: string;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
    hoverHandlers?: { onMouseEnter?: () => void; onMouseLeave?: () => void };
  }) => {
    const state = getStyleFieldState(key, currentValue, fallbackValue);
    return (
      <div className="flex items-center gap-0.5">
        <Label
          className={changedLabelClass(state)}
          title={title}
          onClick={onClick}
          {...hoverHandlers}
        >
          {label}
        </Label>
        {isActiveStyleLabel(state) && (
          <button
            type="button"
            className="inline-flex h-4 w-4 items-center justify-center mb-1 text-gray-400 hover:text-gray-200"
            title={`Clear ${typeof label === "string" ? label : "value"}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              resetStyleFieldToBaseline(key, fallbackValue);
            }}
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  };
  const getStyleFieldState = (key: string, currentValue: unknown, fallbackValue?: unknown) => {
    const explicitSource = resolvedStyleState.fieldSourceMap[key] || "";
    if (selectedStylePresetId === HOTSPOT_LOCAL_STYLE_ID) {
      if (Object.prototype.hasOwnProperty.call(resolvedStyleState.localStyleContent, key)) {
        return "selected" as const;
      }
      return explicitSource ? ("inherited" as const) : false;
    }
    if (explicitSource === selectedStylePresetId) {
      return "selected" as const;
    }
    if (explicitSource) {
      return "inherited" as const;
    }
    const baselineValue = getStyleBaselineValue(key, fallbackValue);
    const normalizedCurrent = currentValue !== undefined ? currentValue : fallbackValue;
    return !areStyleValuesEqual(normalizedCurrent, baselineValue) ? ("selected" as const) : false;
  };
  // Remove fallback for info/pin types

  const PRIMARY_ICON_PRESET_NAMES = preferredIconOrder;
  const PRIMARY_ICON_PRESETS = PRIMARY_ICON_PRESET_NAMES.map((iconName) => {
    const IconComp = (icons as Record<string, (props: { size?: number }) => JSX.Element>)[iconName];
    if (!IconComp) return "";
    return renderToStaticMarkup(<IconComp size={48} />);
  }).filter((icon) => icon.length > 0);

  // Add a small set of Remix Icon SVGs as presets
  const EXTRA_ICON_PRESETS = [
    // Shopping bag
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12.0049 0.999695C14.7663 0.999695 17.0049 3.23827 17.0049 5.99969V7.99969H20.0049C20.5572 7.99969 21.0049 8.44741 21.0049 8.99969V20.9997C21.0049 21.552 20.5572 21.9997 20.0049 21.9997H4.00488C3.4526 21.9997 3.00488 21.552 3.00488 20.9997V8.99969C3.00488 8.44741 3.4526 7.99969 4.00488 7.99969H7.00488V5.99969C7.00488 3.23827 9.24346 0.999695 12.0049 0.999695ZM17.0049 10.9997H15.0049V11.9997C15.0049 12.552 15.4526 12.9997 16.0049 12.9997C16.5177 12.9997 16.9404 12.6137 16.9982 12.1163L17.0049 11.9997V10.9997ZM9.00488 10.9997H7.00488V11.9997C7.00488 12.552 7.4526 12.9997 8.00488 12.9997C8.51772 12.9997 8.94039 12.6137 8.99815 12.1163L9.00488 11.9997V10.9997ZM12.0049 2.99969C10.4072 2.99969 9.10122 4.24861 9.00998 5.82342L9.00488 5.99969V7.99969H15.0049V5.99969C15.0049 4.40201 13.756 3.09603 12.1812 3.00479L12.0049 2.99969Z"></path></svg>`,
    // Bookmark
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M5 2H19C19.5523 2 20 2.44772 20 3V22.1433C20 22.4194 19.7761 22.6434 19.5 22.6434C19.4061 22.6434 19.314 22.6168 19.2344 22.5669L12 18.0313L4.76559 22.5669C4.53163 22.7136 4.22306 22.6429 4.07637 22.4089C4.02647 22.3293 4 22.2373 4 22.1433V3C4 2.44772 4.44772 2 5 2ZM18 4H6V19.4324L12 15.6707L18 19.4324V4Z"></path></svg>`,
    // Coffee
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M5 3H20C21.1046 3 22 3.89543 22 5V8C22 9.10457 21.1046 10 20 10H18V13C18 15.2091 16.2091 17 14 17H8C5.79086 17 4 15.2091 4 13V4C4 3.44772 4.44772 3 5 3ZM18 5V8H20V5H18ZM2 19H20V21H2V19Z"></path></svg>`,
    // Hotel bed sleep
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M22 11V20H20V17H4V20H2V4H4V14H12V7H18C20.2091 7 22 8.79086 22 11ZM8 13C6.34315 13 5 11.6569 5 10C5 8.34315 6.34315 7 8 7C9.65685 7 11 8.34315 11 10C11 11.6569 9.65685 13 8 13Z"></path></svg>`,
    // Bank
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M2 20H22V22H2V20ZM4 12H6V19H4V12ZM9 12H11V19H9V12ZM13 12H15V19H13V12ZM18 12H20V19H18V12ZM2 7L12 2L22 7V11H2V7ZM4 8.23607V9H20V8.23607L12 4.23607L4 8.23607ZM12 8C11.4477 8 11 7.55228 11 7C11 6.44772 11.4477 6 12 6C12.5523 6 13 6.44772 13 7C13 7.55228 12.5523 8 12 8Z"></path></svg>`,
    // Share
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M13.1202 17.0228L8.92129 14.7324C8.19135 15.5125 7.15261 16 6 16C3.79086 16 2 14.2091 2 12C2 9.79086 3.79086 8 6 8C7.15255 8 8.19125 8.48746 8.92118 9.26746L13.1202 6.97713C13.0417 6.66441 13 6.33707 13 6C13 3.79086 14.7909 2 17 2C19.2091 2 21 3.79086 21 6C21 8.20914 19.2091 10 17 10C15.8474 10 14.8087 9.51251 14.0787 8.73246L9.87977 11.0228C9.9583 11.3355 10 11.6629 10 12C10 12.3371 9.95831 12.6644 9.87981 12.9771L14.0788 15.2675C14.8087 14.4875 15.8474 14 17 14C19.2091 14 21 15.7909 21 18C21 20.2091 19.2091 22 17 22C14.7909 22 13 20.2091 13 18C13 17.6629 13.0417 17.3355 13.1202 17.0228ZM6 14C7.10457 14 8 13.1046 8 12C8 10.8954 7.10457 10 6 10C4.89543 10 4 10.8954 4 12C4 13.1046 4.89543 14 6 14ZM17 8C18.1046 8 19 7.10457 19 6C19 4.89543 18.1046 4 17 4C15.8954 4 15 4.89543 15 6C15 7.10457 15.8954 8 17 8ZM17 20C18.1046 20 19 19.1046 19 18C19 16.8954 18.1046 16 17 16C15.8954 16 15 16.8954 15 18C15 19.1046 15.8954 20 17 20Z"></path></svg>`,
    // Open new tab
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M10 3V5H5V19H19V14H21V20C21 20.5523 20.5523 21 20 21H4C3.44772 21 3 20.5523 3 20V4C3 3.44772 3.44772 3 4 3H10ZM17.5858 5H13V3H21V11H19V6.41421L12 13.4142L10.5858 12L17.5858 5Z"></path></svg>`,
    // Pdf file
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M5 4H15V8H19V20H5V4ZM3.9985 2C3.44749 2 3 2.44405 3 2.9918V21.0082C3 21.5447 3.44476 22 3.9934 22H20.0066C20.5551 22 21 21.5489 21 20.9925L20.9997 7L16 2H3.9985ZM10.4999 7.5C10.4999 9.07749 10.0442 10.9373 9.27493 12.6534C8.50287 14.3757 7.46143 15.8502 6.37524 16.7191L7.55464 18.3321C10.4821 16.3804 13.7233 15.0421 16.8585 15.49L17.3162 13.5513C14.6435 12.6604 12.4999 9.98994 12.4999 7.5H10.4999ZM11.0999 13.4716C11.3673 12.8752 11.6042 12.2563 11.8037 11.6285C12.2753 12.3531 12.8553 13.0182 13.5101 13.5953C12.5283 13.7711 11.5665 14.0596 10.6352 14.4276C10.7999 14.1143 10.9551 13.7948 11.0999 13.4716Z"></path></svg>`,
    // Volume up
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M2 16.0001H5.88889L11.1834 20.3319C11.2727 20.405 11.3846 20.4449 11.5 20.4449C11.7761 20.4449 12 20.2211 12 19.9449V4.05519C12 3.93977 11.9601 3.8279 11.887 3.73857C11.7121 3.52485 11.3971 3.49335 11.1834 3.66821L5.88889 8.00007H2C1.44772 8.00007 1 8.44778 1 9.00007V15.0001C1 15.5524 1.44772 16.0001 2 16.0001ZM23 12C23 15.292 21.5539 18.2463 19.2622 20.2622L17.8445 18.8444C19.7758 17.1937 21 14.7398 21 12C21 9.26016 19.7758 6.80629 17.8445 5.15557L19.2622 3.73779C21.5539 5.75368 23 8.70795 23 12ZM18 12C18 10.0883 17.106 8.38548 15.7133 7.28673L14.2842 8.71584C15.3213 9.43855 16 10.64 16 12C16 13.36 15.3213 14.5614 14.2842 15.2841L15.7133 16.7132C17.106 15.6145 18 13.9116 18 12Z"></path></svg>`,
    // Volume muted
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M5.88889 16.0001H2C1.44772 16.0001 1 15.5524 1 15.0001V9.00007C1 8.44778 1.44772 8.00007 2 8.00007H5.88889L11.1834 3.66821C11.3971 3.49335 11.7121 3.52485 11.887 3.73857C11.9601 3.8279 12 3.93977 12 4.05519V19.9449C12 20.2211 11.7761 20.4449 11.5 20.4449C11.3846 20.4449 11.2727 20.405 11.1834 20.3319L5.88889 16.0001ZM20.4142 12.0001L23.9497 15.5356L22.5355 16.9498L19 13.4143L15.4645 16.9498L14.0503 15.5356L17.5858 12.0001L14.0503 8.46454L15.4645 7.05032L19 10.5859L22.5355 7.05032L23.9497 8.46454L20.4142 12.0001Z"></path></svg>`,
    // Facebook
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M14 13.5H16.5L17.5 9.5H14V7.5C14 6.47062 14 5.5 16 5.5H17.5V2.1401C17.1743 2.09685 15.943 2 14.6429 2C11.9284 2 10 3.65686 10 6.69971V9.5H7V13.5H10V22H14V13.5Z"></path></svg>`,
    // Messenger
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12.001 2C17.6345 2 22.001 6.1265 22.001 11.7C22.001 17.2735 17.6345 21.4 12.001 21.4C11.0233 21.4023 10.0497 21.273 9.10648 21.0155C8.92907 20.9668 8.7403 20.9808 8.57198 21.055L6.58748 21.931C6.34398 22.0386 6.06291 22.018 5.83768 21.8761C5.61244 21.7342 5.47254 21.4896 5.46448 21.2235L5.40998 19.4445C5.40257 19.2257 5.30547 19.0196 5.14148 18.8745C3.19598 17.1345 2.00098 14.6155 2.00098 11.7C2.00098 6.1265 6.36748 2 12.001 2ZM5.99598 14.5365C5.71398 14.984 6.26398 15.488 6.68498 15.1685L9.84048 12.7735C10.0543 12.6122 10.3491 12.6122 10.563 12.7735L12.8995 14.5235C13.2346 14.7749 13.6596 14.8747 14.0716 14.7987C14.4836 14.7227 14.8451 14.4779 15.0685 14.1235L18.006 9.4635C18.288 9.016 17.738 8.512 17.317 8.8315L14.1615 11.2265C13.9476 11.3878 13.6528 11.3878 13.439 11.2265L11.1025 9.4765C10.7673 9.22511 10.3423 9.12532 9.93034 9.2013C9.51834 9.27728 9.15689 9.5221 8.93348 9.8765L5.99598 14.5365Z"></path></svg>`,
    // Signal
    `<svg viewBox="0 0 24 24" fill="none">
  <g fill="currentColor" transform="scale(0.15)"><path d="m80 0c4.1505 0 8.2271.31607 12.2072.925452l-1.1444 7.413248c-3.6069-.55226-7.3014-.8387-11.0628-.8387-3.7612 0-7.4555.28641-11.0623.83862l-1.1444-7.413245c3.9799-.609332 8.0564-.925375 12.2067-.925375z"/><path d="m98.9849 2.26619-1.7798 7.28755c7.3099 1.77896 14.1849 4.66606 20.4389 8.47306l3.895-6.411c-6.901-4.20091-14.488-7.38658-22.5541-9.34961z"/>
    <path d="m127.279 15.4591-4.432 6.0507c5.977 4.3861 11.257 9.6664 15.643 15.6437l6.051-4.4324c-4.84-6.5957-10.666-12.4222-17.262-17.262z"/>
    <path d="m148.384 38.4618-6.411 3.8942c3.807 6.2541 6.694 13.1299 8.473 20.4395l7.288-1.7798c-1.963-8.0657-5.149-15.6528-9.35-22.5539z"/>
    <path d="m159.075 67.7934-7.414 1.1444c.553 3.6067.839 7.301.839 11.0622 0 3.7614-.286 7.4559-.839 11.0628l7.414 1.1444c.609-3.9801.925-8.0567.925-12.2072 0-4.1503-.316-8.2267-.925-12.2066z"/>
    <path d="m141.973 117.645c3.807-6.255 6.694-13.13 8.473-20.44l7.288 1.7798c-1.963 8.0662-5.149 15.6532-9.35 22.5542z"/><path d="m138.49 122.847 6.051 4.432c-4.84 6.596-10.666 12.422-17.262 17.262l-4.433-6.051c5.978-4.386 11.258-9.666 15.644-15.643z"/><path d="m117.644 141.973 3.894 6.411c-6.901 4.201-14.488 7.387-22.5537 9.35l-1.7798-7.288c7.3095-1.779 14.1855-4.666 20.4395-8.473z"/>
<path d="m91.0622 151.661 1.1445 7.414c-3.9799.609-8.0564.925-12.2067.925-4.1505 0-8.2272-.316-12.2073-.925l1.1442-7.413c3.6054.552 7.2997.838 11.0631.838 3.7612 0 7.4555-.286 11.0622-.839z"/>
<path d="m62.7945 150.448-1.7794 7.286c-6.0589-1.475-11.8477-3.639-17.2785-6.406l-7.5927 1.772-1.7042-7.304 10.2604-2.394 2.4408 1.243c4.9187 2.506 10.1623 4.467 15.6536 5.803z"/>
<path d="m28.1097 147.273 1.7042 7.304-13.0145 3.036c-8.66079 2.021-16.433718-5.752-14.41286-14.412l3.03673-13.015 7.30383 1.704-3.03675 13.015c-.75782 3.248 2.15705 6.162 5.40485 5.405z"/>
<path d="m14.2041 125.56-7.30383-1.704 1.77163-7.593c-2.76664-5.431-4.93123-11.22-6.40585-17.2787l7.28586-1.7794c1.33599 5.4911 3.29709 10.7351 5.80279 15.6541l1.2435 2.441z"/>
<path d="m8.33759 91.0624-7.412228 1.1442c-.609324-3.9799-.925362-8.0563-.925362-12.2066 0-4.1505.316067-8.2271.925446-12.2072l7.413244 1.1444c-.55225 3.607-.83869 7.3014-.83869 11.0628 0 3.7631.28613 7.4572.83759 11.0624z"/>
<path d="m9.55373 62.795-7.28755-1.7798c1.96302-8.0657 5.1487-15.6528 9.34962-22.5539l6.411 3.8942c-3.807 6.2541-6.6941 13.1299-8.47307 20.4395z"/>
<path d="m21.5098 37.1531-6.0507-4.4324c4.8398-6.5957 10.6663-12.4221 17.262-17.2619l4.4324 6.0507c-5.9773 4.3861-11.2576 9.6663-15.6437 15.6436z"/><path d="m42.356 18.0266-3.8943-6.4111c6.9011-4.20082 14.4882-7.38645 22.554-9.34944l1.7798 7.28755c-7.3096 1.77899-14.1854 4.66589-20.4395 8.47299z"/>
<path d="m145 80c0 35.899-29.101 65-65 65-11.3866 0-22.0893-2.928-31.3965-8.072-.8961-.495-1.9417-.658-2.9389-.426l-28.9134 6.747 6.7465-28.914c.2326-.997.0692-2.043-.426-2.939-5.1439-9.307-8.0717-20.0095-8.0717-31.396 0-35.8985 29.1015-65 65-65 35.899 0 65 29.1015 65 65z"/>
  </g>
</svg>`,
    // Whatsapp
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12.001 2C17.5238 2 22.001 6.47715 22.001 12C22.001 17.5228 17.5238 22 12.001 22C10.1671 22 8.44851 21.5064 6.97086 20.6447L2.00516 22L3.35712 17.0315C2.49494 15.5536 2.00098 13.8345 2.00098 12C2.00098 6.47715 6.47813 2 12.001 2ZM8.59339 7.30019L8.39232 7.30833C8.26293 7.31742 8.13607 7.34902 8.02057 7.40811C7.93392 7.45244 7.85348 7.51651 7.72709 7.63586C7.60774 7.74855 7.53857 7.84697 7.46569 7.94186C7.09599 8.4232 6.89729 9.01405 6.90098 9.62098C6.90299 10.1116 7.03043 10.5884 7.23169 11.0336C7.63982 11.9364 8.31288 12.8908 9.20194 13.7759C9.4155 13.9885 9.62473 14.2034 9.85034 14.402C10.9538 15.3736 12.2688 16.0742 13.6907 16.4482C13.6907 16.4482 14.2507 16.5342 14.2589 16.5347C14.4444 16.5447 14.6296 16.5313 14.8153 16.5218C15.1066 16.5068 15.391 16.428 15.6484 16.2909C15.8139 16.2028 15.8922 16.159 16.0311 16.0714C16.0311 16.0714 16.0737 16.0426 16.1559 15.9814C16.2909 15.8808 16.3743 15.81 16.4866 15.6934C16.5694 15.6074 16.6406 15.5058 16.6956 15.3913C16.7738 15.2281 16.8525 14.9166 16.8838 14.6579C16.9077 14.4603 16.9005 14.3523 16.8979 14.2854C16.8936 14.1778 16.8047 14.0671 16.7073 14.0201L16.1258 13.7587C16.1258 13.7587 15.2563 13.3803 14.7245 13.1377C14.6691 13.1124 14.6085 13.1007 14.5476 13.097C14.4142 13.0888 14.2647 13.1236 14.1696 13.2238C14.1646 13.2218 14.0984 13.279 13.3749 14.1555C13.335 14.2032 13.2415 14.3069 13.0798 14.2972C13.0554 14.2955 13.0311 14.292 13.0074 14.2858C12.9419 14.2685 12.8781 14.2457 12.8157 14.2193C12.692 14.1668 12.6486 14.1469 12.5641 14.1105C11.9868 13.8583 11.457 13.5209 10.9887 13.108C10.8631 12.9974 10.7463 12.8783 10.6259 12.7616C10.2057 12.3543 9.86169 11.9211 9.60577 11.4938C9.5918 11.4705 9.57027 11.4368 9.54708 11.3991C9.50521 11.331 9.45903 11.25 9.44455 11.1944C9.40738 11.0473 9.50599 10.9291 9.50599 10.9291C9.50599 10.9291 9.74939 10.663 9.86248 10.5183C9.97128 10.379 10.0652 10.2428 10.125 10.1457C10.2428 9.95633 10.2801 9.76062 10.2182 9.60963C9.93764 8.92565 9.64818 8.24536 9.34986 7.56894C9.29098 7.43545 9.11585 7.33846 8.95659 7.32007C8.90265 7.31384 8.84875 7.30758 8.79459 7.30402C8.66053 7.29748 8.5262 7.29892 8.39232 7.30833L8.59339 7.30019Z"></path></svg>`,
    // Linkedin
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M6.94048 4.99993C6.94011 5.81424 6.44608 6.54702 5.69134 6.85273C4.9366 7.15845 4.07187 6.97605 3.5049 6.39155C2.93793 5.80704 2.78195 4.93715 3.1105 4.19207C3.43906 3.44699 4.18654 2.9755 5.00048 2.99993C6.08155 3.03238 6.94097 3.91837 6.94048 4.99993ZM7.00048 8.47993H3.00048V20.9999H7.00048V8.47993ZM13.3205 8.47993H9.34048V20.9999H13.2805V14.4299C13.2805 10.7699 18.0505 10.4299 18.0505 14.4299V20.9999H22.0005V13.0699C22.0005 6.89993 14.9405 7.12993 13.2805 10.1599L13.3205 8.47993Z"></path></svg>`,
    // X twitter
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M17.6874 3.0625L12.6907 8.77425L8.37045 3.0625H2.11328L9.58961 12.8387L2.50378 20.9375H5.53795L11.0068 14.6886L15.7863 20.9375H21.8885L14.095 10.6342L20.7198 3.0625H17.6874ZM16.6232 19.1225L5.65436 4.78217H7.45745L18.3034 19.1225H16.6232Z"></path></svg>`,
    // Instagram
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M13.0281 2.00073C14.1535 2.00259 14.7238 2.00855 15.2166 2.02322L15.4107 2.02956C15.6349 2.03753 15.8561 2.04753 16.1228 2.06003C17.1869 2.1092 17.9128 2.27753 18.5503 2.52503C19.2094 2.7792 19.7661 3.12253 20.3219 3.67837C20.8769 4.2342 21.2203 4.79253 21.4753 5.45003C21.7219 6.0867 21.8903 6.81337 21.9403 7.87753C21.9522 8.1442 21.9618 8.3654 21.9697 8.58964L21.976 8.78373C21.9906 9.27647 21.9973 9.84686 21.9994 10.9723L22.0002 11.7179C22.0003 11.809 22.0003 11.903 22.0003 12L22.0002 12.2821L21.9996 13.0278C21.9977 14.1532 21.9918 14.7236 21.9771 15.2163L21.9707 15.4104C21.9628 15.6347 21.9528 15.8559 21.9403 16.1225C21.8911 17.1867 21.7219 17.9125 21.4753 18.55C21.2211 19.2092 20.8769 19.7659 20.3219 20.3217C19.7661 20.8767 19.2069 21.22 18.5503 21.475C17.9128 21.7217 17.1869 21.89 16.1228 21.94C15.8561 21.9519 15.6349 21.9616 15.4107 21.9694L15.2166 21.9757C14.7238 21.9904 14.1535 21.997 13.0281 21.9992L12.2824 22C12.1913 22 12.0973 22 12.0003 22L11.7182 22L10.9725 21.9993C9.8471 21.9975 9.27672 21.9915 8.78397 21.9768L8.58989 21.9705C8.36564 21.9625 8.14444 21.9525 7.87778 21.94C6.81361 21.8909 6.08861 21.7217 5.45028 21.475C4.79194 21.2209 4.23444 20.8767 3.67861 20.3217C3.12278 19.7659 2.78028 19.2067 2.52528 18.55C2.27778 17.9125 2.11028 17.1867 2.06028 16.1225C2.0484 15.8559 2.03871 15.6347 2.03086 15.4104L2.02457 15.2163C2.00994 14.7236 2.00327 14.1532 2.00111 13.0278L2.00098 10.9723C2.00284 9.84686 2.00879 9.27647 2.02346 8.78373L2.02981 8.58964C2.03778 8.3654 2.04778 8.1442 2.06028 7.87753C2.10944 6.81253 2.27778 6.08753 2.52528 5.45003C2.77944 4.7917 3.12278 4.2342 3.67861 3.67837C4.23444 3.12253 4.79278 2.78003 5.45028 2.52503C6.08778 2.27753 6.81278 2.11003 7.87778 2.06003C8.14444 2.04816 8.36564 2.03847 8.58989 2.03062L8.78397 2.02433C9.27672 2.00969 9.8471 2.00302 10.9725 2.00086L13.0281 2.00073ZM12.0003 7.00003C9.23738 7.00003 7.00028 9.23956 7.00028 12C7.00028 14.7629 9.23981 17 12.0003 17C14.7632 17 17.0003 14.7605 17.0003 12C17.0003 9.23713 14.7607 7.00003 12.0003 7.00003ZM12.0003 9.00003C13.6572 9.00003 15.0003 10.3427 15.0003 12C15.0003 13.6569 13.6576 15 12.0003 15C10.3434 15 9.00028 13.6574 9.00028 12C9.00028 10.3431 10.3429 9.00003 12.0003 9.00003ZM17.2503 5.50003C16.561 5.50003 16.0003 6.05994 16.0003 6.74918C16.0003 7.43843 16.5602 7.9992 17.2503 7.9992C17.9395 7.9992 18.5003 7.4393 18.5003 6.74918C18.5003 6.05994 17.9386 5.49917 17.2503 5.50003Z"></path></svg>`,
    // Camera photos
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M9 3H15L17 5H21C21.5523 5 22 5.44772 22 6V20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20V6C2 5.44772 2.44772 5 3 5H7L9 3ZM12 19C15.3137 19 18 16.3137 18 13C18 9.68629 15.3137 7 12 7C8.68629 7 6 9.68629 6 13C6 16.3137 8.68629 19 12 19ZM12 17C9.79086 17 8 15.2091 8 13C8 10.7909 9.79086 9 12 9C14.2091 9 16 10.7909 16 13C16 15.2091 14.2091 17 12 17Z"></path></svg>`,
    // Camera videos
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M17 9.2L22.2133 5.55071C22.4395 5.39235 22.7513 5.44737 22.9096 5.6736C22.9684 5.75764 23 5.85774 23 5.96033V18.0397C23 18.3158 22.7761 18.5397 22.5 18.5397C22.3974 18.5397 22.2973 18.5081 22.2133 18.4493L17 14.8V19C17 19.5523 16.5523 20 16 20H2C1.44772 20 1 19.5523 1 19V5C1 4.44772 1.44772 4 2 4H16C16.5523 4 17 4.44772 17 5V9.2Z"></path></svg>`,
    // Love heart
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12.001 4.52853C14.35 2.42 17.98 2.49 20.2426 4.75736C22.5053 7.02472 22.583 10.637 20.4786 12.993L11.9999 21.485L3.52138 12.993C1.41705 10.637 1.49571 7.01901 3.75736 4.75736C6.02157 2.49315 9.64519 2.41687 12.001 4.52853ZM18.827 6.1701C17.3279 4.66794 14.9076 4.60701 13.337 6.01687L12.0019 7.21524L10.6661 6.01781C9.09098 4.60597 6.67506 4.66808 5.17157 6.17157C3.68183 7.66131 3.60704 10.0473 4.97993 11.6232L11.9999 18.6543L19.0201 11.6232C20.3935 10.0467 20.319 7.66525 18.827 6.1701Z"></path></svg>`,
    // Thumbs up like
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M14.5998 8.00033H21C22.1046 8.00033 23 8.89576 23 10.0003V12.1047C23 12.3659 22.9488 12.6246 22.8494 12.8662L19.755 20.3811C19.6007 20.7558 19.2355 21.0003 18.8303 21.0003H2C1.44772 21.0003 1 20.5526 1 20.0003V10.0003C1 9.44804 1.44772 9.00033 2 9.00033H5.48184C5.80677 9.00033 6.11143 8.84246 6.29881 8.57701L11.7522 0.851355C11.8947 0.649486 12.1633 0.581978 12.3843 0.692483L14.1984 1.59951C15.25 2.12534 15.7931 3.31292 15.5031 4.45235L14.5998 8.00033ZM7 10.5878V19.0003H18.1606L21 12.1047V10.0003H14.5998C13.2951 10.0003 12.3398 8.77128 12.6616 7.50691L13.5649 3.95894C13.6229 3.73105 13.5143 3.49353 13.3039 3.38837L12.6428 3.0578L7.93275 9.73038C7.68285 10.0844 7.36341 10.3746 7 10.5878ZM5 11.0003H3V19.0003H5V11.0003Z"></path></svg>`,
    // Microphone
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M11.9998 1C14.7612 1 16.9998 3.23858 16.9998 6V10C16.9998 12.7614 14.7612 15 11.9998 15C9.23833 15 6.99976 12.7614 6.99976 10V6C6.99976 3.23858 9.23833 1 11.9998 1ZM3.05469 11H5.07065C5.55588 14.3923 8.47329 17 11.9998 17C15.5262 17 18.4436 14.3923 18.9289 11H20.9448C20.4837 15.1716 17.1714 18.4839 12.9998 18.9451V23H10.9998V18.9451C6.82814 18.4839 3.51584 15.1716 3.05469 11Z"></path></svg>`,
    // Attachment
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M14.8287 7.75737L9.1718 13.4142C8.78127 13.8047 8.78127 14.4379 9.1718 14.8284C9.56232 15.219 10.1955 15.219 10.586 14.8284L16.2429 9.17158C17.4144 8.00001 17.4144 6.10052 16.2429 4.92894C15.0713 3.75737 13.1718 3.75737 12.0002 4.92894L6.34337 10.5858C4.39075 12.5384 4.39075 15.7042 6.34337 17.6569C8.29599 19.6095 11.4618 19.6095 13.4144 17.6569L19.0713 12L20.4855 13.4142L14.8287 19.0711C12.095 21.8047 7.66283 21.8047 4.92916 19.0711C2.19549 16.3374 2.19549 11.9053 4.92916 9.17158L10.586 3.51473C12.5386 1.56211 15.7045 1.56211 17.6571 3.51473C19.6097 5.46735 19.6097 8.63317 17.6571 10.5858L12.0002 16.2427C10.8287 17.4142 8.92916 17.4142 7.75759 16.2427C6.58601 15.0711 6.58601 13.1716 7.75759 12L13.4144 6.34316L14.8287 7.75737Z"></path></svg>`,
    // Download
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M3 19H21V21H3V19ZM13 13.1716L19.0711 7.1005L20.4853 8.51472L12 17L3.51472 8.51472L4.92893 7.1005L11 13.1716V2H13V13.1716Z"></path></svg>`,
    // Upload
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M3 19H21V21H3V19ZM13 5.82843V17H11V5.82843L4.92893 11.8995L3.51472 10.4853L12 2L20.4853 10.4853L19.0711 11.8995L13 5.82843Z"></path></svg>`,
    // Event
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M9 1V3H15V1H17V3H21C21.5523 3 22 3.44772 22 4V20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20V4C2 3.44772 2.44772 3 3 3H7V1H9ZM20 11H4V19H20V11ZM11 13V17H6V13H11ZM7 5H4V9H20V5H17V7H15V5H9V7H7V5Z"></path></svg>`,
  ];
  const REMIX_ICON_PRESETS = [...PRIMARY_ICON_PRESETS, ...EXTRA_ICON_PRESETS];

  // Add a small set of emoji as presets
  const EMOJI_PRESETS = [
    "ℹ︎",
    "✔️",
    "✅",
    "𐄂",
    "🟢",
    "🟠",
    "🔴",
    "⭐️",
    "🌟",
    "✨",
    "⚠️",
    "🏷️",
    "✉️",
    "🌳",
    "💡",
    "❗",
    "❓",
    "🚕",
    "🛴",
    "🚂",
    "🛑",
    "🧭",
    "🏠",
    "🗺️",
    "🖼️",
    "🎥",
    "🔗",
    "📝",
    "🔒",
    "🔓",
    "🛠️",
    "🧩",
    "🧑‍💻",
    "👋",
    "🕹️",
    "🎯",
    "🧭",
  ];

  const handleDeleteClick = () => {
    deleteHotspotFromPanel(localHotspot.id);
  };

  const handleHotspotLucideIconSelect = (iconName: string, svgCode: string) => {
    handleUpdateContent({
      contentType: "icon",
      iconVariable: undefined,
      lucideIcon: iconName,
      iconOrSvg: svgCode,
    });
  };

  const normalizeSvgIconSize = (svgMarkup: string, targetSize: number = 48) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgMarkup, "image/svg+xml");
      const svg = doc.querySelector("svg");
      if (!svg) return svgMarkup;
      const widthRaw = svg.getAttribute("width");
      const heightRaw = svg.getAttribute("height");
      const width = widthRaw ? parseFloat(widthRaw) : NaN;
      const height = heightRaw ? parseFloat(heightRaw) : NaN;
      if (!svg.getAttribute("viewBox")) {
        const viewW = Number.isFinite(width) ? width : targetSize;
        const viewH = Number.isFinite(height) ? height : targetSize;
        svg.setAttribute("viewBox", `0 0 ${viewW} ${viewH}`);
      }
      svg.setAttribute("width", String(targetSize));
      svg.setAttribute("height", String(targetSize));
      svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
      return svg.outerHTML;
    } catch (_error) {
      return svgMarkup;
    }
  };

  const isSvgMarkupValue = (value: string) => value.trim().startsWith("<svg");
  const isImageSourceValue = (value: string) =>
    /^(https?:)?\/\//i.test(value) || /^data:image\//i.test(value);

  const normalizedIconVariableQuery = iconVariableInput.trim().toLowerCase();
  const filteredCraftspaceIconVariables = craftspaceOnlyIconVariables.filter((iconVar) =>
    iconVar.name.toLowerCase().includes(normalizedIconVariableQuery),
  );
  const filteredProjectIconVariables = projectIconVariables.filter((iconVar) =>
    iconVar.name.toLowerCase().includes(normalizedIconVariableQuery),
  );
  const filteredLucideIconNames = lucideIconNames.filter((iconName) =>
    iconName.toLowerCase().includes(normalizedIconVariableQuery),
  );
  const hasFilteredIconResults =
    filteredCraftspaceIconVariables.length > 0 ||
    filteredProjectIconVariables.length > 0 ||
    filteredLucideIconNames.length > 0;

  const selectIconVariable = (name: string, closePopover: boolean = true) => {
    if (!name) return;
    handleUpdateContent({ contentType: "icon", iconVariable: name });
    if (closePopover) setIconVariablePopoverOpen(false);
  };

  const getIconAutocompleteMatch = (rawValue: string) => {
    const query = rawValue.trim().toLowerCase();
    if (!query) return null;
    const craftspaceMatch = craftspaceOnlyIconVariables.find((item) =>
      item.name.toLowerCase().startsWith(query),
    );
    if (craftspaceMatch) return { type: "variable" as const, name: craftspaceMatch.name };
    const projectMatch = projectIconVariables.find((item) =>
      item.name.toLowerCase().startsWith(query),
    );
    if (projectMatch) return { type: "variable" as const, name: projectMatch.name };
    const lucideMatch = lucideIconNames.find((name) => name.toLowerCase().startsWith(query));
    if (lucideMatch) return { type: "lucide" as const, name: lucideMatch };
    return null;
  };

  const handleIconVariableInputChange = (value: string, cursorPos: number | null) => {
    const match = getIconAutocompleteMatch(value);
    if (match && match.name.toLowerCase() !== value.trim().toLowerCase()) {
      setIconVariableInput(match.name);
      requestAnimationFrame(() => {
        const input = iconVariableInputRef.current;
        if (!input) return;
        const start = cursorPos ?? value.length;
        input.setSelectionRange(start, match.name.length);
      });
      return;
    }
    setIconVariableInput(value);
  };

  const commitIconVariableInput = (closePopover: boolean) => {
    const trimmed = iconVariableInput.trim();
    if (!trimmed) {
      handleUpdateContent({ iconVariable: undefined });
      if (closePopover) setIconVariablePopoverOpen(false);
      return;
    }
    const exactVariable = allIconVariableNames.find(
      (candidate) => candidate.toLowerCase() === trimmed.toLowerCase(),
    );
    if (exactVariable) {
      selectIconVariable(exactVariable, closePopover);
      return;
    }
    const exactLucide = lucideIconNames.find(
      (candidate) => candidate.toLowerCase() === trimmed.toLowerCase(),
    );
    if (exactLucide) {
      const IconComp = (icons as Record<string, (props: { size?: number; className?: string }) => JSX.Element>)[
        exactLucide
      ];
      if (IconComp) {
        const svgCode = renderToStaticMarkup(<IconComp size={48} />);
        handleUpdateContent({
          contentType: "icon",
          iconVariable: undefined,
          lucideIcon: exactLucide,
          iconOrSvg: svgCode,
        });
      }
      if (closePopover) setIconVariablePopoverOpen(false);
      return;
    }
    setIconVariableInput(iconVariableName || "");
    if (closePopover) setIconVariablePopoverOpen(false);
  };

  const handleIconVariableInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commitIconVariableInput(true);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setIconVariableInput(iconVariableName || "");
      setIconVariablePopoverOpen(false);
    }
  };

  const renderIconPreview = (value: string, className: string = "h-4 w-4") => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith("lucide:")) {
      const iconName = trimmed.slice("lucide:".length);
      const IconComp = (icons as Record<string, (props: { size?: number; className?: string }) => JSX.Element>)[
        iconName
      ];
      return IconComp ? <IconComp size={14} className="text-gray-200" /> : null;
    }
    if (isSvgMarkupValue(trimmed)) {
      const sanitized = sanitizeSvgMarkup(trimmed);
      if (!sanitized) return null;
      return (
        <span
          className={`${className} inline-flex items-center justify-center text-gray-200`}
          // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized SVG markup
          dangerouslySetInnerHTML={{ __html: sanitized }}
        />
      );
    }
    if (isImageSourceValue(trimmed)) {
      return <img src={trimmed} alt="" className={`${className} object-contain`} />;
    }
    return (
      <span className="text-xs text-gray-200">
        {trimmed.length > 2 ? trimmed.slice(0, 2) : trimmed}
      </span>
    );
  };
  const renderLucideIconPreview = (iconName: string) => {
    const IconComp = (icons as Record<string, (props: { size?: number; className?: string }) => JSX.Element>)[
      iconName
    ];
    return IconComp ? <IconComp size={16} /> : null;
  };
  const selectLocalLucideIcon = (iconName: string) => {
    const IconComp = (icons as Record<string, (props: { size?: number; className?: string }) => JSX.Element>)[
      iconName
    ];
    if (!IconComp) return;
    const svgCode = renderToStaticMarkup(<IconComp size={48} />);
    handleUpdateContent({
      contentType: "icon",
      iconVariable: undefined,
      lucideIcon: iconName,
      iconOrSvg: svgCode,
    });
    setIconVariablePopoverOpen(false);
  };
  const hasIconVariables = craftspaceOnlyIconVariables.length > 0 || projectIconVariables.length > 0;
  const iconVariableField = (
    <div className="space-y-1">
      {renderStyleLabel({
        label: "Icon",
        key: "iconVariable",
        currentValue: content.iconVariable,
        fallbackValue: "",
        title: getStyleSourceTooltip("iconVariable"),
        onClick: (e) => handleStyleLabelOptionClick(e, "iconVariable", ""),
        hoverHandlers: labelHoverHandlers("iconVariable"),
      })}
      <Popover open={iconVariablePopoverOpen} onOpenChange={setIconVariablePopoverOpen}>
        <PopoverAnchor asChild>
          <div
            data-icon-variable-anchor="true"
            className={`flex items-center gap-2 ${hotspotFieldClassName} h-10 px-2`}
          >
            {iconVariableValue ? renderIconPreview(iconVariableValue, "h-4 w-4") : null}
            <input
              ref={iconVariableInputRef}
              value={iconVariableInput}
              onChange={(event) => {
                handleIconVariableInputChange(event.target.value, event.currentTarget.selectionStart);
                setIconVariablePopoverOpen(true);
              }}
              onFocus={(event) => {
                setIconVariablePopoverOpen(true);
                event.currentTarget.select();
              }}
              onBlur={() => commitIconVariableInput(false)}
              onKeyDown={handleIconVariableInputKeyDown}
              placeholder="Type icon variable..."
              className="flex-1 bg-transparent text-xs text-white outline-none placeholder:text-gray-400"
            />
            <PopoverTrigger asChild>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-200"
                aria-label="Toggle icon variables"
              >
                <ChevronDown size={14} />
              </button>
            </PopoverTrigger>
          </div>
        </PopoverAnchor>
        <PopoverContent
          className="z-[10000] w-72 max-w-[calc(100vw-140px)] p-3 bg-gray-800 border-gray-700"
          align="start"
          onOpenAutoFocus={(event) => event.preventDefault()}
          onInteractOutside={(event) => {
            const target = event.target as HTMLElement | null;
            if (target?.closest?.("[data-icon-variable-anchor='true']")) {
              event.preventDefault();
            }
          }}
        >
          <div className="space-y-2">
            {filteredProjectIconVariables.length > 0 && (
              <div>
                <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-gray-400 mb-1">
                  <span>Project</span>
                  <button
                    type="button"
                    onClick={() => {
                      if (projectId) {
                        navigate(`/${projectId}/variables`);
                      }
                    }}
                    className={cn(
                      "normal-case tracking-normal text-[10px] font-medium text-blue-300 hover:text-blue-200",
                      !projectId && "cursor-not-allowed text-gray-500 hover:text-gray-500",
                    )}
                    disabled={!projectId}
                  >
                    Manage
                  </button>
                </div>
                <div className="space-y-1">
                  {filteredProjectIconVariables.map((iconVar) => (
                    <button
                      key={`proj-${iconVar.id}`}
                      type="button"
                      onClick={() => {
                        selectIconVariable(iconVar.name);
                      }}
                      className="w-full flex items-center justify-between p-2 rounded bg-gray-700 hover:bg-gray-600 text-left"
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        {renderIconPreview(iconVar.value, "h-4 w-4")}
                        <span className="text-xs text-white truncate">{iconVar.name}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {filteredCraftspaceIconVariables.length > 0 && (
              <div>
                <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-gray-400 mb-1">
                  <span>Craftspace</span>
                  <button
                    type="button"
                    onClick={() => {
                      if (craftspaceId) {
                        navigate("/dashboard", {
                          state: { activeView: "variables-icons", craftspaceId },
                        });
                      }
                    }}
                    className={cn(
                      "normal-case tracking-normal text-[10px] font-medium text-blue-300 hover:text-blue-200",
                      !craftspaceId && "cursor-not-allowed text-gray-500 hover:text-gray-500",
                    )}
                    disabled={!craftspaceId}
                  >
                    Manage
                  </button>
                </div>
                <div className="space-y-1">
                  {filteredCraftspaceIconVariables.map((iconVar) => (
                    <button
                      key={`ws-${iconVar.id}`}
                      type="button"
                      onClick={() => {
                        selectIconVariable(iconVar.name);
                      }}
                      className="w-full flex items-center justify-between p-2 rounded bg-gray-700 hover:bg-gray-600 text-left"
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        {renderIconPreview(iconVar.value, "h-4 w-4")}
                        <span className="text-xs text-white truncate">{iconVar.name}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">
                Local
              </div>
              <div className="grid grid-cols-6 gap-1 justify-items-center max-h-48 overflow-y-auto overflow-x-hidden rounded p-0">
                {filteredLucideIconNames.map((iconName) => (
                  <button
                    key={iconName}
                    type="button"
                    onClick={() => selectLocalLucideIcon(iconName)}
                    className="flex items-center justify-center h-9 w-9 rounded bg-gray-700 hover:bg-gray-600 text-gray-200"
                    title={iconName}
                    aria-label={`Use ${iconName}`}
                  >
                    {renderLucideIconPreview(iconName)}
                  </button>
                ))}
                {filteredLucideIconNames.length === 0 && (
                  <div className="col-span-6 text-xs text-gray-400">
                    No local icons match "{iconVariableInput}".
                  </div>
                )}
              </div>


            </div>

            {hasIconVariables && !hasFilteredIconResults && (
              <p className="text-xs text-gray-400">
                No icon variables match "{iconVariableInput}".
              </p>
            )}

            {!hasIconVariables && (
              <p className="text-xs text-gray-400">
                No icon variables yet. Create them in Variables → Icons.
              </p>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-y-scroll">
        <div className="pl-3 pr-1 pt-0 pb-3">
          <Tabs
            value={hotspotTab}
            onValueChange={(value) => setHotspotTab(value as "style" | "actions")}
            className="w-full"
          >
            <TabsList className="sticky top-0 z-20 grid h-10 min-h-10 w-full grid-cols-3 items-stretch bg-gray-800/95 p-0 pb-0 mb-1 rounded-none border-b border-gray-700 backdrop-blur">
              <TabsTrigger
                value="style"
                className="relative flex h-full min-h-full items-center justify-center whitespace-nowrap rounded-none bg-transparent px-3 py-0 text-[11px] font-semibold leading-none tracking-[0.2em] text-gray-400 transition-colors hover:bg-gray-700/40 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-white after:absolute after:bottom-0 after:left-0 after:right-0 after:h-1 after:bg-transparent after:content-[''] data-[state=active]:after:bg-blue-500"
              >
                STYLING
              </TabsTrigger>
              <TabsTrigger
                value="settings"
                className="relative flex h-full min-h-full items-center justify-center whitespace-nowrap rounded-none bg-transparent px-3 py-0 text-[11px] font-semibold leading-none tracking-[0.2em] text-gray-400 transition-colors hover:bg-gray-700/40 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-white after:absolute after:bottom-0 after:left-0 after:right-0 after:h-1 after:bg-transparent after:content-[''] data-[state=active]:after:bg-blue-500"
              >
                SETTINGS
              </TabsTrigger>
              <TabsTrigger
                value="actions"
                className="relative flex h-full min-h-full items-center justify-center whitespace-nowrap rounded-none bg-transparent px-3 py-0 text-[11px] font-semibold leading-none tracking-[0.2em] text-gray-400 transition-colors hover:bg-gray-700/40 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-white after:absolute after:bottom-0 after:left-0 after:right-0 after:h-1 after:bg-transparent after:content-[''] data-[state=active]:after:bg-blue-500"
              >
                ACTIONS
              </TabsTrigger>
            </TabsList>

            {/* Style Tab */}
            <TabsContent value="style" className="mt-2">
              <div className="space-y-3">
                {/* Hotspot Styles */}
                <div className="sticky top-0 z-20 px-0 pb-2 pt-2 bg-gray-800/95 backdrop-blur">
                  <Label className="text-xs text-gray-400 p-0.5 mb-1">Hotspot Styles</Label>
                  <div className="relative">
                    <div
                      className="flex min-h-8 w-full items-stretch overflow-hidden rounded-md bg-gray-700 text-white"
                      onMouseDown={(e) => {
                        if ((e.target as HTMLElement).tagName === "INPUT") {
                          return;
                        }
                        if ((e.target as HTMLElement).closest("[data-style-source-chip]")) {
                          return;
                        }
                        e.preventDefault();
                        setIsStyleDropdownOpen(true);
                        requestAnimationFrame(() => stylePresetInputRef.current?.focus());
                      }}
                    >
                      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1 px-1.5 py-1">
                        {attachedStyleSourceChips.map((preset) => {
                          const isActive = selectedStylePresetId === preset.id;
                          const isUnderlined = hoveredStyleSourceId === preset.id && !isActive;
                          return (
                            <button
                              key={preset.id}
                              type="button"
                              data-style-source-chip="true"
                              className={`group inline-flex h-6 max-w-[120px] items-center gap-1 rounded px-2 text-[11px] font-medium leading-none transition-colors ${
                                isActive
                                  ? "bg-[#006ACC] text-white"
                                  : "bg-gray-600 text-gray-100 hover:bg-gray-500"
                              }`}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setSelectedStylePresetId(preset.id);
                                setStylePresetInputValue("");
                              }}
                            >
                              <span
                                className={`truncate underline decoration-transparent decoration-2 underline-offset-[2px] transition-colors duration-150 ${
                                  isUnderlined ? "!decoration-orange-400" : ""
                                }`}
                              >
                                {preset.name}
                              </span>
                              <span
                                className={`cursor-pointer text-[10px] ${
                                  isActive ? "text-white/80 hover:text-white" : "text-gray-300 hover:text-white"
                                }`}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleDetachStylePreset(preset.id);
                                }}
                              >
                                ✕
                              </span>
                            </button>
                          );
                        })}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              data-style-source-chip="true"
                              className={`inline-flex h-6 items-center rounded px-2 text-[11px] font-medium leading-none transition-colors ${
                                selectedStylePresetId === HOTSPOT_LOCAL_STYLE_ID
                                  ? "bg-[#006ACC] text-white"
                                  : "bg-gray-600 text-gray-100 hover:bg-gray-500"
                              }`}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setSelectedStylePresetId(HOTSPOT_LOCAL_STYLE_ID);
                                setStylePresetInputValue("");
                              }}
                            >
                              <span
                                className={`underline decoration-transparent decoration-2 underline-offset-[2px] transition-colors duration-150 ${
                                  hoveredStyleSourceId === HOTSPOT_LOCAL_STYLE_ID &&
                                  selectedStylePresetId !== HOTSPOT_LOCAL_STYLE_ID
                                    ? "!decoration-orange-400"
                                    : ""
                                }`}
                              >
                                local
                              </span>
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom">
                            Changes on local only affect this hotspot.
                          </TooltipContent>
                        </Tooltip>
                        <input
                          ref={stylePresetInputRef}
                          value={stylePresetInputValue}
                          onFocus={() => setIsStyleDropdownOpen(true)}
                          onBlur={() => {
                            setTimeout(() => {
                              setIsStyleDropdownOpen(false);
                              setDeleteConfirmPopoverOpen(false);
                              setPresetToDelete(null);
                            }, 120);
                          }}
                          onChange={(e) => {
                            setStylePresetInputValue(e.target.value);
                            setIsStyleDropdownOpen(true);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleStylePresetInputEnter();
                            }
                          }}
                          placeholder=""
                          className="min-w-[56px] flex-1 bg-transparent px-1 text-xs text-white outline-none placeholder:text-gray-500"
                        />
                      </div>
                      <div className="flex flex-shrink-0 items-center self-stretch border-l border-gray-600 px-2">
                        <button
                          type="button"
                          className="text-gray-400 hover:text-gray-200"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setIsStyleDropdownOpen((open) => !open);
                          }}
                          aria-label="Toggle hotspot style list"
                        >
                          <ChevronDown size={14} />
                        </button>
                      </div>
                    </div>
                    {isStyleDropdownOpen &&
                      (compatibleStylePresets.length > 0 ||
                        stylePresetInputValue.trim() ||
                        selectedStylePresetId) && (
                      <div className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-md border border-gray-600 bg-gray-700 shadow-lg">
                        {stylePresetInputValue.trim() &&
                          !matchingPresetByInput &&
                          normalizeStyleName(stylePresetInputValue) !== HOTSPOT_LOCAL_STYLE_ID && (
                            <>
                              <div className="px-3 pt-2 pb-1 text-[11px] font-semibold text-gray-300">
                                New token
                              </div>
                              <div
                                className="px-3 py-2 text-left text-xs text-white hover:bg-gray-600 cursor-pointer"
                                onMouseDown={(e) => {
                                  if (e.button !== 0) return;
                                  e.preventDefault();
                                  handleStylePresetInputEnter();
                                }}
                              >
                                Create <span className="rounded bg-[#006ACC] px-1 text-white">{stylePresetInputValue.trim()}</span>
                              </div>
                            </>
                          )}
                        <div className="px-3 pt-2 pb-1 text-[11px] font-semibold text-gray-300">
                          Global tokens
                        </div>
                        {filteredStylePresets.map((preset) => (
                          <div
                            key={preset.id}
                            className={`flex items-center justify-between px-3 py-2 text-left text-xs group cursor-pointer ${
                              preset.id === selectedStylePresetId
                                ? "bg-blue-500/30 text-blue-100"
                                : resolvedStyleState.attachedPresetIds.includes(preset.id)
                                  ? "bg-gray-600/70 text-gray-200 hover:bg-gray-600"
                                  : "text-white hover:bg-gray-600"
                            }`}
                            onMouseDown={(e) => {
                              if (e.button !== 0) return; // Only left click
                              e.preventDefault();
                              handleApplyStylePreset(preset);
                              setIsStyleDropdownOpen(false);
                            }}
                          >
                            <span className="flex-1">{preset.name}</span>
                            <div className="flex items-center gap-2 ml-2">
                              <button
                                ref={deleteButtonRef}
                                type="button"
                                className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-300 text-xs leading-none"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (e.metaKey || e.ctrlKey) {
                                    handleDeleteStylePreset(preset.id);
                                    setIsStyleDropdownOpen(false);
                                  } else {
                                    setPresetToDelete(preset);
                                    setDeleteConfirmPopoverOpen(true);
                                    // Calculate button position for fixed positioning
                                    const rect = deleteButtonRef.current?.getBoundingClientRect();
                                    if (rect) {
                                      const tooltipWidth = 192; // w-48 = 12rem = 192px
                                      const tooltipHeight = 100; // approximate
                                      const viewportWidth = window.innerWidth;
                                      const viewportHeight = window.innerHeight;

                                      // Try to position to the right first
                                      let left = rect.right + 8;

                                      // If it would go off-screen, position to the left instead
                                      if (left + tooltipWidth + 10 > viewportWidth) {
                                        left = Math.max(10, rect.left - tooltipWidth - 8);
                                      }

                                      // Vertical position: center on button with offset downward, but keep within viewport
                                      let top = rect.top + rect.height / 2 - tooltipHeight / 2 + 15;
                                      top = Math.max(10, Math.min(top, viewportHeight - tooltipHeight - 10));

                                      setDeleteButtonPosition({ top, left });
                                    }
                                  }
                                }}
                                title="Delete (Cmd+Click to skip confirmation)"
                              >
                                ✕
                              </button>
                              {deleteConfirmPopoverOpen && presetToDelete?.id === preset.id && deleteButtonPosition && (
                                <div className="fixed z-50 bg-gray-700 border border-gray-600 text-white rounded-md shadow-md p-3 w-48 animate-in fade-in-0 zoom-in-95" style={{ top: `${deleteButtonPosition.top}px`, left: `${deleteButtonPosition.left}px` }}>
                                  <div className="space-y-2">
                                    <p className="text-sm text-white">Delete style "{preset.name}"?</p>
                                    <div className="flex gap-2">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          setDeleteConfirmPopoverOpen(false);
                                          setPresetToDelete(null);
                                        }}
                                      >
                                        Cancel
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="destructive"
                                        onClick={() => {
                                          handleDeleteStylePreset(preset.id);
                                          setIsStyleDropdownOpen(false);
                                          setDeleteConfirmPopoverOpen(false);
                                          setPresetToDelete(null);
                                        }}
                                      >
                                        Delete
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {(viewportControls || localHotspot.type === "html") && (
                  <div className="grid grid-cols-2 gap-3 !mt-0">
                    {viewportControls ? (
                      <div className="space-y-1">
                        <Select
                          value={viewportControls.activeBreakpointKey ?? "base"}
                          onValueChange={(value) => {
                            const preset = BREAKPOINT_PRESETS.find((item) => item.key === value);
                            if (!preset || preset.width === null) {
                              viewportControls.setViewportAuto();
                            } else {
                              viewportControls.setViewportFixedWidth(preset.width);
                            }
                          }}
                        >
                          <SelectTrigger className="h-8 text-xs bg-gray-700/60 border-0 px-3 pr-2 rounded-md text-white focus:ring-0 focus:ring-offset-0">
                            <SelectValue placeholder="Base" />
                          </SelectTrigger>
                          <SelectContent side="bottom" align="start">
                            {BREAKPOINT_PRESETS.map((preset) => (
                              <SelectItem key={preset.key} value={preset.key}>
                                <div className="flex items-center gap-2">
                                  {preset.icon && <preset.icon className="h-3.5 w-3.5" />}
                                  <span>{preset.label}</span>
                                  {preset.width !== null && (
                                    <span className="text-gray-400 text-[11px]">≤ {preset.width}px</span>
                                  )}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : (
                      <div />
                    )}

                    {localHotspot.type === "html" ? (
                      <div className="space-y-1">
                        <Select
                          value={htmlStyleTab}
                          onValueChange={(value) =>
                            setHtmlStyleTab(value as "base" | "hover" | "active" | "visited")
                          }
                        >
                          <SelectTrigger className="h-8 text-xs bg-gray-700/60 border-0 px-3 pr-2 rounded-md text-white focus:ring-0 focus:ring-offset-0">
                            <SelectValue placeholder="Normal" />
                          </SelectTrigger>
                          <SelectContent side="bottom" align="start">
                            <SelectItem value="base">
                              <div className="flex items-center gap-2">
                                <MousePointer2 className="h-3.5 w-3.5" />
                                <span>Normal</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="hover">
                              <div className="flex items-center gap-2">
                                <SquareMousePointer className="h-3.5 w-3.5" />
                                <span>Hover</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="active">
                              <div className="flex items-center gap-2">
                                <Check className="h-3.5 w-3.5" />
                                <span>Active</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="visited">
                              <div className="flex items-center gap-2">
                                <Eye className="h-3.5 w-3.5" />
                                <span>Visited</span>
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    ) : (
                      <div />
                    )}
                  </div>
                )}

                {localHotspot.type === "html" && <Separator className="bg-gray-700" />}
                <div className="space-y-4">

                {/* Color and Icon for SVG hotspots */}
                {localHotspot.type === "svg" && (
                  <>
                    <div className="flex items-center space-x-2">
                      <div className="flex-1">
                        <ColorPicker
                          value={content.color || "#E3F7FF"}
                          onChange={(color) => handleUpdateContent({ color })}
                          label="Background Color"
                          labelClassName={changedLabelClass(
                            getStyleFieldState("color", content.color, "#E3F7FF"),
                          )}
                          labelTooltip={getStyleSourceTooltip("color")}
                          onLabelMouseEnter={labelHoverHandlers("color").onMouseEnter}
                          onLabelMouseLeave={labelHoverHandlers("color").onMouseLeave}
                          onReset={() => resetStyleFieldToBaseline("color", "#E3F7FF")}
                          showReset={isActiveStyleLabel(getStyleFieldState("color", content.color, "#E3F7FF"))}
                          variant="compact"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      {renderStyleLabel({
                        label: "Icon or SVG",
                        key: "iconOrSvg",
                        currentValue: content.iconOrSvg,
                        fallbackValue: "",
                        title: getStyleSourceTooltip("iconOrSvg"),
                        onClick: (e) => handleStyleLabelOptionClick(e, "iconOrSvg", ""),
                        hoverHandlers: labelHoverHandlers("iconOrSvg"),
                      })}
                      <div className="flex items-center space-x-2">
                        <Input
                          value={iconOrSvgValue}
                          onChange={(e) => handleUpdateContent({ iconOrSvg: e.target.value })}
                          placeholder="Enter a single emoji/character, <path .../>, or <svg>...</svg>"
                          className={`${hotspotFieldClassName} px-3`}
                        />
                        <Popover open={iconPopoverOpen} onOpenChange={setIconPopoverOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="h-10 w-10 p-0 flex items-center justify-center bg-gray-700 border-gray-600 text-white hover:bg-gray-600 text-white"
                              tabIndex={-1}
                              type="button"
                            >
                              <span className="text-lg">★</span>
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-64 p-4 bg-gray-800 border-gray-700"
                            onOpenAutoFocus={(e) => e.preventDefault()}
                          >
                            <div className="grid grid-cols-4 gap-2">
                              {REMIX_ICON_PRESETS.map((icon) => {
                                const sanitizedIcon = sanitizeSvgMarkup(icon);
                                if (!sanitizedIcon) {
                                  return null;
                                }
                                return (
                                  <button
                                    key={icon}
                                    className="w-12 h-12 flex items-center justify-center rounded border border-gray-600 bg-gray-700 hover:bg-gray-600 text-white"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleUpdateContent({
                                        iconOrSvg: icon,
                                      }); /* do not close popover */
                                    }}
                                    type="button"
                                    tabIndex={-1}
                                    // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized preset SVG
                                    dangerouslySetInnerHTML={{ __html: sanitizedIcon }}
                                  />
                                );
                              })}
                            </div>
                          </PopoverContent>
                        </Popover>
                        <Popover open={emojiPopoverOpen} onOpenChange={setEmojiPopoverOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="h-10 w-10 p-0 flex items-center justify-center bg-gray-700 border-gray-600 text-white hover:bg-gray-600 text-white"
                              tabIndex={-1}
                              type="button"
                            >
                              <span className="text-lg">😊</span>
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-64 p-4 bg-gray-800 border-gray-700"
                            onOpenAutoFocus={(e) => e.preventDefault()}
                          >
                            <div className="grid grid-cols-8 gap-2">
                              {EMOJI_PRESETS.map((emoji) => (
                                <button
                                  key={emoji}
                                  className="w-8 h-8 flex items-center justify-center rounded border border-gray-600 bg-gray-700 hover:bg-gray-600 text-lg"
                                  onClick={() => {
                                    handleUpdateContent({
                                      iconOrSvg: emoji,
                                    }); /* do not close popover */
                                  }}
                                  type="button"
                                  tabIndex={-1}
                                >
                                  {emoji}
                                </button>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                      <LucideIconSearch
                        onIconSelect={handleHotspotLucideIconSelect}
                        selectedIcon={typeof content.lucideIcon === "string" ? content.lucideIcon : undefined}
                        onSvgUpload={(svgMarkup) =>
                          handleUpdateContent({
                            contentType: "icon",
                            iconVariable: undefined,
                            lucideIcon: undefined,
                            iconOrSvg: svgMarkup,
                          })
                        }
                        onClear={() =>
                          handleUpdateContent({
                            iconVariable: undefined,
                            lucideIcon: undefined,
                            iconOrSvg: "",
                          })
                        }
                      />
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-300">Upload Icon (SVG)</Label>
                        <div className="relative">
                          <input
                            type="file"
                            accept=".svg,image/svg+xml,.png,image/png"
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              const isSvg =
                                file.type === "image/svg+xml" ||
                                file.name.toLowerCase().endsWith(".svg");
                              if (isSvg) {
                                const reader = new FileReader();
                                reader.onload = (event) => {
                                  const result = event.target?.result as string;
                                  const normalized = normalizeSvgIconSize(result);
                                  const sanitized = sanitizeSvgMarkup(normalized);
                                  if (!sanitized) return;
                                  handleUpdateContent({
                                    lucideIcon: undefined,
                                    iconOrSvg: sanitized,
                                  });
                                };
                                reader.readAsText(file);
                              } else if (
                                file.type === "image/png" ||
                                file.name.toLowerCase().endsWith(".png")
                              ) {
                                const reader = new FileReader();
                                reader.onload = () => {
                                  const dataUrl = reader.result as string;
                                  const img = new Image();
                                  img.onload = () => {
                                    const width = Math.max(1, Math.round(img.naturalWidth || 1));
                                    const height = Math.max(1, Math.round(img.naturalHeight || 1));
                                    const svgMarkup = `
                                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="48" height="48" preserveAspectRatio="xMidYMid meet">
                                        <image href="${dataUrl}" width="${width}" height="${height}" preserveAspectRatio="xMidYMid meet" />
                                      </svg>
                                    `;
                                    const sanitized = sanitizeSvgMarkup(svgMarkup);
                                    if (!sanitized) return;
                                    handleUpdateContent({
                                      lucideIcon: undefined,
                                      iconOrSvg: sanitized,
                                    });
                                  };
                                  img.src = dataUrl;
                                };
                                reader.readAsDataURL(file);
                              }
                              e.currentTarget.value = "";
                            }}
                          />
                          <div className="flex items-center justify-between w-full h-8 px-3 py-1 text-xs bg-gray-700 border border-gray-600 text-white rounded cursor-pointer hover:bg-gray-600 transition-colors">
                            <span className="truncate">Choose SVG/PNG</span>
                            <span className="text-gray-300">Browse</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <div className="flex-1">
                        <ColorPicker
                          value={content.fontColor || "#ffffff"}
                          onChange={(fontColor) => handleUpdateContent({ fontColor })}
                          label="Font Color"
                          labelClassName={changedLabelClass(
                            getStyleFieldState("fontColor", content.fontColor, "#ffffff"),
                          )}
                          labelTooltip={getStyleSourceTooltip("fontColor")}
                          onLabelMouseEnter={labelHoverHandlers("fontColor").onMouseEnter}
                          onLabelMouseLeave={labelHoverHandlers("fontColor").onMouseLeave}
                          onReset={() => resetStyleFieldToBaseline("fontColor", "#ffffff")}
                          showReset={isActiveStyleLabel(getStyleFieldState("fontColor", content.fontColor, "#ffffff"))}
                          variant="compact"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <Separator className="bg-gray-700" />

              {/* Video Hotspot Settings */}
              {localHotspot.type === "video" && (
                <>
                  <div className="space-y-2">
                    <h4 className="text-xs text-gray-400 font-medium">Video</h4>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-400">Video URL</Label>
                      <Input
                        type="text"
                        value={(content.videoUrl as string) || ""}
                        onChange={(e) => handleUpdateContent({ videoUrl: e.target.value })}
                        placeholder="https://example.com/video.mp4"
                        className={hotspotFieldClassName}
                      />
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs text-gray-400">Upload Video</Label>
                      <div className="relative">
                        <input
                          type="file"
                          accept="video/*"
                          disabled={isVideoUploading}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              void handleVideoUpload(file);
                            }
                            e.currentTarget.value = "";
                          }}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        <div className="flex items-center justify-between w-full h-8 px-3 py-1 text-xs bg-gray-700 border border-gray-600 text-white rounded cursor-pointer hover:bg-gray-600 transition-colors">
                          <span className="truncate">
                            {isVideoUploading ? "Uploading..." : videoUploadName || "Choose File"}
                          </span>
                          <span className="text-gray-300">Browse</span>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { key: "videoLoop", label: "Loop", value: content.videoLoop !== false },
                        { key: "videoMuted", label: "Muted", value: content.videoMuted !== false },
                        { key: "videoAutoplay", label: "Autoplay", value: content.videoAutoplay !== false },
                      ].map((toggle) => (
                        <div
                          key={toggle.key}
                          className="flex items-center justify-between rounded border border-gray-700 bg-gray-800/60 px-2 py-1"
                        >
                          <Label className="text-[11px] text-gray-300">{toggle.label}</Label>
                          <Switch
                            checked={toggle.value}
                            onCheckedChange={(checked) =>
                              handleUpdateContent({ [toggle.key]: checked })
                            }
                            className="h-4 w-8 data-[state=checked]:bg-gray-500 data-[state=unchecked]:bg-gray-600 [&>span]:h-3 [&>span]:w-3 [&>span]:bg-gray-300 [&>span]:data-[state=checked]:translate-x-4"
                          />
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-400">Width</Label>
                        <SmoothNumberInput
                          value={Number(content.videoWidth ?? 80)}
                          onChange={(value) => handleUpdateContent({ videoWidth: value })}
                          className={hotspotFieldClassName}
                          step={1}
                          min={1}
                          max={1000}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-400">Height</Label>
                        <SmoothNumberInput
                          value={Number(content.videoHeight ?? 45)}
                          onChange={(value) => handleUpdateContent({ videoHeight: value })}
                          className={hotspotFieldClassName}
                          step={1}
                          min={1}
                          max={1000}
                        />
                      </div>
                    </div>
                  </div>

                  <Separator className="bg-gray-700" />

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs text-gray-400 font-medium">Chroma Key</h4>
                      <Switch
                        checked={content.chromaKeyEnabled !== false}
                        onCheckedChange={(checked) => handleUpdateContent({ chromaKeyEnabled: checked })}
                        className="h-4 w-8 data-[state=checked]:bg-gray-500 data-[state=unchecked]:bg-gray-600 [&>span]:h-3 [&>span]:w-3 [&>span]:bg-gray-300 [&>span]:data-[state=checked]:translate-x-4"
                      />
                    </div>

                    {content.chromaKeyEnabled !== false && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div
                              className="h-5 w-5 rounded border border-gray-600"
                              style={{
                                backgroundColor:
                                  (content.chromaKeyColor as string) || "#00ff00",
                              }}
                            />
                            <span className="text-[11px] text-gray-400">
                              {(content.chromaKeyColor as string) || "#00ff00"}
                            </span>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant={isPickingChromaKey ? "secondary" : "outline"}
                            onClick={() =>
                              setChromaKeyPickHotspotId(
                                isPickingChromaKey ? null : localHotspot.id,
                              )
                            }
                            className="h-7 text-[11px]"
                          >
                            {isPickingChromaKey ? "Cancel Pick" : "Pick In Viewer"}
                          </Button>
                        </div>
                        {isPickingChromaKey && (
                          <div className="text-[11px] text-gray-500">
                            Click the video in the viewer to pick a color. Press Esc to cancel.
                          </div>
                        )}

                        <div className="grid grid-cols-3 gap-2">
                          <div className="space-y-1">
                            <Label className="text-[11px] text-gray-300">Similarity</Label>
                            <SmoothNumberInput
                              value={Number(content.chromaKeySimilarity ?? 0.12)}
                              onChange={(value) => handleUpdateContent({ chromaKeySimilarity: value })}
                              className={hotspotFieldClassName}
                              step={0.01}
                              min={0}
                              max={1}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-[11px] text-gray-300">Smoothness</Label>
                            <SmoothNumberInput
                              value={Number(content.chromaKeySmoothness ?? 0.08)}
                              onChange={(value) => handleUpdateContent({ chromaKeySmoothness: value })}
                              className={hotspotFieldClassName}
                              step={0.01}
                              min={0}
                              max={1}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-[11px] text-gray-300">Spill</Label>
                            <SmoothNumberInput
                              value={Number(content.chromaKeySpill ?? 0.2)}
                              onChange={(value) => handleUpdateContent({ chromaKeySpill: value })}
                              className={hotspotFieldClassName}
                              step={0.01}
                              min={0}
                              max={1}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <Separator className="bg-gray-700" />
                </>
              )}

              {/* Hotspot Content & Styling */}
              {localHotspot.type === "html" && (
                <>
                  <Separator className="bg-gray-700" />

                  <div className="space-y-2">
                    {htmlStyleTab === "base" && (
                      <>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <h3 className="text-xs font-semibold text-gray-300">Borders</h3>
                            <Switch
                              checked={bordersEnabled}
                              onCheckedChange={(checked) => setBordersEnabled(checked)}
                              className="h-4 w-8 shrink-0 data-[state=checked]:bg-gray-500 data-[state=unchecked]:bg-gray-600 [&>span]:h-3 [&>span]:w-3 [&>span]:bg-gray-300 [&>span]:data-[state=checked]:translate-x-4"
                            />
                          </div>

                          {bordersEnabled && (
                            <>
                              <div className="flex gap-3">
                                <button
                                  type="button"
                                  onClick={() => setBorderTabActive("border")}
                                  className={`text-xs px-1 transition-colors relative ${
                                    borderTabActive === "border"
                                      ? "text-white"
                                      : "text-gray-400 hover:text-gray-300"
                                  }`}
                                >
                                  Border
                                  {borderTabActive === "border" && (
                                    <div className="absolute -bottom-0.5 left-0 h-0.5 bg-white" style={{ width: "42px" }} />
                                  )}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setBorderTabActive("outline")}
                                  className={`text-xs px-1 transition-colors relative ${
                                    borderTabActive === "outline"
                                      ? "text-white"
                                      : "text-gray-400 hover:text-gray-300"
                                  }`}
                                >
                                  Outline
                                  {borderTabActive === "outline" && (
                                    <div className="absolute -bottom-0.5 left-0 h-0.5 bg-white" style={{ width: "47px" }} />
                                  )}
                                </button>
                              </div>
                            </>
                          )}

                          {bordersEnabled && borderTabActive === "border" && (
                            <div className="space-y-2">
                              <div className="space-y-1">
                                {renderStyleLabel({
                                  label: "Border Width",
                                  key: "borderWidth",
                                  currentValue: content.borderWidth,
                                  fallbackValue: 0,
                                  title: getStyleSourceTooltip("borderWidth"),
                                  onClick: (e) => handleStyleLabelOptionClick(e, "borderWidth", 0),
                                  hoverHandlers: labelHoverHandlers("borderWidth"),
                                })}
                                <div className="relative">
                                  <Input
                                    type="text"
                                    inputMode="decimal"
                                    pattern="[0-9]*[.,]?[0-9]*"
                                    value={borderWidthInput}
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                      setBorderWidthInput(raw);
                                      if (
                                        tryAutocompleteSizeName(raw, e.currentTarget, setBorderWidthInput)
                                      ) {
                                        return;
                                      }
                                      const normalized = raw.replace(",", ".");
                                      const value = parseFloat(normalized);
                                      if (!Number.isNaN(value)) {
                                        handleUpdateContentLocalOnly({ borderWidth: value });
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        if (
                                          applySizeVariableInput(borderWidthInput, setBorderWidthInput, (value) =>
                                            handleUpdateContent({ borderWidth: value }),
                                          )
                                        ) {
                                          e.preventDefault();
                                          e.currentTarget.select();
                                          return;
                                        }
                                        const normalized = borderWidthInput.replace(",", ".");
                                        const value = parseFloat(normalized);
                                        if (!Number.isNaN(value)) {
                                          flushUpdateContent({ borderWidth: value });
                                        }
                                        e.currentTarget.select();
                                      }
                                      if (e.key === "Escape") {
                                        const currentContent =
                                          (localHotspot?.content as HotspotContentRecord) || {};
                                        const revertValue = currentContent.borderWidth ?? 0;
                                        handleUpdateContentLocalOnly({ borderWidth: revertValue });
                                        setBorderWidthInput(String(revertValue));
                                        e.currentTarget.blur();
                                      }
                                    }}
                                    onBlur={() => {
                                      if (
                                        applySizeVariableInput(borderWidthInput, setBorderWidthInput, (value) =>
                                          handleUpdateContent({ borderWidth: value }),
                                        )
                                      ) {
                                        return;
                                      }
                                      const normalized = borderWidthInput.replace(",", ".");
                                      const value = parseFloat(normalized);
                                      if (!Number.isNaN(value)) {
                                        flushUpdateContent({ borderWidth: value });
                                      }
                                    }}
                                    className={cn("pr-8", hotspotFieldClassName)}
                                  />
                                  <NumberDropdown
                                    value={Number.isNaN(Number(borderWidthInput)) ? null : Number(borderWidthInput)}
                                    onNumberChange={(nextValue) => {
                                      const nextString = String(nextValue);
                                      setBorderWidthInput(nextString);
                                      handleUpdateContentLocalOnly({ borderWidth: nextValue });
                                      flushUpdateContent({ borderWidth: nextValue });
                                    }}
                                    allowSizeVariables
                                    onSelectVariable={(nextValue) => {
                                      setBorderWidthInput(displaySizeValue(nextValue));
                                      handleUpdateContent({ borderWidth: nextValue });
                                    }}
                                    className="absolute right-2 top-1/2 -translate-y-1/2"
                                  />
                                </div>
                              </div>

                              <div className="space-y-1">
                                {renderStyleLabel({
                                  label: "Border Radius",
                                  key: "borderRadius",
                                  currentValue: content.borderRadius,
                                  fallbackValue: 0,
                                  title: getStyleSourceTooltip("borderRadius"),
                                  onClick: (e) => handleStyleLabelOptionClick(e, "borderRadius", 0),
                                  hoverHandlers: labelHoverHandlers("borderRadius"),
                                })}
                                <div className="relative">
                                  <Input
                                    type="text"
                                    inputMode="decimal"
                                    pattern="[0-9]*[.,]?[0-9]*"
                                    value={borderRadiusInput}
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                      setBorderRadiusInput(raw);
                                      if (
                                        tryAutocompleteSizeName(raw, e.currentTarget, setBorderRadiusInput)
                                      ) {
                                        return;
                                      }
                                      const normalized = raw.replace(",", ".");
                                      const numericValue = parseFloat(normalized);
                                      if (!Number.isNaN(numericValue) && numericValue >= 0) {
                                        handleUpdateContentLocalOnly({ borderRadius: numericValue });
                                      }
                                    }}
                                    onBlur={() => {
                                      if (
                                        applySizeVariableInput(borderRadiusInput, setBorderRadiusInput, (value) =>
                                          handleUpdateContent({ borderRadius: value }),
                                        )
                                      ) {
                                        return;
                                      }
                                      const normalized = borderRadiusInput.replace(",", ".");
                                      const numericValue = parseFloat(normalized);
                                      if (!Number.isNaN(numericValue) && numericValue >= 0) {
                                        flushUpdateContent({ borderRadius: numericValue });
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        if (
                                          applySizeVariableInput(borderRadiusInput, setBorderRadiusInput, (value) =>
                                            handleUpdateContent({ borderRadius: value }),
                                          )
                                        ) {
                                          e.preventDefault();
                                          e.currentTarget.select();
                                          return;
                                        }
                                        const normalized = borderRadiusInput.replace(",", ".");
                                        const numericValue = parseFloat(normalized);
                                        if (!Number.isNaN(numericValue) && numericValue >= 0) {
                                          flushUpdateContent({ borderRadius: numericValue });
                                        }
                                        e.currentTarget.select();
                                      } else if (e.key === "Escape") {
                                        const currentContent =
                                          (localHotspot?.content as HotspotContentRecord) || {};
                                        const revertValue = currentContent.borderRadius ?? 4;
                                        handleUpdateContentLocalOnly({ borderRadius: revertValue });
                                        setBorderRadiusInput(String(revertValue));
                                        e.currentTarget.blur();
                                      }
                                    }}
                                    className={cn("pr-14", hotspotFieldClassName)}
                                    placeholder="4"
                                  />
                                  <NumberDropdown
                                    value={Number.isNaN(Number(borderRadiusInput)) ? null : Number(borderRadiusInput)}
                                    onNumberChange={(nextValue) => {
                                      const nextString = String(nextValue);
                                      setBorderRadiusInput(nextString);
                                      handleUpdateContentLocalOnly({ borderRadius: nextValue });
                                      flushUpdateContent({ borderRadius: nextValue });
                                    }}
                                    allowSizeVariables
                                    onSelectVariable={(nextValue) => {
                                      setBorderRadiusInput(displaySizeValue(nextValue));
                                      handleUpdateContent({ borderRadius: nextValue });
                                    }}
                                    className="absolute right-2 top-1/2 -translate-y-1/2"
                                  />
                                </div>
                              </div>

                              <ColorPicker
                                value={content.borderColor || "#ffffff"}
                                onChange={(color) => handleUpdateContent({ borderColor: color })}
                                label="Border Color"
                                labelClassName={changedLabelClass(
                                  getStyleFieldState("borderColor", content.borderColor, "#ffffff"),
                                )}
                                labelTooltip={getStyleSourceTooltip("borderColor")}
                                onLabelMouseEnter={labelHoverHandlers("borderColor").onMouseEnter}
                                onLabelMouseLeave={labelHoverHandlers("borderColor").onMouseLeave}
                                onReset={() => resetStyleFieldToBaseline("borderColor", "#ffffff")}
                                showReset={isActiveStyleLabel(
                                  getStyleFieldState("borderColor", content.borderColor, "#ffffff"),
                                )}
                                variant="compact"
                              />

                              <div className="space-y-1">
                                {renderStyleLabel({
                                  label: "Border Style",
                                  key: "borderStyle",
                                  currentValue: content.borderStyle,
                                  fallbackValue: "solid",
                                  title: getStyleSourceTooltip("borderStyle"),
                                  onClick: (e) => handleStyleLabelOptionClick(e, "borderStyle", "solid"),
                                  hoverHandlers: labelHoverHandlers("borderStyle"),
                                })}
                                <div className="flex h-7 border border-gray-600 rounded overflow-hidden">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setBorderStyleState("solid");
                                      handleUpdateContent({ borderStyle: "solid" });
                                    }}
                                    className={`flex-1 flex items-center justify-center border-r border-gray-600 text-xs ${
                                      borderStyleState === "solid"
                                        ? "bg-gray-500 text-white"
                                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                                    }`}
                                    title="Solid"
                                  >
                                    ━
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setBorderStyleState("dashed");
                                      handleUpdateContent({ borderStyle: "dashed" });
                                    }}
                                    className={`flex-1 flex items-center justify-center border-r border-gray-600 text-xs ${
                                      borderStyleState === "dashed"
                                        ? "bg-gray-500 text-white"
                                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                                    }`}
                                    title="Dashed"
                                  >
                                    ┅
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setBorderStyleState("dotted");
                                      handleUpdateContent({ borderStyle: "dotted" });
                                    }}
                                    className={`flex-1 flex items-center justify-center text-xs ${
                                      borderStyleState === "dotted"
                                        ? "bg-gray-500 text-white"
                                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                                    }`}
                                    title="Dotted"
                                  >
                                    ⋯
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                          {bordersEnabled && borderTabActive === "outline" && (
                            <div className="space-y-2">
                              <div className="space-y-1">
                                {renderStyleLabel({
                                  label: "Outline Style",
                                  key: "outlineStyle",
                                  currentValue: content.outlineStyle,
                                  fallbackValue: "none",
                                  title: getStyleSourceTooltip("outlineStyle"),
                                  onClick: (e) => handleStyleLabelOptionClick(e, "outlineStyle", "none"),
                                  hoverHandlers: labelHoverHandlers("outlineStyle"),
                                })}
                                <div className="flex h-7 border border-gray-600 rounded overflow-hidden">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setOutlineStyleState("none");
                                      handleUpdateContent({ outlineStyle: "none" });
                                    }}
                                    className={`flex-1 flex items-center justify-center border-r border-gray-600 text-xs ${
                                      outlineStyleState === "none"
                                        ? "bg-blue-600 text-white"
                                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                                    }`}
                                    title="None"
                                  >
                                    ✕
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setOutlineStyleState("solid");
                                      handleUpdateContent({ outlineStyle: "solid" });
                                    }}
                                    className={`flex-1 flex items-center justify-center border-r border-gray-600 text-xs ${
                                      outlineStyleState === "solid"
                                        ? "bg-blue-600 text-white"
                                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                                    }`}
                                    title="Solid"
                                  >
                                    ━
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setOutlineStyleState("dashed");
                                      handleUpdateContent({ outlineStyle: "dashed" });
                                    }}
                                    className={`flex-1 flex items-center justify-center border-r border-gray-600 text-xs ${
                                      outlineStyleState === "dashed"
                                        ? "bg-blue-600 text-white"
                                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                                    }`}
                                    title="Dashed"
                                  >
                                    ┅
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setOutlineStyleState("dotted");
                                      handleUpdateContent({ outlineStyle: "dotted" });
                                    }}
                                    className={`flex-1 flex items-center justify-center text-xs ${
                                      outlineStyleState === "dotted"
                                        ? "bg-blue-600 text-white"
                                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                                    }`}
                                    title="Dotted"
                                  >
                                    ⋯
                                  </button>
                                </div>
                              </div>

                              <div className="space-y-1">
                                {renderStyleLabel({
                                  label: "Outline Width",
                                  key: "outlineWidth",
                                  currentValue: content.outlineWidth,
                                  fallbackValue: 0,
                                  title: getStyleSourceTooltip("outlineWidth"),
                                  onClick: (e) => handleStyleLabelOptionClick(e, "outlineWidth", 0),
                                  hoverHandlers: labelHoverHandlers("outlineWidth"),
                                })}
                                <Input
                                  type="text"
                                  inputMode="decimal"
                                  pattern="[0-9]*[.,]?[0-9]*"
                                  value={outlineWidthInput}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    setOutlineWidthInput(raw);
                                    if (
                                      tryAutocompleteSizeName(raw, e.currentTarget, setOutlineWidthInput)
                                    ) {
                                      return;
                                    }
                                    const normalized = raw.replace(",", ".");
                                    const value = parseFloat(normalized);
                                    if (!Number.isNaN(value)) {
                                      handleUpdateContentLocalOnly({ outlineWidth: value });
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      if (
                                        applySizeVariableInput(outlineWidthInput, setOutlineWidthInput, (value) =>
                                          handleUpdateContent({ outlineWidth: value }),
                                        )
                                      ) {
                                        e.preventDefault();
                                        e.currentTarget.select();
                                        return;
                                      }
                                      const normalized = outlineWidthInput.replace(",", ".");
                                      const value = parseFloat(normalized);
                                      if (!Number.isNaN(value)) {
                                        flushUpdateContent({ outlineWidth: value });
                                      }
                                      e.currentTarget.select();
                                    }
                                    if (e.key === "Escape") {
                                      const currentContent =
                                        (localHotspot?.content as HotspotContentRecord) || {};
                                      const revertValue = currentContent.outlineWidth ?? 0;
                                      handleUpdateContentLocalOnly({ outlineWidth: revertValue });
                                      setOutlineWidthInput(String(revertValue));
                                      e.currentTarget.blur();
                                    }
                                  }}
                                  onBlur={() => {
                                    if (
                                      applySizeVariableInput(outlineWidthInput, setOutlineWidthInput, (value) =>
                                        handleUpdateContent({ outlineWidth: value }),
                                      )
                                    ) {
                                      return;
                                    }
                                    const normalized = outlineWidthInput.replace(",", ".");
                                    const value = parseFloat(normalized);
                                    if (!Number.isNaN(value)) {
                                      flushUpdateContent({ outlineWidth: value });
                                    }
                                  }}
                                  className={hotspotFieldClassName}
                                />
                              </div>

                              <ColorPicker
                                value={content.outlineColor || "#3b82f6"}
                                onChange={(color) => handleUpdateContent({ outlineColor: color })}
                                label="Outline Color"
                                labelClassName={changedLabelClass(
                                  getStyleFieldState("outlineColor", content.outlineColor, "#3b82f6"),
                                )}
                                labelTooltip={getStyleSourceTooltip("outlineColor")}
                                onLabelMouseEnter={labelHoverHandlers("outlineColor").onMouseEnter}
                                onLabelMouseLeave={labelHoverHandlers("outlineColor").onMouseLeave}
                                onReset={() => resetStyleFieldToBaseline("outlineColor", "#3b82f6")}
                                showReset={isActiveStyleLabel(
                                  getStyleFieldState("outlineColor", content.outlineColor, "#3b82f6"),
                                )}
                                variant="compact"
                              />

                              <div className="space-y-1">
                                {renderStyleLabel({
                                  label: "Outline Offset",
                                  key: "outlineOffset",
                                  currentValue: content.outlineOffset,
                                  fallbackValue: 0,
                                  title: getStyleSourceTooltip("outlineOffset"),
                                  onClick: (e) => handleStyleLabelOptionClick(e, "outlineOffset", 0),
                                  hoverHandlers: labelHoverHandlers("outlineOffset"),
                                })}
                                <Input
                                  type="text"
                                  inputMode="decimal"
                                  pattern="[0-9]*[.,]?[0-9]*"
                                  value={outlineOffsetInput}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    setOutlineOffsetInput(raw);
                                    if (
                                      tryAutocompleteSizeName(raw, e.currentTarget, setOutlineOffsetInput)
                                    ) {
                                      return;
                                    }
                                    const normalized = raw.replace(",", ".");
                                    const value = parseFloat(normalized);
                                    if (!Number.isNaN(value)) {
                                      handleUpdateContentLocalOnly({ outlineOffset: value });
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      if (
                                        applySizeVariableInput(outlineOffsetInput, setOutlineOffsetInput, (value) =>
                                          handleUpdateContent({ outlineOffset: value }),
                                        )
                                      ) {
                                        e.preventDefault();
                                        e.currentTarget.select();
                                        return;
                                      }
                                      const normalized = outlineOffsetInput.replace(",", ".");
                                      const value = parseFloat(normalized);
                                      if (!Number.isNaN(value)) {
                                        flushUpdateContent({ outlineOffset: value });
                                      }
                                      e.currentTarget.select();
                                    }
                                    if (e.key === "Escape") {
                                      const currentContent =
                                        (localHotspot?.content as HotspotContentRecord) || {};
                                      const revertValue = currentContent.outlineOffset ?? 0;
                                      handleUpdateContentLocalOnly({ outlineOffset: revertValue });
                                      setOutlineOffsetInput(String(revertValue));
                                      e.currentTarget.blur();
                                    }
                                  }}
                                  onBlur={() => {
                                    if (
                                      applySizeVariableInput(outlineOffsetInput, setOutlineOffsetInput, (value) =>
                                        handleUpdateContent({ outlineOffset: value }),
                                      )
                                    ) {
                                      return;
                                    }
                                    const normalized = outlineOffsetInput.replace(",", ".");
                                    const value = parseFloat(normalized);
                                    if (!Number.isNaN(value)) {
                                      flushUpdateContent({ outlineOffset: value });
                                    }
                                  }}
                                  className={hotspotFieldClassName}
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        <ColorPicker
                          value={content.backgroundColor || "#FFFFFFE6"}
                          onChange={(color) => handleUpdateContent({ backgroundColor: color })}
                          label="Background Color"
                          labelClassName={changedLabelClass(
                            getStyleFieldState("backgroundColor", content.backgroundColor, "#FFFFFFE6"),
                          )}
                          labelTooltip={getStyleSourceTooltip("backgroundColor")}
                          onLabelMouseEnter={labelHoverHandlers("backgroundColor").onMouseEnter}
                          onLabelMouseLeave={labelHoverHandlers("backgroundColor").onMouseLeave}
                          onReset={() => resetStyleFieldToBaseline("backgroundColor", "#FFFFFFE6")}
                          showReset={isActiveStyleLabel(
                            getStyleFieldState("backgroundColor", content.backgroundColor, "#FFFFFFE6"),
                          )}
                          variant="compact"
                        />

                        <div className="space-y-1">
                        {renderStyleLabel({
                          label: "Padding",
                          key: "padding",
                          currentValue: content.padding,
                          fallbackValue: 0,
                          title: getStyleSourceTooltip("padding"),
                          onClick: (e) => handleStyleLabelOptionClick(e, "padding", 0),
                          hoverHandlers: labelHoverHandlers("padding"),
                        })}
                          <div className="flex items-center gap-2">
                            <div className="relative flex-1">
                              <Input
                                type="text"
                                inputMode="decimal"
                                pattern="[0-9]*[.,]?[0-9]*"
                                min="0"
                                step="0.1"
                                value={paddingInput}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  setPaddingInput(raw);
                                  const normalized = raw.replace(",", ".");
                                  const numericValue = parseFloat(normalized);
                                  if (!Number.isNaN(numericValue) && numericValue >= 0) {
                                    handleUpdateContentLocalOnly({
                                      padding: numericValue,
                                      paddingTop: numericValue,
                                      paddingRight: numericValue,
                                      paddingBottom: numericValue,
                                      paddingLeft: numericValue,
                                    });
                                    const nextValue = String(numericValue);
                                    setPaddingTopInput(nextValue);
                                    setPaddingRightInput(nextValue);
                                    setPaddingBottomInput(nextValue);
                                    setPaddingLeftInput(nextValue);
                                  }
                                }}
                                onBlur={() => {
                                  const normalized = paddingInput.replace(",", ".");
                                  const numericValue = parseFloat(normalized);
                                  if (!Number.isNaN(numericValue) && numericValue >= 0) {
                                    flushUpdateContent({
                                      padding: numericValue,
                                      paddingTop: numericValue,
                                      paddingRight: numericValue,
                                      paddingBottom: numericValue,
                                      paddingLeft: numericValue,
                                    });
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    const normalized = paddingInput.replace(",", ".");
                                    const numericValue = parseFloat(normalized);
                                    if (!Number.isNaN(numericValue) && numericValue >= 0) {
                                      flushUpdateContent({
                                        padding: numericValue,
                                        paddingTop: numericValue,
                                        paddingRight: numericValue,
                                        paddingBottom: numericValue,
                                        paddingLeft: numericValue,
                                      });
                                    }
                                    e.currentTarget.select();
                                  } else if (e.key === "Escape") {
                                    const currentContent =
                                      (localHotspot?.content as HotspotContentRecord) || {};
                                    const revertValue = currentContent.padding ?? 6;
                                    handleUpdateContentLocalOnly({
                                      padding: revertValue,
                                      paddingTop: revertValue,
                                      paddingRight: revertValue,
                                      paddingBottom: revertValue,
                                      paddingLeft: revertValue,
                                    });
                                    const revertString = String(revertValue);
                                    setPaddingInput(revertString);
                                    setPaddingTopInput(revertString);
                                    setPaddingRightInput(revertString);
                                    setPaddingBottomInput(revertString);
                                    setPaddingLeftInput(revertString);
                                    e.currentTarget.blur();
                                  }
                                }}
                                className={hotspotFieldClassName}
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => setShowPaddingFields((value) => !value)}
                              className="h-8 w-8 flex items-center justify-center rounded border border-gray-600 hover:bg-gray-600 text-gray-400 hover:text-white"
                              title="Toggle individual padding fields"
                            >
                              ▢
                            </button>
                          </div>
                          {showPaddingFields && (
                            <div className="grid grid-cols-2 gap-2 mt-2">
                              {[
                                { key: "paddingTop", label: "↑ Top", value: paddingTopInput, setter: setPaddingTopInput },
                                { key: "paddingRight", label: "→ Right", value: paddingRightInput, setter: setPaddingRightInput },
                                { key: "paddingBottom", label: "↓ Bottom", value: paddingBottomInput, setter: setPaddingBottomInput },
                                { key: "paddingLeft", label: "← Left", value: paddingLeftInput, setter: setPaddingLeftInput },
                              ].map((field) => (
                                <div key={field.key} className="space-y-1">
                                  <div className="flex items-center gap-0.5">
                                    <Label
                                      className={`text-xs p-0.5 rounded cursor-pointer ${
                                        !areStyleValuesEqual(
                                          Number.parseFloat(field.value),
                                          getStyleBaselineValue("padding", 6),
                                        )
                                          ? "text-blue-400 bg-blue-400/20"
                                          : "text-gray-400"
                                      }`}
                                      onMouseDown={(e) => {
                                        if (!e.altKey && !e.metaKey) return;
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const revertValue = getStyleBaselineValue("padding", 6);
                                        handleUpdateContent({
                                          [field.key]: revertValue,
                                        });
                                        field.setter(String(revertValue));
                                      }}
                                    >
                                      {field.label}
                                    </Label>
                                    {!areStyleValuesEqual(
                                      Number.parseFloat(field.value),
                                      getStyleBaselineValue("padding", 6),
                                    ) && (
                                      <button
                                        type="button"
                                        className="inline-flex h-4 w-4 items-center justify-center mb-1 text-gray-400 hover:text-gray-200"
                                        title={`Clear ${field.label}`}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          const revertValue = getStyleBaselineValue("padding", 6);
                                          handleUpdateContent({
                                            [field.key]: revertValue,
                                          });
                                          field.setter(String(revertValue));
                                        }}
                                      >
                                        <X className="h-3 w-3" />
                                      </button>
                                    )}
                                  </div>
                                  <Input
                                    type="text"
                                    inputMode="decimal"
                                    pattern="[0-9]*[.,]?[0-9]*"
                                    min="0"
                                    step="0.1"
                                    value={field.value}
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                      field.setter(raw);
                                      const normalized = raw.replace(",", ".");
                                      const numericValue = parseFloat(normalized);
                                      if (!Number.isNaN(numericValue) && numericValue >= 0) {
                                        handleUpdateContentLocalOnly({ [field.key]: numericValue });
                                      }
                                    }}
                                    onBlur={() => {
                                      const normalized = field.value.replace(",", ".");
                                      const numericValue = parseFloat(normalized);
                                      if (!Number.isNaN(numericValue) && numericValue >= 0) {
                                        flushUpdateContent({ [field.key]: numericValue });
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        const normalized = field.value.replace(",", ".");
                                        const numericValue = parseFloat(normalized);
                                        if (!Number.isNaN(numericValue) && numericValue >= 0) {
                                          flushUpdateContent({ [field.key]: numericValue });
                                        }
                                        e.currentTarget.select();
                                      } else if (e.key === "Escape") {
                                        const currentContent =
                                          (localHotspot?.content as HotspotContentRecord) || {};
                                        const revertValue = currentContent[field.key] ?? 0.3;
                                        handleUpdateContentLocalOnly({ [field.key]: revertValue });
                                        field.setter(String(revertValue));
                                        e.currentTarget.blur();
                                      }
                                    }}
                                    className={`${hotspotFieldClassName} text-xs`}
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <ColorPicker
                          value={content.textColor || content.color || "#ffffff"}
                          onChange={(color) => handleUpdateContent({ textColor: color })}
                          label="Font Color"
                          labelClassName={changedLabelClass(
                            getStyleFieldState(
                              "textColor",
                              content.textColor !== undefined ? content.textColor : content.color,
                              "#ffffff",
                            ),
                          )}
                          labelTooltip={getStyleSourceTooltip("textColor")}
                          onLabelMouseEnter={labelHoverHandlers("textColor").onMouseEnter}
                          onLabelMouseLeave={labelHoverHandlers("textColor").onMouseLeave}
                          onReset={() => resetStyleFieldToBaseline("textColor", "#ffffff")}
                          showReset={isActiveStyleLabel(
                            getStyleFieldState(
                              "textColor",
                              content.textColor !== undefined ? content.textColor : content.color,
                              "#ffffff",
                            ),
                          )}
                          variant="compact"
                        />
                        <div className="space-y-1">
                          {renderStyleLabel({
                            label: "Font Size",
                            key: "fontSize",
                            currentValue: content.fontSize,
                            fallbackValue: "12px",
                            title: getStyleSourceTooltip("fontSize"),
                            onClick: (e) => handleStyleLabelOptionClick(e, "fontSize", "12px"),
                            hoverHandlers: labelHoverHandlers("fontSize"),
                          })}
                          <div className="relative">
                          <DraggableSizeInput
                            value={fontSizeInput}
                            onChange={(raw) => {
                              setFontSizeInput(raw);
                              handleUpdateContent({ fontSize: normalizeFontSizeInputValue(raw) });
                            }}
                            onNumericDragChange={(nextValue) => {
                              const displayValue = displaySizeValue(nextValue);
                              setFontSizeInput(displayValue);
                              handleUpdateContent({ fontSize: nextValue });
                            }}
                            className={cn("pr-8", hotspotFieldClassName)}
                            placeholder="12px"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                const applied = applySizeVariableInput(
                                  fontSizeInput,
                                  setFontSizeInput,
                                  (next) => handleUpdateContent({ fontSize: next }),
                                );
                                if (applied) {
                                  e.preventDefault();
                                  e.currentTarget.select();
                                  return;
                                }
                                const normalized = normalizeFontSizeInputValue(fontSizeInput);
                                setFontSizeInput(displaySizeValue(normalized));
                                handleUpdateContent({ fontSize: normalized });
                              }
                            }}
                            onBlur={() => {
                              const normalized = normalizeFontSizeInputValue(fontSizeInput);
                              if (normalized !== fontSizeInput) {
                                setFontSizeInput(displaySizeValue(normalized));
                              }
                              handleUpdateContent({ fontSize: normalized });
                            }}
                          />
                            <NumberDropdown
                              value={fontSizeNumber}
                              onNumberChange={(nextValue) =>
                                handleUpdateContent({ fontSize: `${nextValue}${fontSizeUnit}` })
                              }
                              allowSizeVariables
                              onSelectVariable={(nextValue) => {
                                setFontSizeInput(displaySizeValue(nextValue));
                                handleUpdateContent({ fontSize: nextValue });
                              }}
                              unitLabel={fontSizeUnit}
                              className="absolute right-2 top-1/2 -translate-y-1/2"
                            />
                          </div>
                        </div>
                        {localHotspot?.type === "html" &&
                          (content.icon || content.lucideIcon || content.iconVariable) && (
                          <div className="space-y-1">
                            {renderStyleLabel({
                              label: "Icon Size",
                              key: "iconSize",
                              currentValue: content.iconSize,
                              fallbackValue: "100%",
                              title: getStyleSourceTooltip("iconSize"),
                              onClick: (e) => handleStyleLabelOptionClick(e, "iconSize", "100%"),
                              hoverHandlers: labelHoverHandlers("iconSize"),
                            })}
                            <div className="relative">
                              <DraggableSizeInput
                                value={iconSizeInput}
                                onChange={(raw) => {
                                  setIconSizeInput(raw);
                                  handleUpdateContent({ iconSize: normalizeFontSizeInputValue(raw) });
                                }}
                                onNumericDragChange={(nextValue) => {
                                  const displayValue = displaySizeValue(nextValue);
                                  setIconSizeInput(displayValue);
                                  handleUpdateContent({ iconSize: nextValue });
                                }}
                                className={cn("pr-8", hotspotFieldClassName)}
                                placeholder="100%"
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    const applied = applySizeVariableInput(
                                      iconSizeInput,
                                      setIconSizeInput,
                                      (next) => handleUpdateContent({ iconSize: next }),
                                    );
                                    if (applied) {
                                      e.preventDefault();
                                      e.currentTarget.select();
                                      return;
                                    }
                                    const normalized = normalizeFontSizeInputValue(iconSizeInput);
                                    setIconSizeInput(displaySizeValue(normalized));
                                    handleUpdateContent({ iconSize: normalized });
                                  }
                                }}
                                onBlur={() => {
                                  const normalized = normalizeFontSizeInputValue(iconSizeInput);
                                  if (normalized !== iconSizeInput) {
                                    setIconSizeInput(displaySizeValue(normalized));
                                  }
                                  handleUpdateContent({ iconSize: normalized });
                                }}
                              />
                              <NumberDropdown
                                value={iconSizeNumber}
                                onNumberChange={(nextValue) =>
                                  handleUpdateContent({ iconSize: `${nextValue}${iconSizeUnit}` })
                                }
                                allowSizeVariables
                                onSelectVariable={(nextValue) => {
                                  setIconSizeInput(displaySizeValue(nextValue));
                                  handleUpdateContent({ iconSize: nextValue });
                                }}
                                unitLabel={iconSizeUnit}
                                className="absolute right-2 top-1/2 -translate-y-1/2"
                              />
                            </div>
                          </div>
                        )}

                        <div className="space-y-2">
                          <h5 className="text-xs text-gray-400 font-medium">Dimensions</h5>

                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              {renderStyleLabel({
                                label: "Width",
                                key: "width",
                                currentValue: content.width,
                                fallbackValue: "auto",
                                title: getStyleSourceTooltip("width"),
                                onClick: (e) => handleStyleLabelOptionClick(e, "width", "auto"),
                                hoverHandlers: labelHoverHandlers("width"),
                              })}
                              <DraggableSizeInput
                                value={content.width || "auto"}
                                onChange={(nextValue) => {
                                  handleUpdateContent({ width: nextValue });
                                  if (localHotspot.type === "html" && nextValue && nextValue !== "auto") {
                                    resetHtmlScale();
                                  }
                                }}
                                onNumericDragChange={(nextValue) => {
                                  handleUpdateContent({ width: nextValue });
                                  if (localHotspot.type === "html") {
                                    resetHtmlScale();
                                  }
                                }}
                                className={hotspotFieldClassName}
                                placeholder="auto"
                              />
                            </div>
                            <div className="space-y-1">
                              {renderStyleLabel({
                                label: "Height",
                                key: "height",
                                currentValue: content.height,
                                fallbackValue: "auto",
                                title: getStyleSourceTooltip("height"),
                                onClick: (e) => handleStyleLabelOptionClick(e, "height", "auto"),
                                hoverHandlers: labelHoverHandlers("height"),
                              })}
                              <DraggableSizeInput
                                value={content.height || "auto"}
                                onChange={(nextValue) => {
                                  handleUpdateContent({ height: nextValue });
                                  if (localHotspot.type === "html" && nextValue && nextValue !== "auto") {
                                    resetHtmlScale();
                                  }
                                }}
                                onNumericDragChange={(nextValue) => {
                                  handleUpdateContent({ height: nextValue });
                                  if (localHotspot.type === "html") {
                                    resetHtmlScale();
                                  }
                                }}
                                className={hotspotFieldClassName}
                                placeholder="auto"
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {htmlStyleTab === "hover" && (
                      <div className="space-y-2">
                        <ColorPicker
                          value={content.hoverBackgroundColor || content.backgroundColor || "#000000"}
                          onChange={(color) => handleUpdateContent({ hoverBackgroundColor: color })}
                          label="Background Color"
                          labelClassName={changedLabelClass(
                            getStyleFieldState("hoverBackgroundColor", content.hoverBackgroundColor, undefined),
                          )}
                          labelTooltip={getStyleSourceTooltip("hoverBackgroundColor")}
                          onLabelMouseEnter={labelHoverHandlers("hoverBackgroundColor").onMouseEnter}
                          onLabelMouseLeave={labelHoverHandlers("hoverBackgroundColor").onMouseLeave}
                          onReset={() => handleUpdateContent({ hoverBackgroundColor: undefined })}
                          showReset={isActiveStyleLabel(
                            getStyleFieldState("hoverBackgroundColor", content.hoverBackgroundColor, undefined),
                          )}
                          variant="compact"
                        />

                        <ColorPicker
                          value={content.hoverBorderColor || content.borderColor || "#ffffff"}
                          onChange={(color) => handleUpdateContent({ hoverBorderColor: color })}
                          label="Border Color"
                          labelClassName={changedLabelClass(
                            getStyleFieldState("hoverBorderColor", content.hoverBorderColor, undefined),
                          )}
                          labelTooltip={getStyleSourceTooltip("hoverBorderColor")}
                          onLabelMouseEnter={labelHoverHandlers("hoverBorderColor").onMouseEnter}
                          onLabelMouseLeave={labelHoverHandlers("hoverBorderColor").onMouseLeave}
                          onReset={() => handleUpdateContent({ hoverBorderColor: undefined })}
                          showReset={isActiveStyleLabel(
                            getStyleFieldState("hoverBorderColor", content.hoverBorderColor, undefined),
                          )}
                          variant="compact"
                        />

                        <ColorPicker
                          value={content.hoverTextColor || content.textColor || content.color || "#ffffff"}
                          onChange={(color) => handleUpdateContent({ hoverTextColor: color })}
                          label="Text Color"
                          labelClassName={changedLabelClass(
                            getStyleFieldState("hoverTextColor", content.hoverTextColor, undefined),
                          )}
                          labelTooltip={getStyleSourceTooltip("hoverTextColor")}
                          onLabelMouseEnter={labelHoverHandlers("hoverTextColor").onMouseEnter}
                          onLabelMouseLeave={labelHoverHandlers("hoverTextColor").onMouseLeave}
                          onReset={() => handleUpdateContent({ hoverTextColor: undefined })}
                          showReset={isActiveStyleLabel(
                            getStyleFieldState("hoverTextColor", content.hoverTextColor, undefined),
                          )}
                          variant="compact"
                        />

                        <div className="space-y-1">
                          {renderStyleLabel({
                            label: "Box Shadow",
                            key: "hoverBoxShadow",
                            currentValue: content.hoverBoxShadow,
                            fallbackValue: undefined,
                            title: getStyleSourceTooltip("hoverBoxShadow"),
                            onClick: (e) => handleStyleLabelOptionClick(e, "hoverBoxShadow", undefined),
                            hoverHandlers: labelHoverHandlers("hoverBoxShadow"),
                          })}
                          <Input
                            type="text"
                            value={(content.hoverBoxShadow as string) || ""}
                            onChange={(e) => handleUpdateContent({ hoverBoxShadow: e.target.value })}
                            className={hotspotFieldClassName}
                            placeholder="0 6px 16px rgba(0,0,0,0.3)"
                          />
                        </div>

                        <div className="space-y-1">
                          {renderStyleLabel({
                            label: "Text Shadow",
                            key: "hoverTextShadow",
                            currentValue: content.hoverTextShadow,
                            fallbackValue: undefined,
                            title: getStyleSourceTooltip("hoverTextShadow"),
                            onClick: (e) => handleStyleLabelOptionClick(e, "hoverTextShadow", undefined),
                            hoverHandlers: labelHoverHandlers("hoverTextShadow"),
                          })}
                          <Input
                            type="text"
                            value={(content.hoverTextShadow as string) || ""}
                            onChange={(e) => handleUpdateContent({ hoverTextShadow: e.target.value })}
                            className={hotspotFieldClassName}
                            placeholder="0 2px 8px rgba(0,0,0,0.35)"
                          />
                        </div>

                        <div className="space-y-1">
                          {renderStyleLabel({
                            label: "Opacity",
                            key: "hoverOpacity",
                            currentValue: content.hoverOpacity,
                            fallbackValue: undefined,
                            title: getStyleSourceTooltip("hoverOpacity"),
                            onClick: (e) => handleStyleLabelOptionClick(e, "hoverOpacity", undefined),
                            hoverHandlers: labelHoverHandlers("hoverOpacity"),
                          })}
                          <Input
                            type="range"
                            min="0"
                            max="1"
                            step="0.05"
                            value={Number(content.hoverOpacity ?? content.opacity ?? 1)}
                            onChange={(e) =>
                              handleUpdateContent({ hoverOpacity: parseFloat(e.target.value) })
                            }
                            className="bg-gray-700 border-gray-600"
                          />
                          <div className="text-xs text-gray-400 text-center">
                            {Math.round(Number(content.hoverOpacity ?? content.opacity ?? 1) * 100)}%
                          </div>
                        </div>

                        <div className="space-y-1">
                          {renderStyleLabel({
                            label: "Transform",
                            key: "hoverTransform",
                            currentValue: content.hoverTransform,
                            fallbackValue: undefined,
                            title: getStyleSourceTooltip("hoverTransform"),
                            onClick: (e) => handleStyleLabelOptionClick(e, "hoverTransform", undefined),
                            hoverHandlers: labelHoverHandlers("hoverTransform"),
                          })}
                          <Input
                            type="text"
                            value={(content.hoverTransform as string) || ""}
                            onChange={(e) => handleUpdateContent({ hoverTransform: e.target.value })}
                            className={hotspotFieldClassName}
                            placeholder="scale(1.05)"
                          />
                        </div>
                      </div>
                    )}

                    {htmlStyleTab === "active" && (
                      <div className="space-y-2">
                        <ColorPicker
                          value={content.activeBackgroundColor || content.backgroundColor || "#000000"}
                          onChange={(color) => handleUpdateContent({ activeBackgroundColor: color })}
                          label="Background Color"
                          labelClassName={changedLabelClass(
                            getStyleFieldState("activeBackgroundColor", content.activeBackgroundColor, undefined),
                          )}
                          labelTooltip={getStyleSourceTooltip("activeBackgroundColor")}
                          onLabelMouseEnter={labelHoverHandlers("activeBackgroundColor").onMouseEnter}
                          onLabelMouseLeave={labelHoverHandlers("activeBackgroundColor").onMouseLeave}
                          onReset={() => handleUpdateContent({ activeBackgroundColor: undefined })}
                          showReset={isActiveStyleLabel(
                            getStyleFieldState("activeBackgroundColor", content.activeBackgroundColor, undefined),
                          )}
                          variant="compact"
                        />

                        <ColorPicker
                          value={content.activeBorderColor || content.borderColor || "#ffffff"}
                          onChange={(color) => handleUpdateContent({ activeBorderColor: color })}
                          label="Border Color"
                          labelClassName={changedLabelClass(
                            getStyleFieldState("activeBorderColor", content.activeBorderColor, undefined),
                          )}
                          labelTooltip={getStyleSourceTooltip("activeBorderColor")}
                          onLabelMouseEnter={labelHoverHandlers("activeBorderColor").onMouseEnter}
                          onLabelMouseLeave={labelHoverHandlers("activeBorderColor").onMouseLeave}
                          onReset={() => handleUpdateContent({ activeBorderColor: undefined })}
                          showReset={isActiveStyleLabel(
                            getStyleFieldState("activeBorderColor", content.activeBorderColor, undefined),
                          )}
                          variant="compact"
                        />

                        <ColorPicker
                          value={content.activeTextColor || content.textColor || content.color || "#ffffff"}
                          onChange={(color) => handleUpdateContent({ activeTextColor: color })}
                          label="Text Color"
                          labelClassName={changedLabelClass(
                            getStyleFieldState("activeTextColor", content.activeTextColor, undefined),
                          )}
                          labelTooltip={getStyleSourceTooltip("activeTextColor")}
                          onLabelMouseEnter={labelHoverHandlers("activeTextColor").onMouseEnter}
                          onLabelMouseLeave={labelHoverHandlers("activeTextColor").onMouseLeave}
                          onReset={() => handleUpdateContent({ activeTextColor: undefined })}
                          showReset={isActiveStyleLabel(
                            getStyleFieldState("activeTextColor", content.activeTextColor, undefined),
                          )}
                          variant="compact"
                        />

                        <div className="space-y-1">
                          {renderStyleLabel({
                            label: "Box Shadow",
                            key: "activeBoxShadow",
                            currentValue: content.activeBoxShadow,
                            fallbackValue: undefined,
                            title: getStyleSourceTooltip("activeBoxShadow"),
                            onClick: (e) => handleStyleLabelOptionClick(e, "activeBoxShadow", undefined),
                            hoverHandlers: labelHoverHandlers("activeBoxShadow"),
                          })}
                          <Input
                            type="text"
                            value={(content.activeBoxShadow as string) || ""}
                            onChange={(e) => handleUpdateContent({ activeBoxShadow: e.target.value })}
                            className={hotspotFieldClassName}
                            placeholder="0 6px 16px rgba(0,0,0,0.3)"
                          />
                        </div>

                        <div className="space-y-1">
                          {renderStyleLabel({
                            label: "Text Shadow",
                            key: "activeTextShadow",
                            currentValue: content.activeTextShadow,
                            fallbackValue: undefined,
                            title: getStyleSourceTooltip("activeTextShadow"),
                            onClick: (e) => handleStyleLabelOptionClick(e, "activeTextShadow", undefined),
                            hoverHandlers: labelHoverHandlers("activeTextShadow"),
                          })}
                          <Input
                            type="text"
                            value={(content.activeTextShadow as string) || ""}
                            onChange={(e) => handleUpdateContent({ activeTextShadow: e.target.value })}
                            className={hotspotFieldClassName}
                            placeholder="0 2px 8px rgba(0,0,0,0.35)"
                          />
                        </div>

                        <div className="space-y-1">
                          {renderStyleLabel({
                            label: "Opacity",
                            key: "activeOpacity",
                            currentValue: content.activeOpacity,
                            fallbackValue: undefined,
                            title: getStyleSourceTooltip("activeOpacity"),
                            onClick: (e) => handleStyleLabelOptionClick(e, "activeOpacity", undefined),
                            hoverHandlers: labelHoverHandlers("activeOpacity"),
                          })}
                          <Input
                            type="range"
                            min="0"
                            max="1"
                            step="0.05"
                            value={Number(content.activeOpacity ?? content.opacity ?? 1)}
                            onChange={(e) =>
                              handleUpdateContent({ activeOpacity: parseFloat(e.target.value) })
                            }
                            className="bg-gray-700 border-gray-600"
                          />
                          <div className="text-xs text-gray-400 text-center">
                            {Math.round(Number(content.activeOpacity ?? content.opacity ?? 1) * 100)}%
                          </div>
                        </div>

                        <div className="space-y-1">
                          {renderStyleLabel({
                            label: "Transform",
                            key: "activeTransform",
                            currentValue: content.activeTransform,
                            fallbackValue: undefined,
                            title: getStyleSourceTooltip("activeTransform"),
                            onClick: (e) => handleStyleLabelOptionClick(e, "activeTransform", undefined),
                            hoverHandlers: labelHoverHandlers("activeTransform"),
                          })}
                          <Input
                            type="text"
                            value={(content.activeTransform as string) || ""}
                            onChange={(e) => handleUpdateContent({ activeTransform: e.target.value })}
                            className={hotspotFieldClassName}
                            placeholder="scale(1.05)"
                          />
                        </div>
                      </div>
                    )}

                    {htmlStyleTab === "visited" && (
                      <div className="space-y-2">
                        <ColorPicker
                          value={content.visitedBackgroundColor || content.backgroundColor || "#000000"}
                          onChange={(color) => handleUpdateContent({ visitedBackgroundColor: color })}
                          label="Background Color"
                          labelClassName={changedLabelClass(
                            getStyleFieldState("visitedBackgroundColor", content.visitedBackgroundColor, undefined),
                          )}
                          labelTooltip={getStyleSourceTooltip("visitedBackgroundColor")}
                          onLabelMouseEnter={labelHoverHandlers("visitedBackgroundColor").onMouseEnter}
                          onLabelMouseLeave={labelHoverHandlers("visitedBackgroundColor").onMouseLeave}
                          onReset={() => handleUpdateContent({ visitedBackgroundColor: undefined })}
                          showReset={isActiveStyleLabel(
                            getStyleFieldState("visitedBackgroundColor", content.visitedBackgroundColor, undefined),
                          )}
                          variant="compact"
                        />

                        <ColorPicker
                          value={content.visitedBorderColor || content.borderColor || "#ffffff"}
                          onChange={(color) => handleUpdateContent({ visitedBorderColor: color })}
                          label="Border Color"
                          labelClassName={changedLabelClass(
                            getStyleFieldState("visitedBorderColor", content.visitedBorderColor, undefined),
                          )}
                          labelTooltip={getStyleSourceTooltip("visitedBorderColor")}
                          onLabelMouseEnter={labelHoverHandlers("visitedBorderColor").onMouseEnter}
                          onLabelMouseLeave={labelHoverHandlers("visitedBorderColor").onMouseLeave}
                          onReset={() => handleUpdateContent({ visitedBorderColor: undefined })}
                          showReset={isActiveStyleLabel(
                            getStyleFieldState("visitedBorderColor", content.visitedBorderColor, undefined),
                          )}
                          variant="compact"
                        />

                        <ColorPicker
                          value={content.visitedTextColor || content.textColor || content.color || "#ffffff"}
                          onChange={(color) => handleUpdateContent({ visitedTextColor: color })}
                          label="Text Color"
                          labelClassName={changedLabelClass(
                            getStyleFieldState("visitedTextColor", content.visitedTextColor, undefined),
                          )}
                          labelTooltip={getStyleSourceTooltip("visitedTextColor")}
                          onLabelMouseEnter={labelHoverHandlers("visitedTextColor").onMouseEnter}
                          onLabelMouseLeave={labelHoverHandlers("visitedTextColor").onMouseLeave}
                          onReset={() => handleUpdateContent({ visitedTextColor: undefined })}
                          showReset={isActiveStyleLabel(
                            getStyleFieldState("visitedTextColor", content.visitedTextColor, undefined),
                          )}
                          variant="compact"
                        />

                        <div className="space-y-1">
                          {renderStyleLabel({
                            label: "Box Shadow",
                            key: "visitedBoxShadow",
                            currentValue: content.visitedBoxShadow,
                            fallbackValue: undefined,
                            title: getStyleSourceTooltip("visitedBoxShadow"),
                            onClick: (e) => handleStyleLabelOptionClick(e, "visitedBoxShadow", undefined),
                            hoverHandlers: labelHoverHandlers("visitedBoxShadow"),
                          })}
                          <Input
                            type="text"
                            value={(content.visitedBoxShadow as string) || ""}
                            onChange={(e) => handleUpdateContent({ visitedBoxShadow: e.target.value })}
                            className={hotspotFieldClassName}
                            placeholder="0 6px 16px rgba(0,0,0,0.3)"
                          />
                        </div>

                        <div className="space-y-1">
                          {renderStyleLabel({
                            label: "Text Shadow",
                            key: "visitedTextShadow",
                            currentValue: content.visitedTextShadow,
                            fallbackValue: undefined,
                            title: getStyleSourceTooltip("visitedTextShadow"),
                            onClick: (e) => handleStyleLabelOptionClick(e, "visitedTextShadow", undefined),
                            hoverHandlers: labelHoverHandlers("visitedTextShadow"),
                          })}
                          <Input
                            type="text"
                            value={(content.visitedTextShadow as string) || ""}
                            onChange={(e) => handleUpdateContent({ visitedTextShadow: e.target.value })}
                            className={hotspotFieldClassName}
                            placeholder="0 2px 8px rgba(0,0,0,0.35)"
                          />
                        </div>

                        <div className="space-y-1">
                          {renderStyleLabel({
                            label: "Opacity",
                            key: "visitedOpacity",
                            currentValue: content.visitedOpacity,
                            fallbackValue: undefined,
                            title: getStyleSourceTooltip("visitedOpacity"),
                            onClick: (e) => handleStyleLabelOptionClick(e, "visitedOpacity", undefined),
                            hoverHandlers: labelHoverHandlers("visitedOpacity"),
                          })}
                          <Input
                            type="range"
                            min="0"
                            max="1"
                            step="0.05"
                            value={Number(content.visitedOpacity ?? content.opacity ?? 1)}
                            onChange={(e) =>
                              handleUpdateContent({ visitedOpacity: parseFloat(e.target.value) })
                            }
                            className="bg-gray-700 border-gray-600"
                          />
                          <div className="text-xs text-gray-400 text-center">
                            {Math.round(Number(content.visitedOpacity ?? content.opacity ?? 1) * 100)}%
                          </div>
                        </div>

                        <div className="space-y-1">
                          {renderStyleLabel({
                            label: "Transform",
                            key: "visitedTransform",
                            currentValue: content.visitedTransform,
                            fallbackValue: undefined,
                            title: getStyleSourceTooltip("visitedTransform"),
                            onClick: (e) => handleStyleLabelOptionClick(e, "visitedTransform", undefined),
                            hoverHandlers: labelHoverHandlers("visitedTransform"),
                          })}
                          <Input
                            type="text"
                            value={(content.visitedTransform as string) || ""}
                            onChange={(e) => handleUpdateContent({ visitedTransform: e.target.value })}
                            className={hotspotFieldClassName}
                            placeholder="scale(1.05)"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <Separator className="bg-gray-700" />
                </>
              )}

              {/* Transform Properties */}
              <div className="space-y-2">
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-gray-400 p-0.5 mb-1">Facing</Label>
                    <Switch
                      checked={facingEnabled}
                      onCheckedChange={(checked) => setFacingEnabled(checked)}
                      className="h-4 w-8 shrink-0 data-[state=checked]:bg-gray-500 data-[state=unchecked]:bg-gray-600 [&>span]:h-3 [&>span]:w-3 [&>span]:bg-gray-300 [&>span]:data-[state=checked]:translate-x-4"
                    />
                  </div>
                  {facingEnabled && (
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setFacingTabActive("billboard");
                          handleUpdateContent({ renderStyle: "billboard" });
                        }}
                        className={`text-xs px-1 transition-colors relative ${
                          facingTabActive === "billboard"
                            ? "text-white"
                            : "text-gray-400 hover:text-gray-300"
                        }`}
                      >
                        Billboard
                        {facingTabActive === "billboard" && (
                          <div className="absolute -bottom-0.5 left-0 h-0.5 bg-white" style={{ width: "62px" }} />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setFacingTabActive("3d");
                          handleUpdateContent({ renderStyle: "3d" });
                        }}
                        className={`text-xs px-1 transition-colors relative ${
                          facingTabActive === "3d"
                            ? "text-white"
                            : "text-gray-400 hover:text-gray-300"
                        }`}
                      >
                        3D
                        {facingTabActive === "3d" && (
                          <div className="absolute -bottom-0.5 left-0 h-0.5 bg-white" style={{ width: "18px" }} />
                        )}
                      </button>
                    </div>
                  )}
                </div>

                {facingEnabled && (
                  <>
                {/* Position - Yaw & Pitch */}
                <div className="space-y-1">
                  <Label className="text-xs text-gray-400 p-0.5 mb-1">Position</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-gray-400 p-0.5 mb-0.5">Yaw (°)</Label>
                      <SmoothNumberInput
                        value={Math.round(positionYawPitch.yaw)}
                        onChange={(value) => handleUpdateYawPitch("position", value, undefined)}
                        className={hotspotFieldSmClassName}
                        step={1}
                        allowAngleVariables
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-400 p-0.5 mb-0.5">Pitch (°)</Label>
                      <SmoothNumberInput
                        value={Math.round(positionYawPitch.pitch)}
                        onChange={(value) => handleUpdateYawPitch("position", undefined, value)}
                        className={hotspotFieldSmClassName}
                        step={1}
                        allowAngleVariables
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-1">
                  {localHotspot.type === "html" ? (
                    <>
                      <Label className="text-xs text-gray-400 p-0.5 mb-1">Size</Label>
                      <div className="flex gap-2 items-end">
                        <div className="flex-1">
                          <Label className="text-xs text-gray-400 p-0.5 mb-0.5">W (px)</Label>
                          <SmoothNumberInput
                            value={getContentWidthPx()}
                            onChange={(value) => handleUpdateWidthPx(value)}
                            className={hotspotFieldSmClassName}
                            step={1}
                          />
                        </div>
                        {/* Aspect Ratio Lock Button */}
                        <button
                          type="button"
                          onClick={() => {
                            setIsAspectRatioLocked(!isAspectRatioLocked);
                          }}
                          className="px-2 py-1 rounded hover:bg-gray-700 transition-colors text-gray-300 hover:text-white mb-0.5"
                          title={isAspectRatioLocked ? "Unlock aspect ratio" : "Lock aspect ratio"}
                        >
                          {isAspectRatioLocked ? (
                            <Link size={18} />
                          ) : (
                            <Link2Off size={18} />
                          )}
                        </button>
                        <div className="flex-1">
                          <Label className="text-xs text-gray-400 p-0.5 mb-0.5">H (px)</Label>
                          <SmoothNumberInput
                            value={getContentHeightPx()}
                            onChange={(value) => handleUpdateHeightPx(value)}
                            className={hotspotFieldSmClassName}
                            step={1}
                          />
                        </div>
                      </div>
                      <div className="space-y-1 mt-2">
                        <Label className="text-xs text-gray-400 p-0.5 mb-0.5">Text Resize</Label>
                        <Select
                          value={textResizeMode}
                          onValueChange={(value) =>
                            handleUpdateContent({
                              textResizeMode: value === "scale-with-box" ? "scale-with-box" : "fixed",
                            })
                          }
                        >
                          <SelectTrigger className={hotspotSelectClassName}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="fixed">Keep text fixed</SelectItem>
                            <SelectItem value="scale-with-box">Scale text with box</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  ) : (
                    <>
                      <Label className="text-xs text-gray-400 p-0.5 mb-1">Scale</Label>
                      <div className="flex gap-2 items-end">
                        <div className="flex-1">
                          <Label
                            className={changedLabelClass(
                              !areStyleValuesEqual(getWidthValue(scale), scaleBaseline.x ?? 1),
                            )}
                            onClick={(e) => handleScaleLabelOptionClick(e, "x")}
                          >
                            Scale X
                          </Label>
                          <SmoothNumberInput
                            value={Number(getWidthValue(scale).toFixed(3))}
                            onChange={(value) => handleUpdateWidth(value)}
                            className={hotspotFieldSmClassName}
                            step={0.05}
                          />
                        </div>
                        {/* Aspect Ratio Lock Button */}
                        <button
                          type="button"
                          onClick={() => {
                            setIsAspectRatioLocked(!isAspectRatioLocked);
                          }}
                          className="px-2 py-1 rounded hover:bg-gray-700 transition-colors text-gray-300 hover:text-white mb-0.5"
                          title={isAspectRatioLocked ? "Unlock aspect ratio" : "Lock aspect ratio"}
                        >
                          {isAspectRatioLocked ? (
                            <Link size={18} />
                          ) : (
                            <Link2Off size={18} />
                          )}
                        </button>
                        <div className="flex-1">
                          <Label
                            className={changedLabelClass(
                              !areStyleValuesEqual(getHeightValue(scale), scaleBaseline.y ?? 1),
                            )}
                            onClick={(e) => handleScaleLabelOptionClick(e, "y")}
                          >
                            Scale Y
                          </Label>
                          <SmoothNumberInput
                            value={Number(getHeightValue(scale).toFixed(3))}
                            onChange={(value) => handleUpdateHeight(value)}
                            className={hotspotFieldSmClassName}
                            step={0.05}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="space-y-1">
                  <Label className="text-xs text-gray-400 p-0.5 mb-1">Rotation</Label>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs text-gray-400 p-0.5 mb-0.5">X</Label>
                      <SmoothNumberInput
                        value={Math.round(getRotationX())}
                        onChange={(value) => setRotationValue("x", value)}
                        className={hotspotFieldSmClassName}
                        step={1}
                        allowAngleVariables
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-400 p-0.5 mb-0.5">Y</Label>
                      <SmoothNumberInput
                        value={Math.round(getRotationY())}
                        onChange={(value) => setRotationValue("y", value)}
                        className={hotspotFieldSmClassName}
                        step={1}
                        allowAngleVariables
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-400 p-0.5 mb-0.5">Z</Label>
                      <SmoothNumberInput
                        value={Math.round(getRotationZ())}
                        onChange={(value) => setRotationValue("z", value)}
                        className={hotspotFieldSmClassName}
                        step={1}
                        allowAngleVariables
                      />
                    </div>
                  </div>
                  <div className="text-[11px] text-gray-500">
                    Tip: hold Shift and drag the hotspot to rotate in 3D mode.
                  </div>
                </div>
                  </>
                )}

                {localHotspot.type === "html" && (
                  <>
                    <Separator className="bg-gray-700" />

                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <h4 className="text-xs text-gray-400 font-medium flex-1 min-w-0">Pointer</h4>
                        <Switch
                          checked={!!content.showPointer}
                          onCheckedChange={(checked) => handleUpdateContent({ showPointer: checked })}
                          className="h-4 w-8 shrink-0 data-[state=checked]:bg-gray-500 data-[state=unchecked]:bg-gray-600 [&>span]:h-3 [&>span]:w-3 [&>span]:bg-gray-300 [&>span]:data-[state=checked]:translate-x-4"
                        />
                      </div>
                      {content.showPointer && (
                        <div className="space-y-2">
                          <div className="space-y-1">
                            <Label className="text-xs text-gray-400">Pointer Shape</Label>
                            <div className="flex h-7 border border-gray-600 rounded overflow-hidden">
                              {[
                                { value: "triangle", label: "Triangle" },
                                { value: "circle", label: "Circle" },
                              ].map((option, index) => (
                                <button
                                  key={option.value}
                                  type="button"
                                  onClick={() => handleUpdateContent({ pointerShape: option.value })}
                                  className={`flex-1 flex items-center justify-center text-xs ${
                                    (content.pointerShape as string) === option.value ||
                                    (!content.pointerShape && option.value === "triangle")
                                      ? "bg-gray-500 text-white"
                                      : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                                  } ${index < 1 ? "border-r border-gray-600" : ""}`}
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                          </div>

                          {(!(content.pointerShape as string) || (content.pointerShape as string) === "triangle") && (
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-400">Pointer Direction</Label>
                              <div className="flex h-7 border border-gray-600 rounded overflow-hidden">
                                {[
                                  { value: "top", icon: "▲", label: "Top" },
                                  { value: "bottom", icon: "▼", label: "Bottom" },
                                  { value: "left", icon: "◀", label: "Left" },
                                  { value: "right", icon: "▶", label: "Right" },
                                ].map((option, index) => (
                                  <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => handleUpdateContent({ pointerDirection: option.value })}
                                    className={`flex-1 flex items-center justify-center text-xs ${
                                      (content.pointerDirection as string) === option.value ||
                                      (!content.pointerDirection && option.value === "bottom")
                                        ? "bg-gray-500 text-white"
                                        : "bg-gray-700 text-gray-400 hover:bg-gray-600"
                                    } ${index < 3 ? "border-r border-gray-600" : ""}`}
                                    title={option.label}
                                  >
                                    {option.icon}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-400">Pointer Size (px)</Label>
                              <Input
                                type="text"
                                value={(content.pointerSize as string) || ""}
                                onChange={(e) => handleUpdateContent({ pointerSize: e.target.value })}
                                placeholder="8"
                                className={hotspotFieldClassName}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-400">Overlap (px)</Label>
                              <Input
                                type="text"
                                value={(content.pointerOverlap as string) || ""}
                                onChange={(e) => handleUpdateContent({ pointerOverlap: e.target.value })}
                                placeholder="2"
                                className={hotspotFieldClassName}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-400">Line Thickness (px)</Label>
                              <Input
                                type="text"
                                value={(content.pointerLineThickness as string) || ""}
                                onChange={(e) => handleUpdateContent({ pointerLineThickness: e.target.value })}
                                placeholder="2"
                                className={hotspotFieldClassName}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <Separator className="bg-gray-700" />

                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <h4 className="text-xs text-gray-400 font-medium flex-1 min-w-0">Tooltip</h4>
                        <Switch
                          checked={!!content.tooltipEnabled}
                          onCheckedChange={(checked) => handleUpdateContent({ tooltipEnabled: checked })}
                          className="h-4 w-8 shrink-0 data-[state=checked]:bg-gray-500 data-[state=unchecked]:bg-gray-600 [&>span]:h-3 [&>span]:w-3 [&>span]:bg-gray-300 [&>span]:data-[state=checked]:translate-x-4"
                        />
                      </div>

                      {content.tooltipEnabled && (
                        <div className="space-y-2">
                          <div className="space-y-1">
                            <Label className="text-xs text-gray-400">Text</Label>
                            <TextVariableInput
                              value={(content.tooltipText as string) || ""}
                              onFocus={showTooltip}
                              onBlur={hideTooltip}
                              onValueChange={(nextValue) => {
                                handleUpdateContent({ tooltipText: nextValue });
                                updateTooltipDOM({ text: nextValue });
                              }}
                              placeholder="Tooltip text..."
                              className={hotspotFieldClassName}
                            />
                          </div>

                          <div onMouseDown={showTooltip}>
                            <ColorPicker
                              value={(content.tooltipBgColor as string) || "#000000CC"}
                              onChange={(color) => { handleUpdateContent({ tooltipBgColor: color }); updateTooltipDOM({ bgColor: color }); }}
                              label="Background Color"
                              variant="compact"
                            />
                          </div>

                          <div onMouseDown={showTooltip}>
                            <ColorPicker
                              value={(content.tooltipFontColor as string) || "#ffffff"}
                              onChange={(color) => { handleUpdateContent({ tooltipFontColor: color }); updateTooltipDOM({ fontColor: color }); }}
                              label="Font Color"
                              variant="compact"
                            />
                          </div>

                          <div className="space-y-1">
                            <Label className="text-xs text-gray-400">Font Size</Label>
                            <Input
                              type="text"
                              value={(content.tooltipFontSize as string) || ""}
                              onFocus={showTooltip}
                              onChange={(e) => {
                                handleUpdateContent({ tooltipFontSize: e.target.value });
                                updateTooltipDOM({ fontSize: e.target.value });
                              }}
                              onBlur={hideTooltip}
                              placeholder="12px"
                              className={hotspotFieldClassName}
                            />
                          </div>

                          <div className="space-y-1">
                            <Label className="text-xs text-gray-400">Border Radius</Label>
                            <Input
                              type="text"
                              value={(content.tooltipBorderRadius as string) || ""}
                              onFocus={showTooltip}
                              onChange={(e) => {
                                handleUpdateContent({ tooltipBorderRadius: e.target.value });
                                updateTooltipDOM({ borderRadius: e.target.value });
                              }}
                              onBlur={hideTooltip}
                              placeholder="4px"
                              className={hotspotFieldClassName}
                            />
                          </div>

                          <div className="space-y-1">
                            <Label className="text-xs text-gray-400">Padding</Label>
                            <Input
                              type="text"
                              value={(content.tooltipPadding as string) || ""}
                              onFocus={showTooltip}
                              onChange={(e) => {
                                handleUpdateContent({ tooltipPadding: e.target.value });
                                updateTooltipDOM({ padding: e.target.value });
                              }}
                              onBlur={hideTooltip}
                              placeholder="4px 8px"
                              className={hotspotFieldClassName}
                            />
                          </div>

                          <div className="flex items-center gap-3">
                            <Label className="text-xs text-gray-400 flex-1 min-w-0">Arrow</Label>
                            <Switch
                              checked={!!content.tooltipArrow}
                              onCheckedChange={(checked) => handleUpdateContent({ tooltipArrow: checked })}
                              className="h-4 w-8 shrink-0 data-[state=checked]:bg-gray-500 data-[state=unchecked]:bg-gray-600 [&>span]:h-3 [&>span]:w-3 [&>span]:bg-gray-300 [&>span]:data-[state=checked]:translate-x-4"
                            />
                          </div>
                          {content.tooltipArrow && (
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-400">Arrow Direction</Label>
                              <select
                                value={(content.tooltipArrowDirection as string) || "top"}
                                onFocus={showTooltip}
                                onChange={(e) => {
                                  handleUpdateContent({ tooltipArrowDirection: e.target.value });
                                  updateTooltipDOM({ arrowDirection: e.target.value });
                                }}
                                onBlur={hideTooltip}
                                className={hotspotSelectClassName}
                              >
                                <option value="top">Top</option>
                                <option value="right">Right</option>
                                <option value="bottom">Bottom</option>
                                <option value="left">Left</option>
                              </select>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              </div>
            </TabsContent>

            <TabsContent value="settings" className="mt-9">
              <div className="space-y-3">
                {/* Hotspot Settings for HTML hotspots */}
                {localHotspot.type === "html" && (
                  <div className="space-y-3">
                    <div className="space-y-2">{iconVariableField}</div>
                    {content.contentType !== "icon" && (
                      <div className="space-y-2">
                        <h4 className="text-xs text-gray-400 font-medium">Content</h4>
                        <div className="space-y-1">
                          {renderStyleLabel({
                            label: "Text",
                            key: "text",
                            currentValue: content.text,
                            fallbackValue: "",
                            onClick: (e) => handleStyleLabelOptionClick(e, "text", ""),
                          })}
                          <HotspotTextEditor
                            value={content.text || ""}
                            onChange={(html) => handleUpdateContent({ text: html, contentType: "text" })}
                            autoFocus={hotspotTextAutoFocus && content.contentType !== "icon"}
                            onAutoFocusApplied={() => setHotspotTextAutoFocus(false)}
                            placeholder="Enter the text content for this hotspot..."
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Actions */}
            <TabsContent value="actions" className="space-y-4 mt-9">
              <ActionsPanel
                actions={readActions(content)}
                panoramas={(panoramas || []) as Array<{ id: string; name: string }>}
                hotspots={hotspots.map((hotspot) => ({ id: hotspot.id, name: hotspot.name }))}
                skinComponents={skinComponents.map((component) => ({
                  id: component.id,
                  name: component.name,
                  parentId: component.parentId,
                }))}
                excludeHotspotId={localHotspot.id}
                isActionMenuOpen={isActionMenuOpen}
                setIsActionMenuOpen={setIsActionMenuOpen}
                expandedActionIds={expandedActionIds}
                setExpandedActionIds={setExpandedActionIds}
                hotspotVisibilityConfigs={hotspotVisibilityConfigs}
                setHotspotVisibilityConfigs={setHotspotVisibilityConfigs}
                onAddAction={addAction}
                onRemoveAction={removeAction}
                onUpdateAction={updateAction}
                menuExtras={
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-start border-gray-600 text-gray-200 hover:bg-gray-700 h-8 text-xs"
                      onClick={() => {
                        setIsConfettiDialogOpen(true);
                        setIsActionMenuOpen(false);
                      }}
                    >
                      🎉
                      <span className="ml-2">Add Confetti</span>
                    </Button>

                    <div className="flex items-center justify-between px-1">
                      <Label className="text-xs text-gray-300">Hotspot Minimization</Label>
                      <Switch
                        checked={Boolean(content.shrinkMode)}
                        onCheckedChange={handleShrinkModeChange}
                      />
                    </div>
                    <div className="flex items-center justify-between px-1">
                      <Label className="text-xs text-gray-300">Move camera to hotspot</Label>
                      <Switch
                        checked={Boolean(content.moveCameraToHotspot)}
                        onCheckedChange={handleMoveCameraToHotspotChange}
                      />
                    </div>
                  </>
                }
                renderDownloadFields={(action, actionId) => (
                  <>
                    <div className="flex items-center gap-2">
                      <Label className="text-[11px] text-gray-400 w-1/3">File</Label>
                      <div className="w-2/3 relative">
                        <input
                          type="file"
                          disabled={isDownloadUploading}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              void handleDownloadUpload(file, (updates) =>
                                updateAction(actionId, updates),
                              );
                            }
                            e.currentTarget.value = "";
                          }}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        <div className="flex items-center justify-between w-full h-7 px-3 text-xs bg-gray-700/60 text-white rounded cursor-pointer hover:bg-gray-700 transition-colors">
                          <span className="truncate">
                            {isDownloadUploading
                              ? "Uploading..."
                              : downloadUploadName ||
                                (action.downloadFileName as string) ||
                                "Choose File"}
                          </span>
                          <span className="text-gray-300">Browse</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-[11px] text-gray-400 w-1/3">File name</Label>
                      <Input
                        className="w-2/3 h-7 text-xs bg-gray-700/60 border-0 text-white"
                        value={(action.downloadFileName as string) || ""}
                        onChange={(e) =>
                          updateAction(actionId, { downloadFileName: e.target.value })
                        }
                      />
                    </div>
                  </>
                )}
              />

              {hasNavigateAction && (
                <div className="rounded-md bg-gray-800/60" style={{display: 'none'}}>
                  <div
                    role="button"
                    tabIndex={0}
                    className="w-full py-2 px-2 flex items-center justify-between text-left bg-gray-700/50 rounded"
                    onClick={() => setIsNavigateExpanded((prev) => !prev)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setIsNavigateExpanded((prev) => !prev);
                      }
                    }}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        {actionType === "notification" ? (
                          <Bell size={16} className="text-gray-400" />
                        ) : actionType === "download" ? (
                          <Download size={16} className="text-gray-400" />
                        ) : actionType === "showHideSkin" || actionType === "showHideHotspots" ? (
                          <Eye size={16} className="text-gray-400" />
                        ) : (
                          <Link size={16} className="text-gray-400" />
                        )}
                        <div className="text-xs text-white font-medium">
                          {actionType === "notification"
                            ? "Notification"
                            : actionType === "download"
                              ? "Download File"
                              : actionType === "showHideSkin"
                                ? "Show/Hide Skin"
                                : actionType === "showHideHotspots"
                                  ? "Show/Hide Hotspots"
                                  : "Navigate to"}
                        </div>
                      </div>
                    </div>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="text-gray-400 hover:text-gray-200 hover:bg-gray-600 p-1 rounded transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveNavigateAction();
                          }}
                        >
                          <Minus size={12} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Remove</TooltipContent>
                    </Tooltip>
                  </div>

                  {isNavigateExpanded && (
                    <div className="pt-1 pb-3 space-y-1">
                      <div className="flex items-center gap-2">
                        <Label className="text-[11px] text-gray-400 w-1/3">Trigger</Label>
                        <div className="w-2/3">
                          <Select
                            value={navigateTrigger}
                            onValueChange={(value) =>
                              handleUpdateContent({ navigateTrigger: value })
                            }
                          >
                            <SelectTrigger className="h-7 text-xs bg-gray-700/60 border-0 px-3 pr-2 rounded-md text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent side="bottom" align="start">
                              <SelectItem value="click">On Click</SelectItem>
                              <SelectItem value="mouseenter">Mouse enter</SelectItem>
                              <SelectItem value="mouseleave">Mouse leave</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-[11px] text-gray-400 w-1/3">
                          {actionType === "showHideSkin" || actionType === "showHideHotspots" ? "Action 1" : "Action"}
                        </Label>
                        <div className="w-2/3">
                          <Select
                            value={actionType}
                            onValueChange={(value) => {
                              if (value === "notification") {
                                handleUpdateContent({
                                  actionType: "notification",
                                  linkedPanoramaId: undefined,
                                  linkTransitionType: undefined,
                                  downloadFileUrl: undefined,
                                  downloadFileName: undefined,
                                });
                              } else if (value === "download") {
                                handleUpdateContent({
                                  actionType: "download",
                                  linkedPanoramaId: undefined,
                                  linkTransitionType: undefined,
                                  notificationTitle: undefined,
                                  notificationMessage: undefined,
                                  notificationAutoHideDuration: undefined,
                                  notificationStyle: undefined,
                                });
                              } else if (value === "showHideSkin") {
                                handleUpdateContent({
                                  actionType: "showHideSkin",
                                  linkedPanoramaId: undefined,
                                  linkTransitionType: undefined,
                                  notificationTitle: undefined,
                                  notificationMessage: undefined,
                                  notificationAutoHideDuration: undefined,
                                  notificationStyle: undefined,
                                  downloadFileUrl: undefined,
                                  downloadFileName: undefined,
                                });
                              } else if (value === "showHideHotspots") {
                                handleUpdateContent({
                                  actionType: "showHideHotspots",
                                  linkedPanoramaId: undefined,
                                  linkTransitionType: undefined,
                                  notificationTitle: undefined,
                                  notificationMessage: undefined,
                                  notificationAutoHideDuration: undefined,
                                  notificationStyle: undefined,
                                  downloadFileUrl: undefined,
                                  downloadFileName: undefined,
                                });
                              } else {
                                handleUpdateContent({
                                  actionType: "navigate",
                                  notificationTitle: undefined,
                                  notificationMessage: undefined,
                                  notificationAutoHideDuration: undefined,
                                  notificationStyle: undefined,
                                  downloadFileUrl: undefined,
                                  downloadFileName: undefined,
                                });
                              }
                            }}
                          >
                          <SelectTrigger className="h-7 text-xs bg-gray-700/60 border-0 px-3 pr-2 rounded-md text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent side="bottom" align="start">
                              <SelectItem value="navigate" className="flex items-center gap-2">
                                <div className="flex items-center gap-2">
                                  <Link size={14} />
                                  <span>Navigate to</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="notification" className="flex items-center gap-2">
                                <div className="flex items-center gap-2">
                                  <Bell size={14} />
                                  <span>Notification</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="download" className="flex items-center gap-2">
                                <div className="flex items-center gap-2">
                                  <Download size={14} />
                                  <span>Download File</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="showHideSkin" className="flex items-center gap-2">
                                <div className="flex items-center gap-2">
                                  <Eye size={14} />
                                  <span>Show/Hide Skin</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="showHideHotspots" className="flex items-center gap-2">
                                <div className="flex items-center gap-2">
                                  <Eye size={14} />
                                  <span>Show/Hide Hotspots</span>
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {actionType === "navigate" ? (
                        <>
                          <div className="flex items-center gap-2">
                            <Label className="text-[11px] text-gray-400 w-1/3">Destination</Label>
                            <div className="w-2/3">
                              <PanoramaDestinationSelect
                                panoramas={panoramas || []}
                                value={(content.linkedPanoramaId as string) || ""}
                                excludePanoramaId={localHotspot.panorama_id}
                                onChange={(value) => {
                                  if (value) {
                                    handleLinkPanorama(value);
                                  }
                                }}
                              />
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Label className="text-[11px] text-gray-400 w-1/3">Transition</Label>
                            <div className="w-2/3">
                              <Select
                                value={linkTransitionType}
                                onValueChange={(value) =>
                                  setLinkTransitionType(
                                    value as "default" | "same-view" | "slide-to" | "morph",
                                  )
                                }
                              >
                                <SelectTrigger className="h-7 text-xs bg-gray-700/60 border-0 px-3 pr-2 rounded-md text-white">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent side="bottom" align="start">
                                  <SelectItem value="default">Default View transition</SelectItem>
                                  <SelectItem value="same-view">Same View transition</SelectItem>
                                  <SelectItem value="slide-to">Slide To</SelectItem>
                                  <SelectItem value="morph">Morph</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </>
                      ) : actionType === "notification" ? (
                        <>
                          <div className="flex items-center gap-2">
                            <Label className="text-[11px] text-gray-400 w-1/3">Title</Label>
                            <Input
                              className="w-2/3 h-7 text-xs bg-gray-700/60 border-0 text-white"
                              value={(content.notificationTitle as string) || ""}
                              onChange={(e) =>
                                handleUpdateContent({ notificationTitle: e.target.value })
                              }
                            />
                          </div>
                          <div className="flex items-start gap-2">
                            <Label className="text-[11px] text-gray-400 w-1/3 pt-1">Message</Label>
                            <Textarea
                              className="w-2/3 text-xs bg-gray-700/60 border-0 text-white min-h-[60px]"
                              value={(content.notificationMessage as string) || ""}
                              onChange={(e) =>
                                handleUpdateContent({ notificationMessage: e.target.value })
                              }
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <Label className="text-[11px] text-gray-400 w-1/3">Style</Label>
                            <div className="w-2/3">
                              <Select
                                value={notificationStyle}
                                onValueChange={(value) =>
                                  handleUpdateContent({ notificationStyle: value })
                                }
                              >
                                <SelectTrigger className="h-7 text-xs bg-gray-700/60 border-0 px-3 pr-2 rounded-md text-white">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent side="bottom" align="start">
                                  <SelectItem value="banner">Banner</SelectItem>
                                  <SelectItem value="alert">Alert</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          {notificationStyle !== "alert" && (
                            <div className="flex items-center gap-2">
                              <Label className="text-[11px] text-gray-400 w-1/3">Auto-hide (ms)</Label>
                              <Input
                                type="number"
                                className="w-2/3 h-7 text-xs bg-gray-700/60 border-0 text-white"
                                value={
                                  (content.notificationAutoHideDuration as number | undefined) ??
                                  ""
                                }
                                onChange={(e) =>
                                  handleUpdateContent({
                                    notificationAutoHideDuration: e.target.value
                                      ? Number(e.target.value)
                                      : undefined,
                                  })
                                }
                              />
                            </div>
                          )}
                        </>
                      ) : actionType === "showHideSkin" ? (
                        <>
                          <div className="flex items-center gap-2">
                            <Label className="text-[11px] text-gray-400 w-1/3">Action 2</Label>
                            <div className="w-2/3">
                              <Select
                                value={(content.skinVisibilityAction as string) || "show"}
                                onValueChange={(value) =>
                                  handleUpdateContent({
                                    skinVisibilityAction: value as "show" | "hide" | "toggle",
                                  })
                                }
                              >
                                <SelectTrigger className="h-7 text-xs bg-gray-700/60 border-0 px-3 pr-2 rounded-md text-white">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent side="bottom" align="start">
                                  <SelectItem value="show">Show</SelectItem>
                                  <SelectItem value="hide">Hide</SelectItem>
                                  <SelectItem value="toggle">Toggle</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <Label className="text-[11px] text-gray-400 w-1/3 pt-1">Components</Label>
                            <div className="w-2/3 max-h-48 overflow-y-auto space-y-0">
                              {skinComponents.length === 0 ? (
                                <p className="text-[10px] text-gray-500 p-2">No skin components</p>
                              ) : (
                                skinComponents
                                  .filter((comp) => !comp.parentId)
                                  .map((comp) => {
                                    const selected = ((content.skinVisibilityTargetComponentIds as string[]) || []).includes(
                                      comp.id,
                                    );
                                    const children = skinComponents.filter((c) => c.parentId === comp.id);
                                    const isExpanded = expandedSkinComponents.has(comp.id);
                                    return (
                                      <div key={comp.id} className="space-y-0">
                                        <div className="flex items-center gap-1 px-2 py-1 hover:bg-gray-700/50 rounded text-[11px] text-gray-200">
                                          {children.length > 0 && (
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const newSet = new Set(expandedSkinComponents);
                                                if (newSet.has(comp.id)) {
                                                  newSet.delete(comp.id);
                                                } else {
                                                  newSet.add(comp.id);
                                                }
                                                setExpandedSkinComponents(newSet);
                                              }}
                                              className="p-0 text-gray-400 hover:text-gray-200 transition-colors"
                                            >
                                              <ChevronDown
                                                size={14}
                                                className={`transition-transform ${isExpanded ? "" : "-rotate-90"}`}
                                              />
                                            </button>
                                          )}
                                          {children.length === 0 && <div className="w-[14px]" />}
                                          <label className="flex items-center gap-2 cursor-pointer flex-1">
                                            <input
                                              type="checkbox"
                                              checked={selected}
                                              onChange={(e) => {
                                                const current = (content.skinVisibilityTargetComponentIds as string[]) || [];
                                                const next = e.target.checked
                                                  ? [...current, comp.id]
                                                  : current.filter((id) => id !== comp.id);
                                                handleUpdateContent({
                                                  skinVisibilityTargetComponentIds: next,
                                                });
                                              }}
                                              className="h-3 w-3 accent-gray-500 cursor-pointer"
                                            />
                                            <span>{comp.name}</span>
                                          </label>
                                        </div>
                                        {isExpanded &&
                                          children.map((child) => {
                                            const childSelected = ((content.skinVisibilityTargetComponentIds as string[]) || []).includes(
                                              child.id,
                                            );
                                            return (
                                              <label
                                                key={child.id}
                                                className="flex items-center gap-2 px-8 py-1 hover:bg-gray-700/50 cursor-pointer rounded text-[11px] text-gray-300"
                                              >
                                                <input
                                                  type="checkbox"
                                                  checked={childSelected}
                                                  onChange={(e) => {
                                                    const current = (content.skinVisibilityTargetComponentIds as string[]) || [];
                                                    const next = e.target.checked
                                                      ? [...current, child.id]
                                                      : current.filter((id) => id !== child.id);
                                                    handleUpdateContent({
                                                      skinVisibilityTargetComponentIds: next,
                                                    });
                                                  }}
                                                  className="h-3 w-3 accent-gray-500 cursor-pointer"
                                                />
                                                <span>{child.name}</span>
                                              </label>
                                            );
                                          })}
                                      </div>
                                    );
                                  })
                              )}
                            </div>
                          </div>
                        </>
                      ) : actionType === "showHideHotspots" ? (
                        <>
                          <div className="flex items-center gap-2">
                            <Label className="text-[11px] text-gray-400 w-1/3">Action 2</Label>
                            <div className="w-2/3">
                              <Select
                                value={(content.hotspotVisibilityAction as string) || "show"}
                                onValueChange={(value) =>
                                  handleUpdateContent({
                                    hotspotVisibilityAction: value as "show" | "hide" | "toggle",
                                  })
                                }
                              >
                                <SelectTrigger className="h-7 text-xs bg-gray-700/60 border-0 px-3 pr-2 rounded-md text-white">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent side="bottom" align="start">
                                  <SelectItem value="show">Show</SelectItem>
                                  <SelectItem value="hide">Hide</SelectItem>
                                  <SelectItem value="toggle">Toggle</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Label className="text-[11px] text-gray-400 w-1/3 pt-0">Items</Label>
                            <div className="w-2/3">
                              <CollectionItemsPanel
                                  config={hotspotVisibilityConfig}
                                  fields={[
                                    { name: "id", displayName: "ID", type: "string" },
                                    { name: "name", displayName: "Name", type: "string" },
                                  ]}
                                  allItems={hotspots.filter((h) => h.id !== localHotspot.id)}
                                  onChange={(newConfig) => {
                                    setHotspotVisibilityConfig(newConfig);
                                    handleUpdateContent({
                                      hotspotVisibilityTargetIds: newConfig.selectedItemIds,
                                      hotspotVisibilityItemsConfig: newConfig,
                                    });
                                  }}
                                  compact={true}
                                />
                              </div>
                            </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center gap-2">
                            <Label className="text-[11px] text-gray-400 w-1/3">File</Label>
                            <div className="w-2/3 relative">
                              <input
                                type="file"
                                disabled={isDownloadUploading}
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    void handleDownloadUpload(file);
                                  }
                                  e.currentTarget.value = "";
                                }}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                              />
                              <div className="flex items-center justify-between w-full h-7 px-3 text-xs bg-gray-700/60 text-white rounded cursor-pointer hover:bg-gray-700 transition-colors">
                                <span className="truncate">
                                  {isDownloadUploading
                                    ? "Uploading..."
                                    : downloadUploadName ||
                                      (content.downloadFileName as string) ||
                                      "Choose File"}
                                </span>
                                <span className="text-gray-300">Browse</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Label className="text-[11px] text-gray-400 w-1/3">File name</Label>
                            <Input
                              className="w-2/3 h-7 text-xs bg-gray-700/60 border-0 text-white"
                              value={(content.downloadFileName as string) || ""}
                              onChange={(e) =>
                                handleUpdateContent({ downloadFileName: e.target.value })
                              }
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              <ConfettiDialog
                onAdd={handleConfettiActionAdd}
                initialAction={content.confettiAction as Record<string, unknown> | undefined}
                isOpen={isConfettiDialogOpen}
                onOpenChange={setIsConfettiDialogOpen}
              />

              <div className="space-y-2">
                <div className="-mx-2 space-y-1">
                  {hasConfetti && (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={handleEditConfetti}
                      className="w-full text-[11px] text-gray-200 bg-gray-700 px-2 py-1 rounded flex items-center justify-between gap-1.5 hover:bg-gray-600 transition-colors text-left select-none"
                    >
                      <div className="flex items-center gap-1.5 min-w-0">
                        <PartyPopper size={12} className="text-gray-300" />
                        <span className="truncate">Confetti</span>
                      </div>
                      <button
                        type="button"
                        title="Delete action"
                        className="p-0.5 rounded hover:bg-gray-500 text-gray-300 hover:text-red-300"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveConfetti();
                        }}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  )}
                </div>

              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};
